export const learningGuides = [
  {
    id: 'csharp-guide',
    title: 'C# (Basic to Advanced)',
    subtitle: 'C# Interview Questions and Answers for Experienced Developers (2026)',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Code',
    description: 'Master Variables, OOP, SOLID, Memory Management, GC, Delegates, LINQ, Async/Await, Generics, and .NET 8/9.',
    questions: [
      {
        id: 'cs-1',
        category: 'Memory Management & GC',
        difficulty: 'Hard',
        question: 'Explain how the Garbage Collector (GC) works in .NET. What are generations, and how do Large Object Heap (LOH) and Pinning affect GC performance?',
        answer: `The **Garbage Collector (GC)** in .NET manages memory allocation and release automatically using a **generational mark-and-sweep algorithm**.

### 1. Generations
To optimize collection times, the GC divides heap objects into three generations:
*   **Generation 0 (Gen 0):** Stores short-lived objects (e.g., local variables). It is collected most frequently, using lightweight collections.
*   **Generation 1 (Gen 1):** Acts as a buffer between Gen 0 and Gen 2. Objects surviving Gen 0 are promoted here.
*   **Generation 2 (Gen 2):** Stores long-lived objects (e.g., static data, application configuration). Collecting Gen 2 triggers a **Full GC**, which is expensive.

### 2. Large Object Heap (LOH)
*   **Threshold:** Objects larger than **85,000 bytes** go directly to the LOH.
*   **GC Generation:** LOH is treated as part of **Gen 2** and is only collected during a Full GC.
*   **Heap Fragmentation:** By default, LOH is **never compacted** (moving large chunks of memory is slow), leading to fragmentation. In .NET Core / .NET 8+, you can configure the GC to compact the LOH or use 'GCSettings.LargeObjectHeapCompactionMode = GCLargeObjectHeapCompactionMode.CompactOnce'.

### 3. Pinning
*   **Definition:** Pinning pins an object in memory (e.g., using the \`fixed\` keyword or \`GCHandleType.Pinned\`) preventing the GC from moving it during compaction.
*   **Performance Impact:** Pinning creates **dikes/obstacles** in the heap. The GC cannot compact around pinned objects, causing severe heap fragmentation, larger memory footprint, and CPU overhead during GC sweeps.
*   **Best Practice:** Keep pinning operations extremely short-lived, or allocate pinned memory in the Pinned Object Heap (POH) introduced in .NET 5+.`
      },
      {
        id: 'cs-2',
        category: 'Async/Await & Threading',
        difficulty: 'Hard',
        question: 'How does the Async/Await state machine work under the hood in C#? What is SynchronizationContext, and how does ConfigureAwait(false) prevent deadlocks?',
        answer: `When the compiler compiles an \`async\` method, it transforms it into a state machine structure implementing \`IAsyncStateMachine\`.

### 1. The State Machine Under the Hood
*   **Compilation:** The compiler transforms your async method into a generated \`struct\`.
*   **State Tracking:** The state machine has fields for parameters, local variables, the active task, and an integer tracking the current state (e.g., \`-1\` for running, \`0\` for first await, etc.).
*   **MoveNext Method:** When an await point is reached, the state machine checks if the awaited task has completed.
    *   If **completed**, execution continues synchronously.
    *   If **not completed**, the awaiter registers the state machine to be called back via a callback when the task finishes, and returns.

### 2. SynchronizationContext
*   **Definition:** An abstraction representing the queueing context where code executes (e.g., the UI thread in WPF/WinForms, or the request context in legacy ASP.NET).
*   **Async Await Behavior:** By default, C# captures the current \`SynchronizationContext\` when awaiting a task. When the task completes, the context is used to post the continuation back to the original thread/context.

### 3. ConfigureAwait(false) & Deadlocks
*   **How it works:** \`ConfigureAwait(false)\` tells the runtime *not* to capture the context and execute the continuation onAny thread pool thread instead of returning to the original context.
*   **Deadlock Prevention:** If a UI thread calls an async method synchronously (e.g., \`MyMethodAsync().Result\`), it blocks the context thread. When the awaited task completes, it attempts to marshal back to the blocked context thread, resulting in a **deadlock**. Using \`ConfigureAwait(false)\` allows the task continuation to run on a background thread pool thread, resolving the deadlock block.
*   **Modern ASP.NET Core:** ASP.NET Core does *not* have a \`SynchronizationContext\`. However, using \`ConfigureAwait(false)\` is still recommended in reusable library code to prevent issues in other environments (like desktop or legacy mobile apps).`
      },
      {
        id: 'cs-3',
        category: 'SOLID & Clean Code',
        difficulty: 'Medium',
        question: 'What is the Dependency Inversion Principle (DIP)? How does it differ from Dependency Injection (DI) and Inversion of Control (IoC)?',
        answer: `While related, **DIP (Principle)**, **IoC (Design Pattern)**, and **DI (Implementation Technique)** are distinct concepts:

### 1. Dependency Inversion Principle (DIP)
DIP is one of the five SOLID principles. It asserts:
*   High-level modules should not depend on low-level modules. Both should depend on **abstractions** (interfaces or abstract classes).
*   Abstractions should not depend on details. Details (concrete implementations) should depend on abstractions.
*   *Analogy:* Wall electrical outlets (abstraction) allow you to plug in a phone charger, laptop, or lamp (details) without rewire-linking the wall circuit directly to the appliance.

### 2. Inversion of Control (IoC)
IoC is a broad architectural design pattern where the control flow of a program is inverted.
*   Instead of your custom code invoking libraries/frameworks, the **framework invokes your code** (Hollywood Principle: *"Don't call us, we'll call you"*).

### 3. Dependency Injection (DI)
DI is a concrete pattern to achieve IoC/DIP. It is the process of supplying/injecting dependencies into an object (e.g., via constructor, property, or method parameters) rather than the class creating them internally.

\`\`\`csharp
// VIOLATION (High dependency on low-level SQL database)
public class OrderService {
    private SqlDatabase _db = new SqlDatabase(); // Tightly coupled!
}

// REMEDIAL (Adhering to DIP and using DI)
public interface IDatabase { void Save(Order order); }

public class OrderService {
    private readonly IDatabase _db;
    public OrderService(IDatabase db) { // Injected via DI
        _db = db;
    }
}
\`\`\``
      },
      {
        id: 'cs-4',
        category: '.NET 8/9 features',
        difficulty: 'Medium',
        question: 'What are primary constructors and collection expressions in recent C# editions (.NET 8/9)? Provide examples.',
        answer: `C# 12 (.NET 8) and C# 13 (.NET 9) introduce syntax enhancements to make code cleaner, safer, and faster.

### 1. Primary Constructors
Allows classes and structs to declare constructor parameters directly in the class header. Parameters remain in scope for the entire class body and can be used to initialize fields or properties.

\`\`\`csharp
// Legacy C#
public class UserService {
    private readonly IUserRepository _repo;
    public UserService(IUserRepository repo) {
        _repo = repo;
    }
}

// C# 12 Primary Constructors
public class UserService(IUserRepository repo) {
    public async Task<User> GetUser(int id) => await repo.FindByIdAsync(id);
}
\`\`\`

### 2. Collection Expressions
Introduces a unified, clean syntax to declare collections using square brackets \`[]\` and the spread operator \`..\`.

\`\`\`csharp
// Before C# 12
int[] x1 = new int[] { 1, 2, 3 };
List<int> x2 = new List<int> { 4, 5, 6 };

// C# 12 Collection Expressions
int[] y1 = [1, 2, 3];
List<int> y2 = [4, 5, 6];

// Spread operator to concatenate/combine
int[] combined = [..y1, ..y2, 7, 8, 9]; // Result: [1, 2, 3, 4, 5, 6, 7, 8, 9]
\`\`\``
      },

      {
        id: 'cs-5',
        category: 'OOP & Runtime',
        difficulty: 'Medium',
        question: 'What is the difference between abstraction, encapsulation, inheritance, and polymorphism in C#? Give practical examples.',
        answer: `These are the four foundational OOP pillars, but they solve different design problems.

### 1. Abstraction
**Abstraction** means exposing only essential behavior while hiding implementation details.
For example, an interface like \`IPaymentGateway\` exposes \`ProcessPayment()\` without revealing whether the provider is Stripe, PayPal, or a bank API.

### 2. Encapsulation
**Encapsulation** means keeping state and behavior together and protecting internal data from invalid changes.
In C#, this is commonly achieved with private fields and public methods/properties.

\`\`\`csharp
public class BankAccount
{
    private decimal _balance;

    public decimal Balance => _balance;

    public void Deposit(decimal amount)
    {
        if (amount <= 0) throw new ArgumentException("Amount must be positive.");
        _balance += amount;
    }
}
\`\`\`

### 3. Inheritance
**Inheritance** allows one class to reuse and extend another class.
For example, \`SavingsAccount\` can inherit from \`BankAccount\` and add interest-specific logic.

### 4. Polymorphism
**Polymorphism** means the same method call can behave differently depending on the actual runtime type.
This usually appears through interfaces, abstract classes, or virtual/override methods.

\`\`\`csharp
public interface INotifier
{
    void Send(string message);
}

public class EmailNotifier : INotifier
{
    public void Send(string message) => Console.WriteLine($"Email: {message}");
}

public class SmsNotifier : INotifier
{
    public void Send(string message) => Console.WriteLine($"SMS: {message}");
}
\`\`\`

A caller can work with \`INotifier\` without caring which implementation it receives.`
      },
      {
        id: 'cs-6',
        category: 'Collections & Generics',
        difficulty: 'Medium',
        question: 'What is the difference between IEnumerable, ICollection, IList, and IQueryable in .NET?',
        answer: `These interfaces represent different levels of capability for working with data.

### 1. IEnumerable<T>
**IEnumerable<T>** supports forward-only iteration.
It is the base abstraction for \`foreach\` and is ideal when you only need to enumerate data.

### 2. ICollection<T>
**ICollection<T>** extends \`IEnumerable<T>\` by adding size and modification operations such as \`Add\`, \`Remove\`, and \`Count\`.
Use it when the collection must be mutable.

### 3. IList<T>
**IList<T>** extends \`ICollection<T>\` and adds index-based access like \`list[0]\`.
Use it when ordering and direct positional access matter.

### 4. IQueryable<T>
**IQueryable<T>** is very different from the others.
It represents a query that can be translated by a provider, such as Entity Framework Core, into another form like SQL.

\`\`\`csharp
IEnumerable<User> inMemory = users.Where(u => u.IsActive); // runs in memory
IQueryable<User> queryable = db.Users.Where(u => u.IsActive); // translated to SQL
\`\`\`

### 5. Important interview point
Calling LINQ methods on \`IEnumerable<T>\` generally executes in memory.
Calling them on \`IQueryable<T>\` builds an expression tree that may execute remotely, for example in SQL Server.
That difference affects performance, filtering, paging, and projection.`
      },
      {
        id: 'cs-7',
        category: 'Exception Handling',
        difficulty: 'Medium',
        question: 'What are best practices for exception handling in C#? When should you use throw, throw ex, and custom exceptions?',
        answer: `Exception handling should preserve useful diagnostic information while keeping business logic readable.

### 1. Use exceptions for exceptional situations
Exceptions should represent unexpected or invalid states, not normal control flow.
For expected failures, prefer validation or result objects where appropriate.

### 2. throw vs throw ex
Use **\`throw;\`** inside a catch block to preserve the original stack trace.
Using **\`throw ex;\`** resets the stack trace and makes debugging harder.

\`\`\`csharp
try
{
    SaveChanges();
}
catch (Exception)
{
    throw; // preserves original call stack
}
\`\`\`

### 3. Create custom exceptions carefully
Use a custom exception when the caller needs domain-specific meaning.

\`\`\`csharp
public class InsufficientFundsException : Exception
{
    public InsufficientFundsException(string message) : base(message) { }
}
\`\`\`

### 4. Add context, don't swallow errors
If you catch an exception, either handle it fully or rethrow it with additional context.

\`\`\`csharp
catch (SqlException ex)
{
    throw new OrderPersistenceException("Failed to save order.", ex);
}
\`\`\`

### 5. Best practices
* Log exceptions close to the boundary of the application.
* Avoid empty catch blocks.
* Catch specific exceptions before generic ones.
* Use global exception handling middleware in ASP.NET Core for API consistency.`
      },
      {
        id: 'cs-8',
        category: 'Delegates, Events & Lambdas',
        difficulty: 'Medium',
        question: 'What are delegates, events, and lambda expressions in C#? How are they related?',
        answer: `These three concepts are closely related and are central to modern C# programming.

### 1. Delegates
A **delegate** is a type-safe function pointer.
It can reference methods with a matching signature.

\`\`\`csharp
public delegate int MathOperation(int a, int b);
\`\`\`

### 2. Events
An **event** is built on top of delegates and provides a controlled publish-subscribe mechanism.
Only the declaring class can raise the event.

\`\`\`csharp
public class OrderService
{
    public event EventHandler<Order>? OrderPlaced;

    public void Place(Order order)
    {
        OrderPlaced?.Invoke(this, order);
    }
}
\`\`\`

### 3. Lambda expressions
A **lambda** is a concise way to write anonymous functions.

\`\`\`csharp
Func<int, int, int> add = (a, b) => a + b;
\`\`\`

### 4. Relationship
* Delegates define the method signature.
* Events use delegates to notify subscribers.
* Lambdas are often used to create delegate instances quickly.

### 5. Common interview examples
* Button click handlers in UI apps
* Event-driven architecture
* LINQ queries using lambdas like \`Where(x => x.IsActive)\``
      },
      {
        id: 'cs-9',
        category: 'LINQ',
        difficulty: 'Medium',
        question: 'What is deferred execution in LINQ? What are the risks and benefits?',
        answer: `**Deferred execution** means a LINQ query is not executed when it is defined, but when it is enumerated.

### 1. Example
\`\`\`csharp
var adults = users.Where(u => u.Age >= 18); // query defined, not executed yet

foreach (var user in adults)
{
    Console.WriteLine(user.Name); // execution happens here
}
\`\`\`

### 2. Benefits
* Improves efficiency by delaying work until needed.
* Enables query composition.
* Avoids unnecessary intermediate collections.

### 3. Risks
* Query may execute multiple times if enumerated repeatedly.
* If the underlying collection changes before enumeration, results may differ.
* In EF Core, deferred execution can lead to unexpected database round trips.

### 4. Materialization
Methods like \`ToList()\`, \`ToArray()\`, \`First()\`, \`Count()\` force execution.

\`\`\`csharp
var adultList = users.Where(u => u.Age >= 18).ToList();
\`\`\`

### 5. Interview takeaway
Deferred execution is powerful, but you should materialize results when you need a stable snapshot or want to avoid repeated expensive evaluation.`
      },
      {
        id: 'cs-10',
        category: 'Memory Management & IDisposable',
        difficulty: 'Hard',
        question: 'What is IDisposable in C#? How is it different from garbage collection, and when should you implement it?',
        answer: `**Garbage Collection** releases managed memory automatically, but it does **not** guarantee timely cleanup of unmanaged resources such as file handles, sockets, or database connections. That is where **IDisposable** comes in.

### 1. Why IDisposable exists
Types that own expensive external resources should implement \`IDisposable\` so callers can release them deterministically.

### 2. Common examples
* \`SqlConnection\`
* \`FileStream\`
* \`HttpResponseMessage\`
* \`Timer\`

### 3. Typical usage
\`\`\`csharp
using var stream = new FileStream("data.txt", FileMode.Open);
// use stream
\`\`\`

### 4. Dispose pattern
If your class directly owns unmanaged resources, or owns disposable children, implement dispose carefully.

\`\`\`csharp
public class ReportWriter : IDisposable
{
    private readonly StreamWriter _writer;
    private bool _disposed;

    public ReportWriter(string path)
    {
        _writer = new StreamWriter(path);
    }

    public void Dispose()
    {
        if (_disposed) return;
        _writer.Dispose();
        _disposed = true;
        GC.SuppressFinalize(this);
    }
}
\`\`\`

### 5. Key distinction
* **GC** manages memory lifetime.
* **Dispose** manages resource lifetime.

That distinction is a common interview point, especially around database connections and file operations.`
      },

      {
        id: 'cs-11',
        category: 'Delegates, Events & Lambdas',
        difficulty: 'Medium',
        question: 'What is the difference between Func<T, TResult>, Action<T>, and Predicate<T>?',
        answer: `These are generic delegates built into the .NET Base Class Library.
* **\`Func<T, TResult>\`** represents a method that takes one or more arguments and returns a value. Example: \`Func<int, int> square = x => x * x;\`.
* **\`Action<T>\`** represents a method that takes arguments but returns nothing (\`void\`). Example: \`Action<string> log = msg => Console.WriteLine(msg);\`.
* **\`Predicate<T>\`** represents a method that takes an argument and returns a \`bool\`. Example: \`Predicate<int> isEven = n => n % 2 == 0;\`.
**Key point:** \`Predicate<T>\` is essentially \`Func<T, bool>\` but predates the broader \`Func\` family. Many LINQ methods accept \`Func<T, bool>\` rather than \`Predicate<T>\`, so direct substitution can require conversion. Prefer \`Func\`/\`Action\` in new code; use \`Predicate\` mainly when interfacing with APIs like \`List<T>.Find(Predicate<T>)\`.`
      },
      {
        id: 'cs-12',
        category: 'Generics & Constraints',
        difficulty: 'Hard',
        question: 'Explain generic type constraints in C# (where T : class, struct, new(), BaseClass, Interface). How do variance (in/out) and covariance/contravariance apply to generics?',
        answer: `**Constraints** narrow what types can substitute a generic parameter.
* \`where T : class\` — reference type only.
* \`where T : struct\` — value type only (excludes \`Nullable\`).
* \`where T : new()\` — must have a public parameterless constructor.
* \`where T : BaseClass\` — must derive from or be the given base type.
* \`where T : IInterface\` — must implement the interface.
* \`where T : U\` — T must be (or derive from) another type parameter U.
**Variance** applies to interfaces and delegates, not classes:
\`\`\`csharp
IEnumerable<out T>        // covariant: IEnumerable<string> -> IEnumerable<object>
Action<in T>             // contravariant: Action<object> -> Action<string>
\`\`\`
Covariance (\`out\`) lets you treat \`IEnumerable<string>\` as \`IEnumerable<object>\` because items flow out. Contravariance (\`in\`) lets you use a more general delegate where a more specific one is expected, because input flows in.`
      },
      {
        id: 'cs-13',
        category: 'Pattern Matching',
        difficulty: 'Medium',
        question: 'Demonstrate modern C# pattern matching: property patterns, tuple patterns, list patterns, and switch expressions.',
        answer: `\`\`\`csharp
// Switch expression with property patterns
decimal discount = customer switch
{
    { IsPremium: true, YearsActive: > 5 } => 0.20m,
    { IsPremium: true } => 0.10m,
    { YearsActive: > 10 } => 0.05m,
    _ => 0m
};
// Tuple pattern
string Classify(int score, int attendance) => (score, attendance) switch
{
    (>= 90, >= 90) => "Excellent",
    (>= 60, >= 80) => "Pass",
    _ => "Fail"
};
// List pattern (C# 11+)
int SumHead(int[] arr) => arr switch
{
    [] => 0,
    [int first, ..] => first
};
// Recursive pattern with 'and' / 'or'
static string Describe(object o) => o switch
{
    int n when n > 0 => "positive int",
    string { Length: > 10 } s => s,
    null => "null",
    _ => "other"
};
\`\`\`
Pattern matching replaces nested \`if/else\` with declarative, compiler-checked logic and integrates naturally with switch expressions.`
      },
      {
        id: 'cs-14',
        category: 'Memory & Spans',
        difficulty: 'Hard',
        question: 'What are Span<T>, Memory<T>, and ReadOnlySpan<T>? How do they reduce allocations and improve performance?',
        answer: `\`Span<T>\` and \`Memory<T>\` provide a view over contiguous memory (arrays, strings, unmanaged buffers) **without copying**.
* **\`Span<T>\`** is a stack-only \`ref struct\` that wraps a pointer + length. Operations like \`Slice\` are allocation-free. Ideal for parsing, pooling, and hot paths.
* **\`Memory<T>\`** is a heap-safe counterpart (can be stored in fields) that you convert to \`Span<T>\` via \`.Span\`.
* **\`ReadOnlySpan<T>\`** is the immutable variant used for \`string\` slicing without allocating substrings.
\`\`\`csharp
ReadOnlySpan<char> ParseTag(string input)
{
    int start = input.IndexOf('<') + 1;
    int end = input.IndexOf('>', start);
    return input.AsSpan().Slice(start, end - start); // no new string
}
\`\`\`
**Rules:** Because \`Span<T>\` is a \`ref struct\`, it cannot be boxed, captured in lambdas, stored in fields, or used across \`await\` boundaries. Use \`Memory<T>\` when you need to cross those lines.`
      },
      {
        id: 'cs-15',
        category: 'Records & Value Semantics',
        difficulty: 'Medium',
        question: 'What is a C# record? Compare record, record struct, and positional vs non-positional records. How does \`with\` expressions work?',
        answer: `Records provide **value-based equality** and immutable-by-default semantics with concise syntax.
\`\`\`csharp
public record Person(string FirstName, string LastName);        // positional, reference type
public record struct Point(double X, double Y);                  // value type
public record Company { public string Name { get; init; } }      // non-positional
\`\`\`
**Value equality:** Two records with identical data compare equal automatically; \`GetHashCode\` is synthesized consistently.
**Immutability & \`with\`:** Properties are \`init\` by default, enabling non-destructive updates:
\`\`\`csharp
var p = new Person("Ada", "Lovelace");
var renamed = p with { LastName = "King" }; // creates a copy with one field changed
\`\`\`
**\`record\` vs \`record struct\`:** The former is a reference type (still gets value equality), the latter a value type. Use \`record\` when you want reference semantics with value equality (DTOs, domain models); use \`record struct\` for small, lightweight values where stack allocation matters.`
      },
      {
        id: 'cs-16',
        category: 'Async Streams',
        difficulty: 'Hard',
        question: 'Explain IAsyncEnumerable<T> and await foreach. How do they differ from Task<IEnumerable<T>>?',
        answer: `\`IAsyncEnumerable<T>\` streams items asynchronously, yielding each as it is produced instead of buffering the entire result.
\`\`\`csharp
async IAsyncEnumerable<int> StreamAsync(
    [EnumeratorCancellation] CancellationToken ct = default)
{
    foreach (var item in Source)
    {
        await Task.Delay(10, ct);
        yield return item;
    }
}
await foreach (var item in StreamAsync().WithCancellation(ct))
    Console.WriteLine(item);
\`\`\`
**Difference vs \`Task<IEnumerable<T>>\`:** A \`Task<IEnumerable>\` must fully compute the collection before returning; the caller blocks until everything is ready. \`IAsyncEnumerable\` lets the caller process the first item while later ones are still being fetched — ideal for paginated APIs, large file reads, or live data feeds where you want backpressure and lower latency to first item.`
      },
      {
        id: 'cs-17',
        category: 'Memory & GC',
        difficulty: 'Hard',
        question: 'What are boxing and unboxing? What hidden costs do they introduce, and how do you avoid them in hot paths?',
        answer: `**Boxing** wraps a value type instance into a reference type object on the heap. **Unboxing** extracts the value back.
\`\`\`csharp
int i = 42;
object o = i;      // boxing: allocates a new object, copies value
int j = (int)o;    // unboxing: type-checked copy back to stack
\`\`\`
**Hidden costs:**
* Heap allocation + GC pressure.
* Indirection and copy on every box/unbox.
* Common traps: \`ArrayList\` vs \`List<int>\`, non-generic APIs, \`Enum.GetName\` / \`Console.WriteLine\` with value types, \`IEnumerator\` (non-generic) iteration.
**Avoidance:**
* Use \`List<T>\`, \`Dictionary<TKey,TValue>\`, \`HashSet<T>\` instead of \`ArrayList\`/\`Hashtable\`.
* Prefer generic interfaces (\`IComparable<T>\` over \`IComparable\`).
* Avoid \`object\` parameters for value types in public APIs.
* Be careful with \`string.Format\` and interpolation over value types — modern interpolated handlers avoid most boxing.`
      },
      {
        id: 'cs-18',
        category: 'Nullable Reference Types',
        difficulty: 'Medium',
        question: 'How do nullable reference types (NRTs) work in C# 8+? What is the difference between \`string\`, \`string?\`, and suppressing warnings?',
        answer: `NRTs are a compile-time flow-analysis feature that distinguishes reference types that are *expected* to be null from those that are not. They do **not** change runtime behavior.
\`\`\`csharp
string name = "Ada";     // non-nullable: warnings if dereferenced when possibly null
string? maybeName = null; // nullable: must check before dereferencing
string forced = null!;   // null-forgiving operator: suppress warning, runtime still null
\`\`\`
**Flow analysis:** Compiler tracks null-state. Assigning \`null\` to a non-nullable reference produces a warning. Reading a \`string?\` without a null check produces a warning.
**Annotations vs warnings:** Warnings are enabled per-project via \`<Nullable>enable</Nullable>\`. Libraries can publish their annotations through \`NullableAttribute\` in metadata so consumers get the right warnings.
**Practical guidance:** Enable NRTs on new projects, use \`string?\` for genuinely optional inputs, avoid \`!\` except where invariants are provably true, and never treat \`!\` as a runtime guarantee.`
      },
      {
        id: 'cs-19',
        category: 'Reflection & Source Generators',
        difficulty: 'Hard',
        question: 'What are C# Source Generators? How do they improve on runtime reflection for scenarios like JSON serialization and dependency injection?',
        answer: `**Source Generators** are Roslyn analyzers that run at compile time and emit additional C# source code into the compilation. They enable **AOT-friendly** metadata inspection without runtime reflection.
**Reflection drawbacks they solve:**
* Runtime cost (type scanning, \`GetCustomAttribute\`).
* Trim-incompatibility — reflection on trimmed assemblies breaks Native AOT.
* No compile-time safety for dynamically discovered types.
**Typical uses:**
* \`System.Text.Json\` source generation (\`JsonSerializerContext\`) produces typed serialization code at build time.
* DI registration helpers that scan assemblies for interfaces and emit \`services.AddSingleton<IFoo, Foo>\` calls.
* Mapper, RPC client, and logging-template generators.
**Key rule:** Generators can only **add** files; they cannot modify existing source. They receive \`Compilation\` and \`IncrementalGeneratorInitializationContext\` (with incremental pipelines) and produce \`SourceText\` added to the build.`
      },
      {
        id: 'cs-20',
        category: 'Collections & Concurrency',
        difficulty: 'Hard',
        question: 'Compare ConcurrentDictionary, ConcurrentQueue, ConcurrentBag, and BlockingCollection. When do you choose each, and what pitfalls exist?',
        answer: `**Concurrent collections** are thread-safe variants tuned for high-contention multi-producer/multi-consumer scenarios.
* **\`ConcurrentDictionary<K,V>\`** — lock-striped dictionary. Use \`GetOrAdd\`, \`AddOrUpdate\` for atomic updates. *Pitfall:* \`GetOrAdd\` factory may run multiple times; for expensive factories use \`Lazy<T>\` values or \`GetOrAdd(key, (_, old) => old ?? factory(key))\` patterns.
* **\`ConcurrentQueue<T>\`** — lock-free FIFO. Good for work stealing pipelines. No bounded capacity.
* **\`ConcurrentBag<T>\`** — unordered, thread-local storage optimized for producer==consumer scenarios. Faster than queue when threads both produce and consume.
* **\`BlockingCollection<T>\`** — wraps a concurrent collection and adds **bounded capacity + blocking** semantics (\`Add\` blocks when full, \`Take\` blocks when empty). Ideal for producer-consumer patterns before \`System.Threading.Channels\`.
**Modern guidance:** For new producer-consumer code, prefer \`System.Threading.Channels\` (\`Channel<T>\`) over \`BlockingCollection\` — better async support, backpressure, and performance under load.`
      },
      {
        id: 'cs-21',
        category: 'Async & ValueTask',
        difficulty: 'Hard',
        question: 'When should you use ValueTask<T> instead of Task<T>? What are the rules and risks?',
        answer: `\`ValueTask<T>\` is a struct that can represent either a completed result synchronously (no allocation) or a \`Task<T>\` if the operation actually goes async.
**When to use:**
* High-throughput paths where the result is *usually* available immediately (e.g., cached reads, buffered streams).
* APIs like \`MemoryStream.ReadAsync\` and \`Socket.ReceiveAsync\` already return \`ValueTask\`.
**Rules and risks:**
* **No multiple awaits:** Awaiting a \`ValueTask\` twice, or inspecting \`.Result\` after awaiting, is undefined behavior (it may have been recycled).
* **Do not store in fields** or pass across API boundaries unless you really understand the contract.
* Only switch from \`Task\` to \`ValueTask\` after measuring — for genuinely async paths the allocation saving is negligible.
**Default recommendation:** Public library APIs should generally return \`Task\`/\`Task<T>\` for usability; use \`ValueTask\` internally on hot paths or in performance-sensitive libraries with documented constraints.`
      },
      {
        id: 'cs-22',
        category: 'Delegates & Multicast',
        difficulty: 'Medium',
        question: 'What is a multicast delegate? How do \`+=\` and \`-=\` behave, and what happens when one handler throws?',
        answer: `A **multicast delegate** holds an invocation list of multiple methods and invokes them in order when called.
\`\`\`csharp
Action<string> log = Console.WriteLine;
log += msg => File.AppendAllText("log.txt", msg);
log("hello"); // both handlers fire
\`\`\`
**\`+=\` / \`-=\`:** Create a new delegate instance (delegates are immutable). \`-=\` removes the last matching entry.
**Exception behavior:** If any handler throws, the delegate stops invoking further handlers and the exception propagates to the caller. Use \`GetInvocationList()\` to iterate manually with try/catch around each:
\`\`\`csharp
foreach (Action<string> h in log.GetInvocationList())
{
    try { h("hello"); } catch (Exception ex) { /* log */ }
}
\`\`\`
**Events vs multicast delegates:** Events restrict external callers — only the declaring class can invoke or clear the list; subscribers can only \`+=\`/\`-=\`.`
      },
      {
        id: 'cs-23',
        category: 'Equality & Hashing',
        difficulty: 'Medium',
        question: 'Explain the difference between \`==\`, \`Object.Equals\`, \`IEquatable<T>\`, and \`IEqualityComparer<T>\` in C#.',
        answer: `**\`==\`** is operator-based and can be overloaded. For reference types the default is reference equality (unless overloaded, e.g., \`string\` and records compare by value). For value types it compares bit patterns.
**\`object.Equals(object)\`** is virtual and boxed for value types; can be overridden. Use it when you need polymorphic equality or work with \`object\` references.
**\`IEquatable<T>.Equals(T)\`** is strongly typed, avoids boxing for value types, and is what \`Dictionary<T>\`, \`HashSet<T>\`, and \`LINQ.Contains\` prefer when available.
**\`IEqualityComparer<T>\`** is a *strategy* for equality supplied externally (e.g., \`StringComparer.OrdinalIgnoreCase\`), useful when you need multiple equality semantics for the same type.
**Recommendation:** For value types and records, implement \`IEquatable<T>\` and override \`GetHashCode\` consistently. Avoid overloading \`==\` for mutable reference types — it confuses callers when equality changes after mutation.`
      },
      {
        id: 'cs-24',
        category: 'LINQ & Performance',
        difficulty: 'Hard',
        question: 'What are the performance pitfalls of LINQ? How do you balance readability vs allocation overhead?',
        answer: `LINQ trades some performance for expressiveness. Common pitfalls:
* **Multiple enumeration:** \`foreach (var x in query) ...\` twice re-runs the query (and may re-execute expensive logic). Materialize with \`.ToList()\` if reused.
* **Closure allocations:** Lambdas capture variables into compiler-generated closure objects, causing heap allocations on each call. In hot paths, prefer static lambdas: \`static bool Filter(int x) => x > 0;\` then \`Where(Filter)\`.
* **Boxing of value types** in non-generic \`IEnumerable\` paths.
* **Delegate allocation** in \`Where(predicate)\` even for static methods — use \`Where(static x => x > 0)\` in C# 9+.
* **Range/chunk overhead:** \`Enumerable.Range\` and \`Chunk\` allocate arrays.
* **Lazy vs eager:** \`Select(i => Expensive(i)).Where(x => x.IsValid)\` may compute expensive items then discard them; reorder to filter first.
**Rule of thumb:** Use LINQ for clarity in 95% of code; on hot paths identified by a profiler, switch to \`for\` loops, \`Span<T>\`, or hand-written iteration.`
      },
      {
        id: 'cs-25',
        category: 'Structs & Memory Layout',
        difficulty: 'Hard',
        question: 'When should you use a struct vs a class in C#? Discuss \`readonly struct\`, \`ref struct\`, and \`StructLayout\`.',
        answer: `**Use a struct when:**
* The type is small (≤ 16 bytes ideally), logically immutable, and has value semantics.
* High allocation counts would otherwise pressure the GC (matrices, points, color values).
* You do not need inheritance (structs are sealed).
**Avoid structs when:**
* The type is large (> 16-24 bytes) — passing by value copies everything.
* It is frequently boxed (stored as \`object\`, used in non-generic APIs).
* It is mutable — mutations through property setters silently no-op on copies.
**Variants:**
* **\`readonly struct\`** — all fields readonly; compiler can pass by \`in\` reference without defensive copies.
* **\`ref struct\`** (e.g., \`Span<T>\`) — stack-only, cannot be boxed or stored on heap; perfect for transient views.
* **\`StructLayout(LayoutKind.Explicit)\`** — controls field offsets; used for interop, unions, and zero-copy parsing.
**\`in\` parameters** pass structs by readonly reference to avoid copies: \`void M(in BigStruct s)\`.`
      },
      {
        id: 'cs-26',
        category: 'Async & Cancellation',
        difficulty: 'Medium',
        question: 'How does CancellationToken work? Where should you check it, and what is \`ThrowIfCancellationRequested\`?',
        answer: `\`CancellationToken\` propagates a cancellation signal from a caller to async/compute operations. It is a lightweight struct wrapping a \`CancellationTokenSource\`.
**Sources:**
\`\`\`csharp
using var cts = new CancellationTokenSource();
cts.CancelAfter(TimeSpan.FromSeconds(5));
await DoWorkAsync(cts.Token);
\`\`\`
**Where to check:**
* At await boundaries (async APIs accept \`CancellationToken\` and forward it to underlying I/O).
* In CPU-bound loops: \`token.ThrowIfCancellationRequested();\` periodically.
* Before non-cancellable external calls — at least check \`token.IsCancellationRequested\` to break early.
**\`ThrowIfCancellationRequested\`** throws \`OperationCanceledException\`, which is the canonical way to surface cancellation. Callers can distinguish cancellation (a coordinated stop) from a fault.
**Best practice:** Thread \`CancellationToken\` through every async method signature, never swallow it silently, and avoid \`Task.Run\` without passing the token (otherwise the work cannot be cancelled cleanly).`
      },
      {
        id: 'cs-27',
        category: 'Memory & Object Pooling',
        difficulty: 'Hard',
        question: 'What is ObjectPool<T> and ArrayPool<T>? How do they reduce GC pressure, and what are the safety rules?',
        answer: `**Pooling** reuses allocated buffers/objects instead of creating and discarding them, drastically reducing GC pressure for high-throughput code.
**\`ArrayPool<T>.Shared\`** rents and returns arrays:
\`\`\`csharp
byte[] buffer = ArrayPool<byte>.Shared.Rent(4096);
try { /* fill, send, parse */ }
finally { ArrayPool<byte>.Shared.Return(buffer, clearArray: true); }
\`\`\`
**\`ObjectPool<T>\`** (Microsoft.Extensions.ObjectPool) does the same for arbitrary objects (e.g., StringBuilder, parsers).
**Safety rules:**
* Always return what you rent (use \`try/finally\`).
* Do not hold references to rented buffers after returning — they may be handed to another caller.
* The rented array may be *larger* than requested; track the logical length separately.
* Use \`clearArray: true\` when returning buffers that contained sensitive data.
* Rent sizes in powers of two where possible — pools are bucketed.
**When to use:** hot paths with frequent allocation (network buffers, JSON parsing, log formatting). Avoid for one-shot or low-frequency allocation — pool overhead isn't free.`
      },
      {
        id: 'cs-28',
        category: 'Reflection & Emit',
        difficulty: 'Hard',
        question: 'Compare reflection, expression trees, and dynamic IL emit for building fast object mappers/serializers. What are the trade-offs?',
        answer: `**Reflection (\`Type.GetMethod\`, \`FieldInfo.GetValue\`)**
* Easiest to write, slowest to run, and trim/AOT-unfriendly.
* Good for tooling, one-off inspection, prototypes.
**Expression Trees (\`System.Linq.Expressions\`)**
* Build a delegate at runtime by composing expression nodes; compile to a fast delegate.
* Faster than reflection (one-time compile cost), much easier than raw IL.
* Used by Dapper, EF Core, and many mappers.
**Dynamic Method / \`Reflection.Emit\` (\`ILGenerator\`)**
* Emit raw IL for maximum speed; closest to hand-written code.
* Hardest to maintain, platform-sensitive (no AOT), and easy to get wrong.
* Reserved for performance-critical libraries (e.g., JSON serializers, serializers in .NET 8+).
**Modern alternative:** Source generators compile-time generate the metadata-access code, giving near-IL speed with AOT compatibility. Prefer them in new libraries; reserve \`Reflection.Emit\` for runtime-only scenarios (e.g., proxy generation in mocking libraries).`
      },
      {
        id: 'cs-29',
        category: 'OOP & Pattern Design',
        difficulty: 'Medium',
        question: 'Explain the difference between abstract classes and interfaces in modern C#. When do you use each, especially with default interface methods?',
        answer: `**Abstract class:**
* Single inheritance only.
* Can hold state (fields), constructors, protected members, and shared implementation.
* Use when subclasses share meaningful *implementation* and a true "is-a" relationship.
**Interface:**
* Multiple implementation.
* No instance fields (default interface methods since C# 8 can have implementation, but only via other interface methods or static fields).
* Use for capabilities/contracts ("can-do") that cut across the class hierarchy — \`IDisposable\`, \`IEnumerable\`, \`IComparable\`.
**Default interface methods (DIM):**
* Let an interface add new members without breaking implementers.
* Classic use: adding \`IEnumerable<T>.Max()\` extension-style methods directly on the interface.
* Caller must be typed as the interface to use the default; class methods still override.
**Rule of thumb:** Default to interfaces for contracts; reach for abstract classes only when there is genuine shared implementation that subclasses must inherit. With records, prefer composition over inheritance wherever possible.`
      },
      {
        id: 'cs-30',
        category: 'Configuration & Hosting',
        difficulty: 'Medium',
        question: 'How do you write a background service in .NET using IHostedService and BackgroundService? What pitfalls exist?',
        answer: `\`BackgroundService\` is the base class for long-running hosted work in ASP.NET Core or generic hosts.
\`\`\`csharp
public class Worker : BackgroundService
{
    private readonly ILogger<Worker> _log;
    public Worker(ILogger<Worker> log) => _log = log;
    protected override async Task ExecuteAsync(CancellationToken stoppingToken)
    {
        while (!stoppingToken.IsCancellationRequested)
        {
            _log.LogInformation("Tick");
            await Task.Delay(TimeSpan.FromSeconds(5), stoppingToken);
        }
    }
}
// Registration
builder.Services.AddHostedService<Worker>();
\`\`\`
**Pitfalls:**
* Forgetting to pass \`stoppingToken\` into \`Task.Delay\` / async calls — the service won't shut down promptly.
* Letting \`ExecuteAsync\` throw — the host logs the error and may not restart the service; wrap the loop body in try/catch.
* Doing blocking work (e.g., \`Thread.Sleep\`) — blocks the host shutdown.
* Using \`AddHostedService<T>\` registers \`T\` as a singleton behind the scenes; do not inject scoped services directly — use \`IServiceScopeFactory\` to create a scope per iteration.
* For queued work, prefer \`IHostedService\` + a channel over \`Task.Run\` fire-and-forget.`
      }

    ]
  },
  {
    id: 'aspnet-guide',
    title: 'ASP.NET Core & Web API',
    subtitle: 'ASP.NET Core & Web API Interview Questions & Design Best Practices',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Layout',
    description: 'Deep dive into Middleware, Dependency Injection, JWT/OAuth auth, API versioning, Minimal APIs, gRPC, and caching.',
    questions: [
      {
        id: 'asp-1',
        category: 'Middleware & Architecture',
        difficulty: 'Medium',
        question: 'What is ASP.NET Core Middleware? How is it structured and executed in the request lifecycle?',
        answer: `**Middleware** consists of components assembled into an application pipeline to handle requests and responses. Each component can decide to pass the request to the next component in the pipeline, and perform actions before or after the next component.

### 1. Execution Order (Pipeline)
The pipeline is visualised as a Russian Doll (inward and outward flow):
\`\`\`
Request -> [Middleware 1] -> [Middleware 2] -> Endpoint (Controller)
Response <- [Middleware 1] <- [Middleware 2] <- Endpoint (Controller)
\`\`\`

### 2. Built-in Pipeline Example
\`\`\`csharp
var builder = WebApplication.CreateBuilder(args);
var app = builder.Build();

app.UseExceptionHandler("/error"); // 1. Exception handling first
app.UseHttpsRedirection();         // 2. HTTPS enforcement
app.UseStaticFiles();              // 3. Serve static files (short circuits if matched)
app.UseRouting();                  // 4. Match URL to Endpoints
app.UseAuthentication();           // 5. Who is the user?
app.UseAuthorization();            // 6. What can the user access?

app.MapControllers();              // 7. Route terminal endpoint execution
app.Run();
\`\`\`
*CRITICAL:* Order is crucial. Having authentication *after* authorization will crash or result in major security vulnerabilities.

### 3. Custom Middleware Creation
Create a custom middleware class with an \`InvokeAsync\` method accepting a \`HttpContext\`.

\`\`\`csharp
public class RequestLoggingMiddleware {
    private readonly RequestDelegate _next;
    public RequestLoggingMiddleware(RequestDelegate next) => _next = next;

    public async Task InvokeAsync(HttpContext context) {
        Console.WriteLine($"Incoming Request: {context.Request.Path}");
        await _next(context); // Call the next middleware
        Console.WriteLine($"Response Status: {context.Response.StatusCode}");
    }
}
\`\`\``
      },
      {
        id: 'asp-2',
        category: 'Dependency Injection (DI)',
        difficulty: 'Medium',
        question: 'Explain the three service lifetimes in ASP.NET Core DI: Transient, Scoped, and Singleton. How does Captive Dependency occur?',
        answer: `ASP.NET Core boasts an integrated Dependency Injection container that supports three primary lifetimes:

| Lifetime | Description |
| :--- | :--- |
| **Transient** | Created **every time** they are requested. Ideal for lightweight, stateless services. |
| **Scoped** | Created **once per client request** (HTTP connection scope). Excellent for Entity Framework DbContexts. |
| **Singleton** | Created **the first time** they are requested and stay alive for the lifetime of the application. |

### Captive Dependency (Major Pitfall)
*   **Definition:** Occurs when a service with a longer lifetime depends on a service with a shorter lifetime (e.g., a **Singleton** service injected with a **Scoped** service).
*   **The Problem:** The Scoped service is kept alive forever inside the Singleton container, essentially trapping it as a Singleton. If the Scoped service keeps holds on resources (like DB connections or transaction flags), it causes major memory leaks, lock failures, and data corruption.
*   **Default Guard:** In ASP.NET Core's default settings in development mode, the container will throw an \`InvalidOperationException\` at startup if it detects a captive dependency configuration. Keep this validation turned on in staging/production where possible.`
      },
      {
        id: 'asp-3',
        category: 'Security & Auth',
        difficulty: 'Hard',
        question: 'Describe JWT Authentication inside ASP.NET Core. What is the role of access tokens, refresh tokens, and token validation parameters?',
        answer: `**JSON Web Token (JWT)** is a stateless auth standard defining a compact, self-contained method for security credentials transmission.

### 1. Token Anatomy
*   **Header:** Alg (signature type), Type (JWT)
*   **Payload:** Claims (e.g., UserId, Email, Roles, Expiry time)
*   **Signature:** Cryptographically signs Header + Payload using a server secret key to guarantee tamper-resistance.

### 2. Implementation Lifecycle
1.  **Authentication:** Client sends username/password to login endpoint. If valid, the server returns a JWT *Access Token* (short expiration, e.g., 15 mins) and optionally copies a *Refresh Token* (stored securely in database, long expiration, e.g., 7 days).
2.  **Authorization:** Client attaches the access token in HTTP header: \`Authorization: Bearer <token>\` on subsequent requests.
3.  **Middleware Execution:** \`JwtBearerHandler\` intercepts the request, decrypts, and performs validation parameters check.

### 3. Key TokenValidationParameters
\`\`\`csharp
builder.Services.AddAuthentication(JwtBearerDefaults.AuthenticationScheme)
    .AddJwtBearer(options => {
        options.TokenValidationParameters = new TokenValidationParameters {
            ValidateIssuer = true,
            ValidateAudience = true,
            ValidateLifetime = true, // Ensure not expired
            ValidateIssuerSigningKey = true, // Verify signature cryptographically
            ValidIssuer = "my-auth-server",
            ValidAudience = "my-api",
            IssuerSigningKey = new SymmetricSecurityKey(Encoding.UTF8.GetBytes(secretKey))
        };
    });
\`\`\``
      },
      {
        id: 'asp-4',
        category: 'Routing & Endpoints',
        difficulty: 'Medium',
        question: 'What is the difference between Conventional Routing, Attribute Routing, and Minimal APIs in ASP.NET Core?',
        answer: `ASP.NET Core supports multiple routing styles, each useful in different scenarios.

### 1. Conventional Routing
Routes are defined centrally, usually in startup configuration.
This was common in MVC apps.

\`\`\`csharp
app.MapControllerRoute(
    name: "default",
    pattern: "{controller=Home}/{action=Index}/{id?}");
\`\`\`

### 2. Attribute Routing
Routes are placed directly on controllers and actions.
This gives more control and is preferred for APIs.

\`\`\`csharp
[ApiController]
[Route("api/[controller]")]
public class ProductsController : ControllerBase
{
    [HttpGet("{id}")]
    public IActionResult Get(int id) => Ok();
}
\`\`\`

### 3. Minimal APIs
Minimal APIs let you define endpoints with minimal ceremony and are great for lightweight services, internal APIs, and microservices.

\`\`\`csharp
app.MapGet("/products/{id}", (int id) => Results.Ok(new { Id = id }));
\`\`\`

### 4. When to use which
* **Conventional Routing:** MVC page-based apps.
* **Attribute Routing:** REST APIs needing explicit route control.
* **Minimal APIs:** Lightweight endpoints and fast bootstrapping.`
      },
      {
        id: 'asp-5',
        category: 'Configuration & Options',
        difficulty: 'Medium',
        question: 'How does configuration work in ASP.NET Core? Explain appsettings.json, environment-specific settings, secrets, and the Options pattern.',
        answer: `ASP.NET Core configuration is provider-based and merges values from multiple sources.

### 1. Common configuration sources
* \`appsettings.json\`
* \`appsettings.{Environment}.json\`
* environment variables
* command-line arguments
* user secrets in development
* Azure Key Vault or other secret stores

Later providers override earlier ones.

### 2. Strongly typed Options pattern
The **Options pattern** maps configuration into a typed class.

\`\`\`csharp
public class JwtOptions
{
    public string Issuer { get; set; } = "";
    public string Audience { get; set; } = "";
}
\`\`\`

\`\`\`csharp
builder.Services.Configure<JwtOptions>(
    builder.Configuration.GetSection("Jwt"));
\`\`\`

### 3. Benefits
* Centralized config access
* Compile-time safety
* Easier testing
* Cleaner dependency injection

### 4. Best practice
Keep secrets out of source control.
Use user secrets locally and secret stores such as Key Vault in cloud deployments.`
      },
      {
        id: 'asp-6',
        category: 'Filters & Cross-Cutting Concerns',
        difficulty: 'Medium',
        question: 'What are filters in ASP.NET Core MVC/Web API? Compare authorization, resource, action, exception, and result filters.',
        answer: `Filters let you run logic at specific points in the MVC request pipeline.

### 1. Authorization Filters
Run first and determine whether the request is allowed to proceed.

### 2. Resource Filters
Run around the rest of the pipeline and are useful for caching or short-circuiting expensive work.

### 3. Action Filters
Run before and after controller actions.
Useful for validation, logging, and enrichment.

### 4. Exception Filters
Handle exceptions thrown by actions or later filters.
They are useful in MVC, though middleware is often preferred for global exception handling.

### 5. Result Filters
Run before and after the action result executes.
Useful for modifying headers or response formatting.

### 6. Interview takeaway
Use **middleware** for app-wide concerns and **filters** for MVC-specific concerns tied to controllers/actions.`
      },
      {
        id: 'asp-7',
        category: 'Caching',
        difficulty: 'Medium',
        question: 'Explain response caching, in-memory caching, and distributed caching in ASP.NET Core. When should each be used?',
        answer: `Caching improves performance, but different cache types solve different problems.

### 1. Response Caching
Caches full HTTP responses based on headers and cache policy.
Useful for public GET endpoints where the same response can be safely reused.

### 2. In-Memory Cache
Stores objects in the memory of a single app instance.

\`\`\`csharp
builder.Services.AddMemoryCache();
\`\`\`

Best for:
* local lookups
* computed reference data
* single-instance apps

### 3. Distributed Cache
Stores cache data in an external shared system like Redis or SQL Server.

\`\`\`csharp
builder.Services.AddStackExchangeRedisCache(options =>
{
    options.Configuration = "localhost:6379";
});
\`\`\`

Best for:
* multi-instance deployments
* session/state sharing
* scalable applications

### 4. Rule of thumb
* **Response cache** for HTTP payload reuse
* **Memory cache** for app-local speed
* **Distributed cache** for scale-out consistency`
      },
      {
        id: 'asp-8',
        category: 'API Design',
        difficulty: 'Hard',
        question: 'What are some best practices for designing REST APIs in ASP.NET Core?',
        answer: `A good REST API is predictable, versionable, secure, and easy for clients to consume.

### 1. Use resource-oriented routes
Prefer nouns over verbs.

* Good: \`GET /api/orders/123\`
* Avoid: \`GET /api/getOrderById/123\`

### 2. Use correct HTTP methods
* \`GET\` for reads
* \`POST\` for create
* \`PUT\` for full update
* \`PATCH\` for partial update
* \`DELETE\` for delete

### 3. Return proper status codes
* \`200 OK\`
* \`201 Created\`
* \`204 No Content\`
* \`400 Bad Request\`
* \`401 Unauthorized\`
* \`403 Forbidden\`
* \`404 Not Found\`
* \`409 Conflict\`

### 4. Validate input consistently
Use model validation and return structured validation errors.

### 5. Version your API
Use URL, header, or query-string versioning to avoid breaking clients.

### 6. Secure by default
Use authentication, authorization, rate limiting, and output filtering for sensitive fields.`
      },
      {
        id: 'asp-9',
        category: 'Observability & Logging',
        difficulty: 'Medium',
        question: 'How should logging and observability be implemented in ASP.NET Core applications?',
        answer: `Production APIs need more than console logs. They need structured observability.

### 1. Structured logging
Use structured logs with named properties instead of building large string messages.

\`\`\`csharp
logger.LogInformation("Order {OrderId} created for customer {CustomerId}", orderId, customerId);
\`\`\`

### 2. Correlation
Include correlation IDs or trace IDs so requests can be followed across services.

### 3. Metrics
Track request count, latency, error rates, cache hit ratio, queue depth, and DB timings.

### 4. Tracing
Distributed tracing helps follow a request across API, queue, cache, and database boundaries.

### 5. Practical tools
Common choices include OpenTelemetry, Application Insights, Seq, ELK, Datadog, or Grafana-based stacks.

### 6. Interview takeaway
Good observability reduces MTTR dramatically during production incidents.`
      },
      {
        id: 'asp-10',
        category: 'Performance',
        difficulty: 'Hard',
        question: 'What are common performance optimizations for ASP.NET Core Web APIs?',
        answer: `Performance work should target the real bottleneck, but some patterns are consistently valuable.

### 1. Make I/O asynchronous
Use async database and HTTP calls to avoid thread blocking.

### 2. Reduce payload size
Use DTO projection, pagination, compression, and avoid over-fetching.

### 3. Cache expensive reads
Use memory or distributed caching for hot paths.

### 4. Optimize database access
Avoid N+1 queries, missing indexes, and over-eager loading.

### 5. Use pooling and reuse
Reuse \`HttpClient\` through \`IHttpClientFactory\`.
Use DbContext correctly with scoped lifetime.

### 6. Minimize middleware and serialization overhead
Keep the pipeline lean and avoid unnecessary per-request work.

### 7. Measure
Use profiling, tracing, and load testing before and after changes.
Interviewers usually care more about disciplined measurement than random micro-optimizations.`
      },
      {
        id: 'asp-11',
        category: 'Minimal APIs',
        difficulty: 'Medium',
        question: 'How do Minimal APIs handle dependency injection, filters, and validation in .NET 8/9?',
        answer: `Minimal APIs use **parameter binding** to inject services — the framework resolves parameters from registered services, route values, query, body, and headers based on type.

\`\`\`csharp
app.MapPost("/orders", async (CreateOrderRequest req, IOrderService svc, 
    CancellationToken ct, ILogger<Program> log) =>
{
    var id = await svc.CreateAsync(req, ct);
    return Results.Created($"/orders/{id}", id);
})
.WithParameterValidation() // .NET 9 built-in validation
.WithSummary("Create a new order");
\`\`\`

**Filters:** Apply endpoint filters via \`AddEndpointFilter\` or \`IEndpointFilter\` implementations for cross-cutting concerns (logging, validation, auth):

\`\`\`csharp
app.MapGet("/items/{id}", (int id) => ...).AddEndpointFilter<LoggingFilter>();
\`\`\`

**Validation:** .NET 9 introduces \`WithParameterValidation()\` for automatic DataAnnotations-based validation, returning \`400\` with structured errors. For complex validation, use FluentValidation or custom filters returning \`Results.ValidationProblem()\`.`
      },
      {
        id: 'asp-12',
        category: 'gRPC',
        difficulty: 'Hard',
        question: 'How does gRPC work in ASP.NET Core? When is it preferable to REST, and what about gRPC-Web?',
        answer: `**gRPC** uses HTTP/2 + Protocol Buffers for strongly-typed, high-performance RPC between services.

\`\`\`proto
service OrderService {
  rpc Create (CreateOrderRequest) returns (CreateOrderReply);
  rpc StreamUpdates (stream OrderId) returns (stream OrderUpdate);
}
\`\`\`

**Strengths:**
* Binary Protobuf payloads — smaller and faster than JSON.
* Bi-directional and server/client streaming natively.
* Strong contracts via \`proto\` files; auto-generated clients.
* HTTP/2 multiplexing reduces connection overhead.

**When to prefer gRPC:** internal service-to-service, real-time streaming, high-throughput backends.

**Limitations:** Browsers cannot speak HTTP/2 gRPC directly. **gRPC-Web** translates via a proxy/envelope so browser apps can call gRPC services (no streaming from browser in older setups; .NET 8 supports gRPC-Web streaming).

**Auth:** Typically uses bearer tokens (JWT) over the same \`Authorization\` header, or mTLS for service-to-service trust.`
      },
      {
        id: 'asp-13',
        category: 'Resilience',
        difficulty: 'Hard',
        question: 'How do you implement resilience in ASP.NET Core 8/9 with Microsoft.Extensions.Resilience and Polly v8?',
        answer: `.NET 8 introduces **\`Microsoft.Extensions.Resilience\`** (built on Polly v8) and **\`Microsoft.Extensions.Http.Resilience\`** for HTTP clients.

\`\`\`csharp
// HTTP client with resilience handler
builder.Services.AddHttpClient("api")
    .AddResilienceHandler("pipeline", pipelineBuilder =>
    {
        pipelineBuilder.AddRetry(new HttpRetryStrategyOptions
        {
            MaxRetryAttempts = 3,
            Delay = TimeSpan.FromMilliseconds(500),
            BackoffType = DelayBackoffType.Exponential
        });
        pipelineBuilder.AddCircuitBreaker(new HttpCircuitBreakerStrategyOptions
        {
            SamplingDuration = TimeSpan.FromSeconds(10),
            FailureRatio = 0.5,
            MinimumThroughput = 8
        });
        pipelineBuilder.AddTimeout(TimeSpan.FromSeconds(5));
    });
\`\`\`

**Key features:** pipelines are named, configurable per client, and integrate telemetry through OpenTelemetry automatically. The strategy uses **hedging** (\`AddHedging\`) optionally to send parallel requests when latency matters.

**Why prefer it over raw Polly:** DI-friendly, observable, and standardized across the application, with consistent retry/backoff/circuit-breaker semantics rather than ad-hoc per-call wrapping.`
      },
      {
        id: 'asp-14',
        category: 'AuthN/AuthZ',
        difficulty: 'Hard',
        question: 'Explain Cookie Authentication vs JWT Bearer vs OAuth2 OpenID Connect in ASP.NET Core. When do you use each?',
        answer: `**Cookie Authentication:** Server-issued encrypted cookie; best for traditional server-rendered MVC/Razor Pages with browser sessions. Stateful on the server side (or distributed cache for multi-instance). Suitable for first-party web apps behind TLS.

**JWT Bearer:** Stateless token; client sends \`Authorization: Bearer <jwt>\`. Best for SPA/mobile clients calling APIs. Server validates signature + claims with no session storage. Pair with refresh tokens to keep access tokens short-lived.

**OAuth2 / OpenID Connect:** Delegated auth protocol. Use when your app must authenticate users via an external provider (Google, Microsoft Entra, Auth0). ASP.NET Core's \`AddOpenIdConnect\` handles the authorization-code flow with PKCE. OIDC adds an identity layer on top of OAuth2 for user identity claims.

**Common hybrid:** Browser session via cookies for the MVC UI, and a separate JWT bearer scheme for the API the SPA calls. The UI exchanges its cookie for an access token (via an OAuth back-channel) before calling the API.`
      },
      {
        id: 'asp-15',
        category: 'Performance & Output Caching',
        difficulty: 'Medium',
        question: 'How does Output Caching middleware differ from Response Caching in ASP.NET Core 8/9?',
        answer: `**Response Cache (\`[ResponseCache]\`)** emits HTTP cache headers (\`Cache-Control\`, \`ETag\`) and supports an in-process response cache. It's mostly client/proxy-side caching controlled by headers.

**Output Cache middleware (\`AddOutputCache\`, .NET 7+)** caches *full responses server-side* keyed by route, query string, headers, and policies you define.

\`\`\`csharp
builder.Services.AddOutputCache(options =>
{
    options.AddPolicy("Expensive", b => b.Expire(TimeSpan.FromMinutes(5))
        .SetVaryByQuery("id"));
});

app.UseOutputCache();

app.MapGet("/products/{id}", (int id) => ...).CacheOutput("Expensive");
\`\`\`

**Key advantages:** supports cache eviction, tag-based invalidation (\`IOutputCacheStore.EvictByTagAsync\`), and Redis-backed distributed storage via \`AddStackExchangeRedisOutputCache\`. Use it for expensive server-rendered GETs; use Response Cache when the same benefit can be achieved by header-driven client caching.`
      },
      {
        id: 'asp-16',
        category: 'Rate Limiting',
        difficulty: 'Medium',
        question: 'How does the built-in Rate Limiting middleware work in ASP.NET Core 7+?',
        answer: `\`System.Threading.RateLimitting\` provides fixed, sliding, token, and concurrency limiters, wired into the pipeline via \`AddRateLimiter\`.

\`\`\`csharp
builder.Services.AddRateLimiter(opts =>
{
    opts.AddFixedWindowLimiter("fixed", o =>
    {
        o.PermitLimit = 100;
        o.Window = TimeSpan.FromMinutes(1);
    });
    opts.AddTokenBucketLimiter("bucket", o =>
    {
        o.TokenLimit = 100;
        o.TokensPerPeriod = 10;
        o.ReplenishmentPeriod = TimeSpan.FromSeconds(6);
    });
    opts.GlobalLimiter = Partitioned.CreateLimiter(
        ctx => RateLimitPartition.GetFixedWindowLimiter(
            ctx.Connection.RemoteIpAddress?.ToString() ?? "anon",
            _ => new FixedWindowRateLimiterOptions { PermitLimit = 1000, Window = TimeSpan.FromMinutes(1) }));
});

app.UseRateLimiter();
\`\`\`

**Partitioning** lets you rate-limit per user, per IP, per tenant. Custom policies can return 429 with \`Retry-After\` headers and tweak response via \`OnRejected\`. Apply named limits per endpoint with \`RequireRateLimiting("fixed")\`.`
      },
      {
        id: 'asp-17',
        category: 'Testing',
        difficulty: 'Medium',
        question: 'How do you write integration tests for ASP.NET Core APIs using WebApplicationFactory?',
        answer: `\`WebApplicationFactory<TEntryPoint>\` boots the app in-memory (TestServer) so you can hit real endpoints without an external host.

\`\`\`csharp
public class OrdersApiTests : IClassFixture<WebApplicationFactory<Program>>
{
    private readonly HttpClient _client;
    public OrdersApiTests(WebApplicationFactory<Program> factory)
    {
        _client = factory.WithWebHostBuilder(b =>
        {
            b.ConfigureServices(s =>
            {
                s.RemoveAll<DbContextOptions<AppDb>>();
                s.AddDbContext<AppDb>(o => o.UseInMemoryDatabase("test"));
            });
        }).CreateClient();
    }

    [Fact]
    public async Task Get_Orders_ReturnsOk()
    {
        var resp = await _client.GetAsync("/api/orders");
        Assert.Equal(HttpStatusCode.OK, resp.StatusCode);
    }
}
\`\`\`

**Best practices:** reset the in-memory DB between tests, mock external HTTP via \`IHttpClientFactory\` and \`StubHttpDelegatingHandler\`, and use \`Program\` partial declarations so the test assembly can reference the entry point.`
      },
      {
        id: 'asp-18',
        category: 'Cross-Cutting & MediatR',
        difficulty: 'Medium',
        question: 'What is the Mediator pattern (e.g., MediatR) in ASP.NET Core? How does it decouple handlers, and what are its trade-offs?',
        answer: `\`MediatR\` routes requests (commands/queries) to handlers via a single \`IMediator\` interface, eliminating direct service-to-service dependencies.

\`\`\`csharp
public record CreateOrderCommand(string Sku, int Qty) : IRequest<Guid>;

public class CreateOrderHandler : IRequestHandler<CreateOrderCommand, Guid>
{
    public async Task<Guid> Handle(CreateOrderCommand req, CancellationToken ct)
    { /* persist */ return Guid.NewGuid(); }
}

// In controller
var id = await _mediator.Send(new CreateOrderCommand("A1", 2));
\`\`\`

**Cross-cutting via pipeline behaviors:** \`IPipelineBehavior<TReq,TResp>\` adds logging, validation, transactions, etc. without touching handlers.

**Trade-offs:**
* Pro: Single entry point, easy to add behaviors, decouples sender from handler.
* Con: Indirection hurts traceability; too many small handlers can fragment logic. Single-instance pipeline registration is subtle — make handlers registered as \`scoped\` or \`transient\`, not \`singleton\`. Avoid MediatR for in-process RPC where direct calls would be clearer.`
      },
      {
        id: 'asp-19',
        category: 'Hosting & Kestrel',
        difficulty: 'Hard',
        question: 'What is Kestrel? How do you tune it for high-throughput production workloads?',
        answer: `\`Kestrel\` is the cross-platform web server built into ASP.NET Core, based on \`System.IO.Pipelines\` and \`Http2\`/\`Http3\` sockets. It can serve traffic directly or sit behind IIS/Nginx/App Gateway.

**Tuning options:**

\`\`\`csharp
builder.WebHost.ConfigureKestrel(o =>
{
    o.Limits.MaxConcurrentConnections = 1000;
    o.Limits.MaxRequestBodySize = 10 * 1024 * 1024;
    o.Limits.KeepAliveTimeout = TimeSpan.FromSeconds(120);
    o.Limits.RequestHeadersTimeout = TimeSpan.FromSeconds(30);
    o.ListenAnyIP(5000, lo => lo.Protocols = HttpProtocols.Http1AndHttp2);
});
\`\`\`

**High-throughput levers:**
* Use HTTP/2 or HTTP/3 to multiplex requests over a single connection.
* Increase \`ThreadPool\` min threads (\`ThreadPool.MinThreads\`) when async I/O saturates.
* Enable \`SocketsHttpHandler\` PooledConnectionLifetime tuning for outbound HTTP.
* Run behind a reverse proxy that terminates TLS and offloads static files.
* On Linux, raise \`ulimit -n\` (file descriptors) — Kestrel needs one per connection.`
      },
      {
        id: 'asp-20',
        category: 'Configuration & Secrets',
        difficulty: 'Medium',
        question: 'How do you override configuration in ASP.NET Core across environments and feature flags?',
        answer: `ASP.NET Core merges multiple configuration sources in order; later sources override earlier ones.

\`\`\`csharp
builder.Configuration
    .AddJsonFile("appsettings.json", optional: false)
    .AddJsonFile($"appsettings.{env}.json", optional: true)
    .AddJsonFile("appsettings.Local.json", optional: true) // dev overrides, not in source control
    .AddEnvironmentVariables()
    .AddUserSecrets<Program>(optional: env.IsDevelopment())
    .AddAzureKeyVault(...) // cloud secret store
    .AddCommandLine(args);
\`\`\`

**Feature flags:** \`Microsoft.FeatureManagement\` lets you toggle behavior via config:

\`\`\`csharp
builder.Services.AddFeatureManagement();
app.MapGet("/dashboard", [FeatureGate("NewDashboard")] () => ...);
\`\`\`

**Best practices:** Never commit secrets; use \`dotnet user-secrets\` locally, Azure Key Vault / AWS Secrets Manager in cloud; map configs to \`IOptions<T>\` with \`IOptionsMonitor<T>\` for hot-reload where supported; use \`IOptionsSnapshot<T>\` for per-request scoped values.`
      },
      {
        id: 'asp-21',
        category: 'Blazor',
        difficulty: 'Medium',
        question: 'Compare Blazor Server, Blazor WebAssembly, and Blazor Web App (.NET 8) unified model.',
        answer: `**Blazor Server:** Components run on the server over a SignalR connection. Fast initial load, full .NET access, but each user has a live WebSocket connection (scaling needs backplane like Azure SignalR). Latency depends on round-trip to server.

**Blazor WebAssembly:** Components run inside the browser via WebAssembly. Slower initial download (CLR + app DLLs), but no server per-interaction; works offline (PWA). Limited to browser capabilities (no direct file/DB).

**Blazor Web App (.NET 8+):** Unified model combining both — initial render happens on the server (fast first paint, SEO-friendly), then interactive rendering happens via WebAssembly or Server based on per-component configuration:

\`\`\`razor
@rendermode InteractiveWebAssembly
\`\`\`

**Choosing:** Use Web App (interactive) by default — it gives fast first paint, scales to client interactivity, and supports streaming render. Pure WASM is reserved for offline scenarios; pure Server for internal apps with modest user counts.`
      },
      {
        id: 'asp-22',
        category: 'Streaming & File Uploads',
        difficulty: 'Hard',
        question: 'How do you handle large file uploads efficiently in ASP.NET Core without buffering entire files in memory?',
        answer: `Use **streaming** with \`IFormFile\` for moderate sizes, or **unbuffered streaming** (\`Request.BodyReader\`) for very large uploads.

\`\`\`csharp
app.MapPost("/upload", async (HttpRequest req, CancellationToken ct) =>
{
    var reader = req.BodyReader;
    await using var fs = File.Create("upload.bin");
    while (true)
    {
        var result = await reader.ReadAsync(ct);
        foreach (var seg in result.Buffer)
            await fs.WriteAsync(seg);
        reader.AdvanceTo(result.Buffer.End);
        if (result.IsCompleted) break;
    }
    return Results.Ok();
});
\`\`\`

**Important settings:** raise \`Kestrel\` \`MaxRequestBodySize\` and \`MultipartBodyLengthLimit\` (\`FormOptions.MultipartBodyLengthLimit = long.MaxValue\`).

**Patterns:**
* For multi-GB uploads, prefer **chunked upload + resumable** endpoints (TUS protocol or custom).
* Stream straight to blob storage using \`CopyToAsync\` on the underlying stream.
* Never trust \`Content-Type\` or filename — validate and sanitize.`
      },
      {
        id: 'asp-23',
        category: 'Async/Await & Deadlocks',
        difficulty: 'Hard',
        question: 'What are common async pitfalls in ASP.NET Core, and how does the absence of SynchronizationContext change behavior?',
        answer: `**Key differences vs classic ASP.NET:**
* ASP.NET Core has **no SynchronizationContext** in request handling — continuations run on thread pool threads. This is why \`MyMethod().Result\` does *not* deadlock by default the way it does on UI threads or legacy ASP.NET.
* That doesn't make sync-over-async safe — it still **causes thread starvation** by blocking a pool thread while async work needs another to complete.

**Pitfalls:**
* **\`async void\`** — exceptions crash the process, callers cannot await. Use \`async Task\`.
* **Forgotten awaits** — \`Task.Run(...)\` without await or \`_\` capture; exceptions become unobserved and may fire only on GC.
* **Mixing \`ConfigureAwait(false)\` in libraries** — fine, but in Core app code it's redundant.
* **CancellationToken not threaded** — long operations can't be cancelled, harming graceful shutdown.
* **Blocking on async in middleware** — deadlocks with \`IResult\` execution paths that expect async.

**Best practice:** Make the entire call chain async top-to-bottom; never block on a Task in server code.`
      },
      {
        id: 'asp-24',
        category: 'Real-Time & SignalR',
        difficulty: 'Hard',
        question: 'How does SignalR scale in production? Discuss backplanes, groups, and connection management.',
        answer: `\`SignalR\` provides persistent connections and RPC from server to client over WebSockets (with Server-Sent Events and long-polling fallbacks).

**Scaling problem:** Each server holds WebSocket connections for connected clients. When a server broadcasts a message, only clients connected to *that* server receive it.

**Backplanes** (Redis, Azure SignalR Service) fan out messages across all server instances:

\`\`\`csharp
builder.Services.AddSignalR().AddStackExchangeRedis(redisConn);
// or
builder.Services.AddSignalR().AddAzureSignalR(connectionString);
\`\`\`

**Groups:** Logical partitioning — \`Groups.AddToGroupAsync(connId, "team-42")\` lets you broadcast only to subscribers without enumerating connections.

**Connection management:**
* Track users via \`IUserIdProvider\` for per-user messaging across multiple tabs/devices.
* Handle \`OnConnectedAsync\` / \`OnDisconnectedAsync\` to clean up state.
* Use \`IHubContext<T>\` to push from non-Hub code (controllers, background services).
* On disconnect, optionally drain pending messages into a queue or DB so reconnecting clients can catch up.

**Azure SignalR Service** is the production default — it manages the connection mesh, scales to tens of thousands of connections, and supports serverless mode via Functions.`
      },
      {
        id: 'asp-25',
        category: 'API Versioning',
        difficulty: 'Medium',
        question: 'How do you implement API versioning in ASP.NET Core 8/9?',
        answer: `Use \`Asp.Versioning.Mvc\` (the community successor to \`Microsoft.AspNetCore.Mvc.Versioning\`):

\`\`\`csharp
builder.Services.AddApiVersioning(o =>
{
    o.DefaultApiVersion = new ApiVersion(1, 0);
    o.AssumeDefaultVersionWhenUnspecified = true;
    o.ReportApiVersions = true;
})
.AddMvc()
.AddApiExplorer(opts =>
{
    opts.GroupNameFormat = "'v'VVV";
});
\`\`\`

**Strategies:**
* URL segment: \`[Route("api/v{version:apiVersion}/[controller]")]\`
* Query string: \`?api-version=2.0\`
* Header: \`X-Api-Version: 2.0\`

**Per-controller:**
\`\`\`csharp
[ApiController]
[ApiVersion("1.0")]
[ApiVersion("2.0")]
[Route("api/v{version:apiVersion}/orders")]
public class OrdersController : ControllerBase { }
\`\`\`

**Best practice:** Maintain v1 and v2 side-by-side with shared internal services; version bump for breaking changes only (contract additions can stay in the same major version with care). Use OpenAPI (\`Swashbuckle\`/\`Microsoft.AspNetCore.OpenApi\`) with versioned docs.`
      },
      {
        id: 'asp-26',
        category: 'Globalization & Localization',
        difficulty: 'Medium',
        question: 'How do you implement localization and culture-aware APIs in ASP.NET Core?',
        answer: `\`\`\`csharp
builder.Services.AddLocalization(o => o.ResourcesPath = "Resources");
builder.Services.Configure<RequestLocalizationOptions>(o =>
{
    var supported = new[] { "en-US", "fr-FR", "de-DE" };
    o.SetDefaultCulture(supported[0])
     .AddSupportedCultures(supported)
     .AddSupportedUICultures(supported);
});

app.UseRequestLocalization();
\`\`\`

**Detection order:** \`QueryStringRequestCultureProvider\` (\`?culture=fr\`), \`CookieRequestCultureProvider\`, \`AcceptLanguageHeaderRequestCultureProvider\`.

**Resources:** Use \`IStringLocalizer<T>\` (key-based) or \`IStringLocalizerFactory\` with \`.resx\` files keyed by culture. For JSON-based catalogs, plug in a custom \`IStringLocalizerFactory\` reading from a database or Azure Blob.

**Best practices:** Culture must be threaded through async flows (it's tied to \`CultureInfo.CurrentCulture\`); be careful with background threads — set culture explicitly. Localize validation messages and model-binding errors via \`ModelBindingMessageProvider\`.`
      },
      {
        id: 'asp-27',
        category: 'Observability & OpenTelemetry',
        difficulty: 'Hard',
        question: 'How do you instrument an ASP.NET Core app with OpenTelemetry for traces, metrics, and logs?',
        answer: `\`\`\`csharp
builder.Services.AddOpenTelemetry()
    .ConfigureResource(r => r.AddService("orders-api"))
    .WithTracing(t => t
        .AddAspNetCoreInstrumentation()
        .AddHttpClientInstrumentation()
        .AddEntityFrameworkCore()
        .AddOtlpExporter()) // OTLP to collector
    .WithMetrics(m => m
        .AddAspNetCoreInstrumentation()
        .AddHttpClientInstrumentation()
        .AddRuntimeInstrumentation()
        .AddOtlpExporter());
\`\`\`

**Logs:** Bridge \`ILogger\` to OTel via \`AddOpenTelemetry().WithLogging(l => l.AddOtlpExporter())\` in .NET 8+, or use \`AddOpenTelemetry\` + \`AddOtlpLogExporter\`.

**Key concepts:**
* **Traces** = distributed spans across service boundaries (auto-instrumented by ASP.NET Core and HTTP client).
* **Metrics** = aggregations (request rate, error rate, GC, threadpool).
* **Logs** = structured events correlated to trace IDs.

**Backends:** OTLP ships to Jaeger, Tempo, Honeycomb, Datadog, Azure Monitor, etc. Use \`Activity.Current?.TraceId\` to log correlation IDs.

**Rule:** Always propagate \`traceparent\` headers to downstream services — auto-instrumentation handles this for HttpClient and gRPC.`
      },
      {
        id: 'asp-28',
        category: 'Background Jobs',
        difficulty: 'Medium',
        question: 'How do you implement scheduled/recurring background jobs in ASP.NET Core without Quartz/Hangfire?',
        answer: `For simple recurring jobs, a \`BackgroundService\` with a timer loop suffices:

\`\`\`csharp
public class CacheRefresher : BackgroundService
{
    protected override async Task ExecuteAsync(CancellationToken ct)
    {
        using var timer = new PeriodicTimer(TimeSpan.FromMinutes(5));
        while (await timer.WaitForNextTickAsync(ct))
        {
            try { await RefreshCacheAsync(ct); }
            catch (Exception ex) { _log.LogError(ex, "refresh failed"); }
        }
    }
}
\`\`\`

**\`IHostedLifecycleService\` (.NET 8+)** hooks into host start/stop for one-time work.

**When to reach for a real scheduler:**
* Need persistence (survive restarts): Hangfire, Quartz.NET with ADOJobStore.
* Distributed coordination (only one instance runs the job at a time): use a distributed lock (Redis \`SETNX\`) or a database lease.
* Cron expressions, retries, dashboards: Hangfire or Quartz.

**\`PeriodicTimer\`** is preferred over \`System.Timers.Timer\` — it doesn't overlap ticks and supports cancellation natively.`
      },
      {
        id: 'asp-29',
        category: 'Containers & AOT',
        difficulty: 'Hard',
        question: 'How do you publish an ASP.NET Core app as a container with Native AOT in .NET 8/9?',
        answer: `\`dotnet publish\` can produce a Docker image and (in .NET 8+) Native AOT binaries.

**Container publish (built-in since .NET 7):**

\`\`\`xml
<PropertyGroup>
  <ContainerFamily>jammy-chiseled</ContainerFamily>
  <ContainerBaseImage>mcr.microsoft.com/dotnet/runtime-deps:8.0-jammy</ContainerBaseImage>
  <PublishSingleFile>true</PublishSingleFile>
</PropertyGroup>
\`\`\`

\`\`\`bash
dotnet publish -p:PublishProfile=DefaultContainer -c Release
\`\`\`

**Native AOT (console/minimal API in .NET 9):**

\`\`\`xml
<PropertyGroup>
  <PublishAot>true</PublishAot>
  <InvariantGlobalization>true</InvariantGlobalization>
</PropertyGroup>
\`\`\`

**Constraints of AOT:**
* No runtime reflection/codegen; replace with source generators.
* Limited runtime-compiled regex and dynamic IL emit.
* Some libraries (heavy reflection-based serializers, EF Core lazy proxies) need source-generated variants.
* Cold-start is dramatically faster; binary size shrinks; memory footprint lower.

**Trade-off:** AOT shines for short-lived serverless (Functions, AWS Lambda) and micro-PK APIs; for large monoliths with EF + reflection, full CoreCLR publish is still more flexible.`
      },
      {
        id: 'asp-30',
        category: 'Security & OWASP',
        difficulty: 'Hard',
        question: 'How do you mitigate OWASP Top 10 risks (SQLi, XSS, CSRF, SSRF) in ASP.NET Core?',
        answer: `**SQL Injection:** Use parameterized queries via EF Core or \`SqlParameter\`. Never concatenate user input into SQL strings.

\`\`\`csharp
// Safe
context.Orders.Where(o => o.Name == userInput).ToList();
// Manual
new SqlParameter("@name", userInput)
\`\`\`

**XSS (Cross-Site Scripting):** Razor auto-encodes output by default. Avoid \`@Html.Raw()\` unless content is sanitized. Validate and sanitize rich-text input (e.g., Ganss.XSS).

**CSRF:** Auto-antiforgery tokens in MVC forms via \`[AutoValidateAntiforgeryToken]\`. For SPA + JWT bearer APIs, CSRF is generally not applicable (no cookies); for cookie-based APIs, use \`SameSite=Strict\` and double-submit tokens.

**SSRF (Server-Side Request Forgery):** Never let users pass arbitrary URLs to \`HttpClient\`. Validate and restrict hostnames; reject link-local/private IPs; use allow-lists; consider a dedicated egress proxy.

**Other:** Force HTTPS via \`UseHttpsRedirection\` + HSTS; security headers (\`X-Content-Type-Options\`, \`Content-Security-Policy\`) via middleware or \`NetEscapade.AspNetCore.SecurityHeaders\`; rate limiting (covered earlier); logging with redaction of sensitive fields.`
      }


    ]
  },
  {
    id: 'sql-guide',
    title: 'SQL Server Master',
    subtitle: 'SQL Server Database Tuning & Core Query Design',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Database',
    description: 'Conquer Indexing internals, Transactions, Isolation Levels, Deadlock resolution, Query profiling, and practical SQL.',
    questions: [
      {
        id: 'sql-1',
        category: 'Indexes & Query Optimization',
        difficulty: 'Hard',
        question: 'What is the physical difference between a Clustered Index and a Non-Clustered Index? Explain "Key Lookup" and how to eliminate it.',
        answer: `Indexes are essential B-Tree data structures used to speed up record lookup times.

### 1. Clustered vs. Non-Clustered Indexes
*   **Clustered Index:**
    *   Determines the **physical sorting order** of data rows on disk.
    *   The leaf nodes contain the **actual data rows**.
    *   Only **one** clustered index can exist per table.
*   **Non-Clustered Index:**
    *   A structure completely **separate** from the data table.
    *   The leaf nodes contain the index key columns and a **row locator/pointer** back to the actual data (a Clustered Index Key or Heap RID).
    *   SQL Server supports up to 999 non-clustered indexes per table.

### 2. What is a Key Lookup?
If a query uses a Non-Clustered Index to find matching records, but requests columns that are *not* inside that index, the engine must perform a **Key Lookup** (or RID lookup). It uses the primary key pointer to fetch those missing values from the data page.
*   *Performance Impact:* Doing hundreds of random disk Lookups is highly inefficient.

### 3. How to Eliminate Key Lookups
You can eliminate Key Lookups using **Covering Indexes** using the \`INCLUDE\` clause:
\`\`\`sql
-- Creates an index covering Name lookup while storing Email in the leaf nodes
CREATE NONCLUSTERED INDEX IX_Employees_Name
ON Employees (FirstName, LastName)
INCLUDE (Email);
\`\`\`
By including \`Email\` in the index leaf nodes, the query optimizer can return FirstName, LastName, and Email *entirely* from the index B-tree without touching data pages (Index Link cover).`
      },
      {
        id: 'sql-2',
        category: 'Transactions & Locking',
        difficulty: 'Hard',
        question: 'Compare SQL Server Isolation Levels: Read Uncommitted, Read Committed, Repeatable Read, Snapshot, and Serializable. What anomalies does each solve?',
        answer: `Transaction isolations manage critical data anomalies during concurrent access.

### Anomalies Definitions
1.  **Dirty Read:** Read uncommitted data modified by another pending transaction.
2.  **Non-Repeatable Read:** Re-reading the same row within a transaction returns different values (due to updates).
3.  **Phantom Read:** Re-running a range query returns new rows inserted by another transaction.

### Isolation Level Feature Matrix
| Isolation Level | Dirty Reads | Non-Repeatable Reads | Phantom Reads | Mechanism / Lock Overhead |
| :--- | :--- | :--- | :--- | :--- |
| **Read Uncommitted** | Yes | Yes | Yes | No read locks. |
| **Read Committed** (Default) | **No** | Yes | Yes | Short-lived read locks. |
| **Repeatable Read** | No | **No** | Yes | Read locks held until transaction completes. |
| **Snapshot** | No | No | **No** | Optimistic concurrency using row versioning (tempdb). |
| **Serializable** | No | No | **No** | Range locks (highest lock contention cost). |

### Snapshot Isolation vs. Read Committed Snapshot (RCSI)
*   **RCSI:** Reads always see the last committed version of the row before the statement started. Eliminates reader/writer blocking without code changes.
*   **Snapshot:** Provides transaction-level consistency. If the transaction tries to update a row that has changed since the transaction started, a write-collision conflict error is thrown.`
      },
      {
        id: 'sql-3',
        category: 'Indexes & Query Optimization',
        difficulty: 'Medium',
        question: 'What is the difference between a seek and a scan in SQL Server, and why does it matter?',
        answer: `A **seek** means SQL Server can navigate efficiently to the needed rows using an index key.
A **scan** means SQL Server reads many or all rows/pages to find matches.

### 1. Index Seek
Efficient when the predicate is selective and aligned with the index structure.

### 2. Index Scan / Table Scan
May be acceptable for small tables, but costly for large ones.

### 3. Why it matters
Scans increase I/O, CPU, and latency, especially under concurrency.

### 4. Common causes of scans
* missing indexes
* non-sargable predicates
* poor cardinality estimates
* functions applied to indexed columns

Example of non-sargable predicate:

\`\`\`sql
WHERE YEAR(OrderDate) = 2026
\`\`\`

Better:

\`\`\`sql
WHERE OrderDate >= '2026-01-01'
  AND OrderDate < '2027-01-01'
\`\`\`

That lets the optimizer use an index more effectively.`
      },
      {
        id: 'sql-4',
        category: 'Execution Plans',
        difficulty: 'Hard',
        question: 'How do you read a SQL Server execution plan, and which operators usually indicate performance problems?',
        answer: `Execution plans show how SQL Server chose to execute a query.

### 1. What to inspect first
* highest cost operators
* actual vs estimated row counts
* warnings such as spills, implicit conversions, or missing indexes

### 2. Common problematic operators
* **Key Lookup** repeated many times
* **Sort** on large data sets
* **Hash Match** when memory is insufficient
* **Table Scan** on large tables
* **Nested Loops** with unexpectedly large outer inputs

### 3. Cardinality mismatch
If estimated rows are far from actual rows, the optimizer may choose the wrong join strategy or memory grant.

### 4. Interview takeaway
Execution plan analysis is less about memorizing icons and more about spotting:
* excessive reads
* bad join choices
* poor estimates
* missing covering indexes`
      },
      {
        id: 'sql-5',
        category: 'Stored Procedures & Parameter Sniffing',
        difficulty: 'Hard',
        question: 'What is parameter sniffing in SQL Server, and how do you fix or mitigate it?',
        answer: `**Parameter sniffing** happens when SQL Server compiles a plan using the first set of parameter values and reuses that plan for later executions, even when those values have very different selectivity.

### 1. Why it is a problem
A plan that is great for a highly selective value may be terrible for a broad value, or vice versa.

### 2. Symptoms
* inconsistent query performance
* same stored procedure fast sometimes and slow other times
* big mismatch in logical reads depending on parameter values

### 3. Mitigations
* \`OPTION (RECOMPILE)\`
* local variables in some cases
* \`OPTIMIZE FOR\`
* query rewriting
* filtered indexes
* plan guides or Query Store forcing where appropriate

### 4. Trade-off
There is no universal fix.
Forcing recompilation improves plan quality but increases compilation overhead.
The right choice depends on frequency and variability of parameter distribution.`
      },
      {
        id: 'sql-6',
        category: 'Normalization & Design',
        difficulty: 'Medium',
        question: 'What are normalization and denormalization? When should you choose each?',
        answer: `**Normalization** organizes data to reduce redundancy and improve consistency.
**Denormalization** intentionally duplicates or precomputes data to improve read performance.

### 1. Normalization
Benefits:
* less duplication
* easier updates
* stronger integrity

Typical use:
* transactional systems such as orders, payments, customers

### 2. Denormalization
Benefits:
* fewer joins
* faster reads
* easier reporting in some workloads

Typical use:
* analytics
* dashboards
* read-heavy systems

### 3. Trade-off
Normalization favors write correctness.
Denormalization favors read speed, often at the cost of more complex updates and potential consistency challenges.`
      },
      {
        id: 'sql-7',
        category: 'Transactions & Locking',
        difficulty: 'Hard',
        question: 'What is a deadlock in SQL Server? How do you detect, prevent, and resolve it?',
        answer: `A **deadlock** occurs when two or more sessions block each other in a circular dependency and SQL Server chooses one as the deadlock victim.

### 1. Example
Transaction A locks row 1 and wants row 2.
Transaction B locks row 2 and wants row 1.

### 2. Detection
* deadlock graphs
* Extended Events
* SQL Server Profiler in older setups
* application error logs showing deadlock victim errors

### 3. Prevention strategies
* access objects in a consistent order
* keep transactions short
* reduce lock scope
* add useful indexes so queries touch fewer rows
* use appropriate isolation levels
* avoid long user interactions inside transactions

### 4. Resolution
The application should retry deadlock-victim operations where safe.
Deadlocks are often a design issue, not just a database issue.`
      },
      {
        id: 'sql-8',
        category: 'Joins & Query Design',
        difficulty: 'Medium',
        question: 'Explain INNER JOIN, LEFT JOIN, RIGHT JOIN, FULL JOIN, and CROSS JOIN with practical use cases.',
        answer: `Joins combine rows from multiple tables based on a relationship.

### 1. INNER JOIN
Returns only matching rows from both tables.
Use when the relationship must exist.

### 2. LEFT JOIN
Returns all rows from the left table and matching rows from the right table.
Use when you want all customers, even those without orders.

### 3. RIGHT JOIN
Same idea as left join, but keeps all rows from the right table.
It is less commonly used because query readability is often better with LEFT JOIN.

### 4. FULL JOIN
Returns rows that match plus unmatched rows from both sides.
Useful in reconciliation reports.

### 5. CROSS JOIN
Returns the Cartesian product of both tables.
Useful rarely, such as generating combinations of dimensions.

Interviewers usually care less about the definitions and more about whether you know when a join can accidentally multiply rows and distort aggregates.`
      },
      {
        id: 'sql-9',
        category: 'Window Functions',
        difficulty: 'Hard',
        question: 'What are SQL window functions, and why are they powerful?',
        answer: `Window functions perform calculations across a set of rows related to the current row without collapsing the result set like \`GROUP BY\`.

### 1. Common functions
* \`ROW_NUMBER()\`
* \`RANK()\`
* \`DENSE_RANK()\`
* \`SUM() OVER(...)\`
* \`AVG() OVER(...)\`
* \`LAG()\`
* \`LEAD()\`

### 2. Example
\`\`\`sql
SELECT
    EmployeeId,
    Department,
    Salary,
    ROW_NUMBER() OVER(PARTITION BY Department ORDER BY Salary DESC) AS RowNum
FROM Employees;
\`\`\`

### 3. Use cases
* top N per group
* running totals
* previous/next row comparison
* trend analysis
* deduplication

Window functions are a common senior-level interview topic because they solve many reporting problems elegantly without complex self-joins.`
      },
      {
        id: 'sql-10',
        category: 'Temp Objects',
        difficulty: 'Medium',
        question: 'What is the difference between CTEs, temporary tables, and table variables in SQL Server?',
        answer: `These are all ways to structure intermediate query logic, but they behave differently.

### 1. CTE
A **Common Table Expression** improves readability and is scoped to a single statement.
It does not inherently materialize data.

### 2. Temporary Table
A **temporary table** such as \`#Temp\` is stored in tempdb and can be indexed.
Useful for larger intermediate result sets and multi-step processing.

### 3. Table Variable
A **table variable** is also stored in tempdb internally, but historically had weaker cardinality estimates in some scenarios.
It is often better for smaller row counts.

### 4. Rule of thumb
* **CTE:** readability and one-statement logic
* **temp table:** larger reusable intermediate sets
* **table variable:** small scoped data sets

The correct choice often depends on row volume, indexing needs, and execution plan quality.`
      },
      {
        id: 'sql-11',
        category: 'Statistics & Cardinality Estimation',
        difficulty: 'Hard',
        question: 'How do statistics affect the query optimizer, and what does "cardinality estimation" actually control in an execution plan?',
        answer: `Statistics are histograms and density information that describe the distribution of values in a column or set of columns. The **Query Optimizer** uses them to estimate how many rows each operator will produce, and those estimates drive join strategy, memory grants, and plan shape.
### 1. What statistics contain
* **Histogram:** up to 200 steps showing value range, EQ_ROWS, RANGE_ROWS, and DISTINCT_RANGE_ROWS.
* **Density vector:** how selective a multi-column key is.
* **Updated counter:** when the statistic was last refreshed.
### 2. Why estimates matter
If SQL Server thinks a scan returns 10 rows but it actually returns 10 million, it may pick Nested Loops instead of Hash Join, request too little memory, and spill to tempdb.
### 3. Common problems
* stale statistics after large inserts/deletes
* ascending key problem (newest values not yet in the histogram)
* multi-column statistics that misrepresent correlation between columns
* filtered statistics that do not cover all predicates
* implicit conversions making predicates non-sargable, which hurts estimates as well as seeks
### 4. Practical fixes
* update statistics with FULLSCAN when data distribution changes significantly
* use auto-update statistics async to avoid synchronous query stalls
* consider filtered statistics on skewed partitions
* enable Query Store and look for plans whose estimates are wildly off
### 5. SQL Server 2014+ Cardinality Estimator
The "New CE" changed assumptions about ascending keys and correlated predicates. Some workloads regress on upgrade; you can use trace flags or Query Store to force the old plan while you re-tune.`
      },
      {
        id: 'sql-12',
        category: 'Wait Statistics',
        difficulty: 'Hard',
        question: 'Explain how you use sys.dm_os_wait_stats and extended events to diagnose performance. Which waits matter most?',
        answer: `Wait statistics tell you what sessions are actually waiting for, which is more useful than CPU or disk counters in isolation.
### 1. The right starting point
\`sys.dm_os_wait_stats\` aggregates waits across the instance since restart or since the stats were cleared. Always snapshot before and after a workload:
\`\`\`sql
DBCC SQLPERF('sys.dm_os_wait_stats', CLEAR);
-- run the workload
SELECT * FROM sys.dm_os_wait_stats ORDER BY waiting_tasks_count DESC;
\`\`\`
### 2. Waits worth investigating
* **PAGEIOLATCH_xx / ASYNCH_IO_COMPLETION** — disk subsystem pressure
* **WRITELOG** — transaction log flush latency
* **PAGELATCH_xx** — contention on a data page (often last-page insert hotspot)
* **LCK_M_xx** — lock waits indicating blocking
* **SOS_SCHEDULER_YIELD** — CPU pressure or spinlock contention
* **RESOURCE_SEMAPHORE** — insufficient memory grants causing queries to wait
* **CXPACKET** — parallelism waits, sometimes normal, sometimes a sign of bad skew
* **SLEEP_BPOOL_FLUSH** — checkpoint or lazy writer I/O burst
### 3. Ignorable waits
\`SLEEP_TASK\`, \`BROKER_TASK_STOP\`, \`LOGMGR_QUEUE\`, \`XE_DISPATCHER_WAIT\` are usually benign background activity.
### 4. Moving to the session level
Aggregate waits are a signal. To find the actual query, use \`sys.dm_exec_requests\` joined with \`sys.dm_os_waiting_tasks\`, or capture an Extended Events session with \`wait_completed\` and \`sql_batch_completed\`.
### 5. Interview framing
Do not chase any single wait in isolation. The goal is to correlate waits with a specific query, plan, and resource bottleneck, then change the workload, schema, or configuration to address root cause.`
      },
      {
        id: 'sql-13',
        category: 'Index Strategy',
        difficulty: 'Hard',
        question: 'When would you choose a Columnstore Index over a traditional B-Tree, and what are the trade-offs?',
        answer: `**Columnstore indexes** store data column-by-column in compressed segments. They are designed for analytic workloads with large scans and aggregations.
### 1. Where columnstore wins
* fact tables with millions to billions of rows
* aggregation queries (SUM, AVG, COUNT, GROUP BY)
* star-schema joins between dimension and fact tables
* read-mostly workloads where compression matters
### 2. Why it is fast
* Each column is stored separately, so a query scanning three columns reads only those three columns.
* Run-length encoding and dictionary compression reduce I/O dramatically.
* Batch mode execution processes rows in batches of ~1024, reducing CPU per row.
### 3. Trade-offs
* Updates and deletes used to be problematic. SQL Server 2014 introduced **clustered columnstore** with delete bitmaps, and later versions improved row-level updates via delta stores.
* Random OLTP-style inserts are still slower than B-Tree inserts.
* Small tables do not benefit because segment overhead outweighs compression.
* OLTP queries touching few rows by key still prefer B-Tree seeks.
### 4. Common patterns
* Clustered columnstore on the fact table
* traditional B-Tree nonclustered indexes on dimension keys
* ordered clustered columnstore (SQL 2017+) when you frequently filter by a date or partition key
* nonclustered columnstore on an OLTP table to power operational analytics without a separate warehouse
### 5. Interview takeaway
Columnstore is not a "better B-Tree." It is a different physical design optimized for a different workload shape. Choosing it for an OLTP table with single-row lookups will usually be a regression.`
      },
      {
        id: 'sql-14',
        category: 'Partitioning',
        difficulty: 'Hard',
        question: 'Explain table partitioning in SQL Server. When is it useful, and what are the common mistakes?',
        answer: `Table partitioning splits a single logical table into multiple physical units based on a partition key, while still presenting it as one table to queries.
### 1. Real benefits
* **Partition switching** allows loading or archiving huge data ranges in metadata-only operations.
* Partition-level maintenance, such as rebuilding only the current month's index.
* Improved manageability for very large tables where a single index rebuild would be too costly.
* Partition elimination during queries that filter on the partition key.
### 2. What partitioning is NOT
* It is not a magic performance boost for arbitrary queries. If a query does not filter on the partition key, every partition is scanned.
* It does not eliminate lock contention by itself.
* It does not replace indexing.
### 3. Common mistakes
* choosing a partition key that does not align with query predicates
* partitioning small tables where the overhead outweighs the benefit
* forgetting aligned indexes — partition switching requires indexes to be aligned
* too many partitions, increasing metadata and plan-search cost
* using partitioning to work around poor query design
### 4. Typical pattern
A sales fact table partitioned by transaction date. Each month is a partition. A staging table with the same schema and partition scheme is loaded during the night, then switched in with \`ALTER TABLE ... SWITCH PARTITION\`. Old months are switched out to an archive table.
### 5. Interview takeaway
Partitioning is an operational technique. Performance gains usually come from partition elimination and switch-based loads, not from partitioning itself.`
      },
      {
        id: 'sql-15',
        category: 'TempDB Internals',
        difficulty: 'Hard',
        question: 'Why is TempDB so critical in SQL Server, and how do you configure and troubleshoot it correctly?',
        answer: `TempDB is the shared scratch space used for user temp tables, table variables, sort spills, hash joins, row versioning, and many internal operations.
### 1. Why it matters
Almost every workload touches TempDB. Contention here manifests as instance-wide slowdowns even when user databases are healthy.
### 2. Configuration best practices
* One data file per logical core, up to about 8 files. Beyond that, add only if contention persists.
* All TempDB data files should be the **same size and same autogrowth setting** to avoid uneven allocation.
* Prefer fixed sizes and manual growth over aggressive autogrowth.
* Use trace flag 1117 (grow all files together) and 1118 (uniform extent allocation) on versions before SQL 2016 — they are default behavior in SQL 2016+.
* Place on fast storage; do not colocate with user database logs.
### 3. Common contention points
* **PAGELATCH_xx on SGAM or PFS pages** — classic file allocation contention, fixed with multiple equally sized files.
* **Allocation contention in tempdb cache** — addressed by 1118.
* **Latch contention from heavy temp table creation** — table variable caching and reuse help.
* **Version store overflow** — long-running transactions under Snapshot or RCSI keep row versions alive in TempDB.
### 4. Troubleshooting queries
\`sys.dm_os_waiting_tasks\` filtered for resource_description containing '2:1:*' (database_id 2 = tempdb) reveals TempDB contention in real time.
### 5. Interview framing
A well-configured TempDB is invisible. A misconfigured one is the root cause of mysterious instance-wide performance drops.`
      },
      {
        id: 'sql-16',
        category: 'Concurrency Deep Dive',
        difficulty: 'Hard',
        question: 'Compare pessimistic and optimistic concurrency in SQL Server. When does each win?',
        answer: `Pessimistic and optimistic concurrency are the two fundamental strategies for handling read/write interaction.
### 1. Pessimistic concurrency
Writers acquire locks; readers also acquire locks (under Repeatable Read or Serializable). The system assumes conflicts are likely and prevents them.
**Strengths:**
* predictable behavior
* no write-conflict errors thrown to the application
* simple to reason about under low contention
**Weaknesses:**
* readers block writers and vice versa
* deadlocks more likely under long transactions
* scaling limits under heavy concurrency
### 2. Optimistic concurrency
Readers see a previously committed version of the row maintained in the version store (TempDB). Writers do not block readers; conflict is detected at write time.
**Two flavors:**
* **RCSI (Read Committed Snapshot Isolation):** statement-level consistency, no code changes required.
* **Snapshot Isolation:** transaction-level consistency, requires explicit \`SET TRANSACTION ISOLATION LEVEL SNAPSHOT\` and write-conflict handling.
### 3. Trade-offs
Optimistic concurrency moves cost from locking to row-versioning in TempDB. Long-running transactions can grow the version store significantly.
Optimistic concurrency can throw update conflicts, which the application must handle with retry logic.
### 4. Choosing
* OLTP workloads with high read concurrency usually benefit from RCSI.
* Workloads with frequent updates to the same hot rows may be better under pessimistic isolation to avoid conflict retries.
* Reporting workloads paired with ETL often benefit from Snapshot to keep readers consistent during long-running reads.
### 5. Interview takeaway
Neither strategy is universally better. The question is which cost — blocking vs. version store + conflict retries — fits the workload.`
      },
      {
        id: 'sql-17',
        category: 'Query Anti-Patterns',
        difficulty: 'Medium',
        question: 'What are the most common T-SQL anti-patterns you encounter in production code, and how do you fix them?',
        answer: `Production T-SQL usually goes wrong in predictable ways. Recognizing them quickly is a senior-engineer skill.
### 1. RBAR — Row By Agonizing Row
Cursors or WHILE loops doing what should be a set-based operation.
**Fix:** rewrite as a single UPDATE/INSERT/MERGE statement. Use window functions instead of correlated subqueries.
### 2. Non-sargable predicates
\`WHERE YEAR(OrderDate) = 2026\` prevents index seek.
**Fix:** \`WHERE OrderDate >= '2026-01-01' AND OrderDate < '2027-01-01'\`.
### 3. Implicit conversions
Comparing a VARCHAR column to an NVARCHAR parameter forces a conversion on the indexed column, often turning a seek into a scan.
**Fix:** align data types between the column, parameter, and join columns.
### 4. Wildcard-first LIKE
\`WHERE Name LIKE '%Smith'\` cannot use an index seek.
**Fix:** full-text search, or accept the scan with a covering index.
### 5. SELECT * in production
Pulls unnecessary columns, defeats covering indexes, and breaks code when schema changes.
**Fix:** list columns explicitly.
### 6. Multi-statement TVFs
These are black boxes to the optimizer and often produce terrible plans.
**Fix:** inline TVFs (SQL Server treats them like parameterized views) or replace with stored procedures using temp tables.
### 7. Function on indexed column in WHERE
\`WHERE ISNULL(Status,'') = 'Active'\` blocks seeks.
**Fix:** rewrite as \`WHERE Status = 'Active' OR Status IS NULL\` or use a computed column with an index.
### 8. OVER(PARTITION BY ... ) without an index
Window functions on large unindexed partitions cause sorts.
**Fix:** add a supporting index aligned with the PARTITION BY and ORDER BY columns.
The common thread: each anti-pattern either defeats the optimizer or makes it work harder than necessary.`
      },
      {
        id: 'sql-18',
        category: 'Index Maintenance',
        difficulty: 'Medium',
        question: 'How do you maintain indexes and statistics in production without causing downtime or regression?',
        answer: `Index maintenance is about balancing fragmentation removal against the cost of the maintenance itself.
### 1. What fragmentation actually does
* **Logical fragmentation (forward scans):** mostly affects traditional spinning-disk reads.
* **Page density:** low density means more pages, more I/O, more memory.
* On modern NVMe storage and in-memory workloads, logical fragmentation matters far less than page density.
### 2. Rebuild vs. Reorganize
* **REBUILD:** creates a new index and swaps. Can be done online with Enterprise edition.
* **REORGANIZE:** lighter, single-threaded, always online but does not update statistics with FULLSCAN.
### 3. Smart maintenance rules
* Only rebuild indexes above a fragmentation threshold (e.g., > 30% rebuild, 5–30% reorganize).
* Skip small indexes; the overhead rarely pays off.
* Update statistics after rebuilds automatically happen; after reorganize they do not.
* Schedule statistics updates independently when distribution changes — auto-update only fires when enough rows change.
### 4. Avoiding regressions
* Do not rebuild every index every night. Many never fragment again.
* Use \`sys.dm_db_index_physical_stats\` to drive maintenance.
* Be careful with online rebuilds: they need extra space and can cause blocking if a long transaction holds locks on the table.
### 5. Statistics-specific work
* Ascending key columns need more frequent stats updates.
* Multi-column statistics on correlated columns may need filtered statistics.
### 6. Interview takeaway
A senior engineer's maintenance plan is selective, evidence-driven, and quiet. It does not blindly rebuild every index in the database every night.`
      },
      {
        id: 'sql-19',
        category: 'In-Memory OLTP',
        difficulty: 'Hard',
        question: 'What are Memory-Optimized Tables in SQL Server, when do they help, and what are the gotchas?',
        answer: `In-Memory OLTP, introduced in SQL Server 2014, places entire tables in memory with a completely different storage and concurrency engine.
### 1. Where it helps
* extreme contention on a hot table, such as a session or queue
* latching bottlenecks on insert-heavy OLTP
* very low-latency single-row operations
* scenarios where traditional locking is the bottleneck
### 2. How it differs
* Data lives entirely in memory, with a checkpoint file stream persisted to disk.
* Native compiled stored procedures compile to native C, bypassing the interpreter.
* Optimistic concurrency at the row level — no locks, no latches.
* Hash indexes (in addition to nonclustered range indexes) for point lookups.
### 3. Limitations
* Memory is the constraint. A 200GB table fits comfortably on disk; in-memory OLTP means reserving 200GB+ of RAM.
* Earlier versions had significant restrictions on data types, foreign keys, and T-SQL surface area. Most were removed by SQL 2016+ but some still exist.
* Hash indexes need careful bucket count sizing. Under-sized buckets cause significant overflow chains.
* Native compiled procedures have a smaller T-SQL surface area than interpreted ones.
* Off-row data (LOB, large VARCHAR) has performance characteristics to plan around.
### 4. When NOT to use it
* "Make this query faster" — usually a tuning problem, not an In-Memory problem.
* Large analytics tables — columnstore is a better fit.
* General-purpose OLTP — well-tuned traditional tables are usually sufficient.
### 5. Interview takeaway
In-Memory OLTP is a niche tool. Senior engineers reach for it when they have measured a specific latch/lock bottleneck that indexing and query tuning cannot solve.`
      },
      {
        id: 'sql-20',
        category: 'High Availability',
        difficulty: 'Hard',
        question: 'Compare Always On Availability Groups, Failover Cluster Instances, and Log Shipping. When do you choose each?',
        answer: `These three features solve overlapping but distinct resilience problems.
### 1. Failover Cluster Instance (FCI)
* Shared-storage cluster: the instance runs on one node, failover moves the instance to another node using shared disks.
* Protects against server failure, not storage failure.
* No readable secondary.
* Best when you need instance-level resilience with a single copy of the data.
### 2. Always On Availability Groups (AG)
* One primary, up to 8 secondaries (recent versions: more).
* Secondaries can be **synchronous** (zero data loss on failover) or **asynchronous** (near-zero RPO, slight risk on failover).
* Secondaries are **readable** by default in SQL 2016+.
* Multiple AGs allow different databases to fail over independently.
* Requires a Windows Server Failover Cluster (on Windows) but does not share storage.
### 3. Log Shipping
* Periodic transaction log backups restored to a standby server.
* Simpler than AG, supports delayed restore (e.g., 4 hours behind) as a defense against accidental DROP or ransomware.
* Manual or scripted failover.
* Lower RPO/RTO than AG, but very robust and cheap.
### 4. Decision matrix
| Requirement | Best choice |
| :--- | :--- |
| Zero or near-zero data loss + readable secondary | AG (synchronous) |
| Single instance resilience against server crash | FCI |
| Cheapest robust DR with delayed recovery | Log Shipping |
| Multi-site disaster recovery | AG (async) + Log Shipping as belt-and-suspenders |
### 5. Combined patterns
Many production setups combine AG and Log Shipping: AG gives RPO/RTO for the primary site, and log shipping to a remote data center provides additional geographic protection.
### 6. Interview takeaway
Each option trades off complexity, RPO, RTO, and cost. The right design depends on the business RPO/RTO requirement, not on what is newest.`
      },
      {
        id: 'sql-21',
        category: 'Backup & Recovery',
        difficulty: 'Medium',
        question: 'Explain SQL Server recovery models and how the transaction log backup chain works.',
        answer: `The recovery model controls how the transaction log is managed and which restore options are possible.
### 1. SIMPLE recovery
* Log space is automatically reclaimed after each checkpoint.
* No transaction log backups are possible.
* Data loss exposure is everything since the last full or differential backup.
* Best for dev/test, read-only databases, and small non-critical data.
### 2. FULL recovery
* Log is preserved until you back it up.
* Allows point-in-time recovery to a specific second.
* Requires a complete **log backup chain**: a full backup followed by unbroken sequence of log backups.
* Best for production OLTP where data loss is unacceptable.
### 3. BULK-LOGGED recovery
* Like FULL but minimal-logs bulk operations (BCP, BULK INSERT, index rebuilds).
* Reduces log size for bulk loads.
* Cannot do point-in-time recovery across a bulk operation — must restore to before or after it.
* Common compromise on data warehouses.
### 4. The log chain
* The first full backup starts the chain.
* Each log backup contains log records written since the previous log backup.
* A break in the chain — e.g., switching to SIMPLE and back — invalidates point-in-time recovery until a new full or differential backup.
* Differential backups act as shortcuts to skip replays of many log backups.
### 5. Interview takeaway
Recovery model choice is an RPO decision. Switching recovery models mid-stream is a common operational mistake that silently breaks the recovery chain.`
      },
      {
        id: 'sql-22',
        category: 'Query Store',
        difficulty: 'Hard',
        question: 'What is Query Store, and how do you use it to stabilize plan regression?',
        answer: `Query Store is a per-database flight recorder for query plans. It captures queries, plans, runtime stats, and waits, and persists them inside the database itself.
### 1. Why it matters
The Query Optimizer is a cost-based system that can legitimately produce a different plan tomorrow for the same query today — due to stats updates, parameter sniffing, CE version changes, or schema changes. Query Store makes this regression **visible and reversible**.
### 2. Core artifacts
* \`sys.query_store_query\`
* \`sys.query_store_plan\`
* \`sys.query_store_runtime_stats\`
* \`sys.query_store_wait_stats\` (SQL 2017+)
### 3. Practical workflow
1. Identify a query whose performance regressed.
2. Compare its plans in Query Store: the regressed plan and the previous good plan.
3. Use \`sp_query_store_force_plan\` to force the prior good plan.
4. Investigate root cause: stale stats, parameter sniffing, missing index, CE regression.
5. Once fixed, unforce with \`sp_query_store_unforce_plan\`.
### 4. Operational tuning
* Set the runtime stats interval appropriately — 1 hour is too coarse for OLTP; 15 minutes is common.
* Use stale query cleanup settings to bound Query Store size.
* Use the included reports in SSMS — Top Resource Consuming Queries, Queries with High Variance, Regression — to find issues fast.
### 5. Wait stats integration
SQL 2017+ stores per-query wait stats inside Query Store. This is often the fastest path from "this query is slow" to "this query is waiting on CXPACKET" without running a custom Extended Events trace.
### 6. Interview takeaway
Query Store shifts plan regression from "mysterious slowdown after upgrade" to a tractable engineering problem with evidence.`
      },
      {
        id: 'sql-23',
        category: 'Extended Events',
        difficulty: 'Hard',
        question: 'When would you use Extended Events instead of SQL Server Profiler/trace, and what are useful sessions to set up?',
        answer: `Extended Events (XEvents) replaced the legacy Profiler/SQL Trace infrastructure. Profiler is deprecated and Microsoft has recommended against it for production diagnosis for years.
### 1. Why XEvents over Profiler
* Lower overhead — designed to be always-on capable.
* More granular events, including wait stats per query.
* Hierarchical eventing with predicates that filter before payload is captured.
* Output to files, memory buffers, or the ring_buffer target.
* Native to the engine, not a separate trace API.
### 2. Useful sessions for production
* **Query timeout detail (\`sql_completed\` with \`sqlserver.sql_batch_completed\`)** — find queries that exceed a threshold.
* **Lock timeout and deadlock** (\`sqlserver.lock_timeout\`, \`sqlserver.xml_deadlock_report\`) — automatic deadlock graph capture.
* **Long-running queries** (\`sqlserver.sql_statement_completed\` with a duration predicate).
* **Error reported** (\`sqlserver.error_reported\`) — capture user-visible errors with severity.
* **Plan regression with Query Store** — XEvents complements rather than replaces Query Store.
### 3. The system_health session
SQL Server ships a built-in session called \`system_health\` that captures deadlocks, severe errors, wait stats above thresholds, and other key events. It is always running by default and frequently the first place to look after an incident.
### 4. Common pitfalls
* Reading the ring_buffer target via XQuery is slow and the target has a fixed size that silently drops events.
* Prefer the file target for production-critical capture.
* Use the session-level option \`MAX_MEMORY\` and event retention carefully.
### 5. Interview takeaway
XEvents is the modern instrumentation layer. A senior engineer should be able to write a session definition from scratch, choose between the file and ring_buffer targets, and know to start from \`system_health\` before creating custom sessions.`
      },
      {
        id: 'sql-24',
        category: 'Security',
        difficulty: 'Medium',
        question: 'How do you secure sensitive data in SQL Server, comparing TDE, Cell-Level Encryption, Always Encrypted, and Dynamic Data Masking?',
        answer: `These four features address different threat models; picking the right one depends on what you are protecting against.
### 1. Transparent Data Encryption (TDE)
* Encrypts **data files and log files at rest**.
* The database file is useless without the master key.
* Threat model: someone steals the disk or backup file.
* Does NOT protect against authenticated users querying the database.
### 2. Cell-Level Encryption
* Encrypt specific columns using certificates and symmetric keys.
* Application decrypts via \`DecryptByCert\` or \`DecryptByKey\`.
* Threat model: protect specific sensitive columns from users who otherwise have access to the table.
* Cost: schema changes, index restrictions on encrypted columns, application-side key management.
### 3. Always Encrypted
* Encryption and decryption happen **client-side inside the ADO.NET driver**.
* The database server never sees plaintext.
* Two flavors: deterministic (allows equality) and randomized (no queries on encrypted data).
* Threat model: protect data from DBAs and database administrators, not just disk theft.
### 4. Dynamic Data Masking
* Masks data returned to certain users, e.g., showing \`XXX-XX-XXXX\` instead of a Social Security number.
* The underlying data is unchanged and stored in plaintext.
* Threat model: limit exposure in application read paths, not protect against attackers with direct DB access.
### 5. Choosing
| Threat to defend | Best feature |
| :--- | :--- |
| Disk/backup theft | TDE |
| Column exposure to authenticated users | Cell-level encryption |
| DBA cannot see plaintext | Always Encrypted |
| Limit what app users see in queries | Dynamic Data Masking |
### 6. Interview takeaway
A common mistake is treating these as interchangeable. Each defends a different trust boundary; misusing them can leave real exposure while creating the illusion of security.`
      },
      {
        id: 'sql-25',
        category: 'Change Data Capture',
        difficulty: 'Medium',
        question: 'What are Change Data Capture (CDC), Change Tracking (CT), and Temporal Tables? When do you use each?',
        answer: `All three capture or surface change history, but the model and use cases differ.
### 1. Change Data Capture (CDC)
* Captures INSERT, UPDATE, DELETE operations by reading the transaction log asynchronously.
* Stores before-and-after state in change tables.
* Best for **ETL**: incrementally loading a data warehouse from an OLTP source.
* Has overhead: log reader agent, change tables, cleanup jobs.
### 2. Change Tracking (CT)
* Lightweight; records only that a row changed (and the primary key), not the column values.
* Synchronous and minimal overhead.
* Best for **cache invalidation** and **sync applications** that need to know which rows changed since their last sync.
### 3. Temporal Tables (System-Versioned)
* Maintains a history table automatically populated on every UPDATE/DELETE.
* Queryable with \`FOR SYSTEM_TIME AS OF\`, \`BETWEEN\`, \`FROM ... TO\`.
* Best for **audit** and **point-in-time history** of a single table.
### 4. Comparison
| Feature | CDC | CT | Temporal |
| :--- | :--- | :--- | :--- |
| Captures values | Yes (full row) | No (only keys) | Yes (full row history) |
| Use case | ETL | Sync/cache | Audit/history |
| Overhead | High | Low | Medium |
| Reads the log | Yes (async) | No | No |
### 5. Choosing
* Need historical state of a table for audit? **Temporal.**
* Need to feed a downstream warehouse with row changes? **CDC.**
* Need to tell a remote sync engine which rows changed? **CT.**
### 6. Interview takeaway
The mistake to avoid: choosing based on overhead alone. Pick the feature whose data model matches the consumer's needs.`
      },
      {
        id: 'sql-26',
        category: 'Resource Governor',
        difficulty: 'Hard',
        question: 'What does Resource Governor do, and what are realistic ways to use it?',
        answer: `Resource Governor limits and isolates resource usage by workload group. It is an Enterprise Edition feature (in most versions).
### 1. What it can limit
* CPU: min and max CPU percentage per workload group.
* Memory: max memory per query, per session, and per pool.
* IOPS in newer versions: limits per workload group.
* Session timeout and pool-level settings.
### 2. Classifier function
A user-defined function routes incoming sessions to workload groups based on login, application name, host, etc.
\`\`\`sql
CREATE FUNCTION dbo.ClassifyReports()
RETURNS SYSNAME
WITH SCHEMABINDING
AS
BEGIN
    IF APP_NAME() LIKE '%ReportServer%' RETURN N'ReportGroup';
    RETURN N'DefaultGroup';
END;
\`\`\`
### 3. Realistic use cases
* Throttle ad-hoc analyst queries so they cannot starve the OLTP workload.
* Limit a batch import job's CPU during business hours.
* Reserve minimum CPU for the application's critical connections.
* Cap memory grants to prevent large queries from exhausting buffer pool.
### 4. Common pitfalls
* Using RG to fix a fundamentally bad query — RG limits the symptom, not the root cause.
* Over-segmenting into many pools; CPU fragmentation hurts more than it helps.
* Forgetting that RG limits at instance level, not database level. It cannot separate two workloads in the same database by tenant.
### 5. Interview takeaway
Resource Governor is a tool for **predictable degradation under load**, not a performance optimizer. Senior engineers use it sparingly, with clear numeric thresholds tied to a measurable business need.`
      },
      {
        id: 'sql-27',
        category: 'Set-Based Thinking',
        difficulty: 'Medium',
        question: 'Why does set-based thinking matter in SQL Server, and what are the signs a query is not truly set-based?',
        answer: `Set-based thinking treats data as sets to be transformed in one pass, not rows to be iterated.
### 1. Why it matters
The SQL Server engine is optimized to operate on sets in parallelizable, vectorized operators (Hash Match, Sort, Batch Mode). Iterative patterns defeat this.
### 2. Smells of non-set-based code
* Cursors or WHILE loops where a single UPDATE/INSERT would work
* Correlated subqueries executed per-row
* Scalar UDFs in WHERE clauses (pre-2019 they forced serial, row-by-row execution)
* Multi-statement table-valued functions
* Repeated single-row INSERT statements in a loop
* Cross applies that scale linearly with row count
### 3. Concrete example
Instead of looping to update each order's status based on a lookup table:
\`\`\`sql
-- RBAR
WHILE EXISTS (SELECT 1 FROM #Ids WHERE Processed = 0)
BEGIN
    -- per-row update logic
END;
-- Set-based
UPDATE o
SET o.Status = s.NewStatus
FROM Orders o
JOIN StatusUpdates s ON s.OrderId = o.OrderId;
\`\`\`
### 4. When iteration is acceptable
* Complex business logic that depends on prior-row computation
* Backfill migrations where batching is needed to control log growth
* Situations where the optimizer genuinely cannot produce a set-based plan
### 5. Interview takeaway
Set-based thinking is less about memorizing syntax and more about asking, before writing a loop: "Can the database do this in a single statement?" Often the answer is yes, with a window function, a CTE, or a recursive CTE.`
      },
      {
        id: 'sql-28',
        category: 'Execution Plan Internals',
        difficulty: 'Hard',
        question: 'Explain Hash Match, Merge Join, and Nested Loops joins. When does the optimizer pick each?',
        answer: `Joins are physical operators. The optimizer chooses based on input size, ordering, and memory.
### 1. Nested Loops
* Outer loop iterates rows from the smaller input; for each, it seeks the inner input via an index.
* Best when one input is small and the inner side has a seekable index.
* Cost is roughly \`O(N * log M)\` where M is the inner table size.
### 2. Merge Join
* Both inputs must be sorted on the join key.
* One pass through each input; cost is \`O(N + M)\`.
* Excellent for large, already-sorted inputs (e.g., both sides have an index on the join key).
* Falls back to a Sort if inputs are not ordered, which can be expensive.
### 3. Hash Match
* Build phase creates an in-memory hash table from the smaller input.
* Probe phase reads the larger input and looks up against the hash table.
* Best for large, unsorted inputs.
* Memory grant sensitive; spills to tempdb (Grace Hash) when memory is insufficient.
* Cannot be used with some outer joins with inequality predicates.
### 4. Selection rules
| Input characteristic | Likely join |
| :--- | :--- |
| Small outer + indexed inner | Nested Loops |
| Both large, both sorted on key | Merge |
| Both large, unsorted | Hash Match |
### 5. Common regression signals
* Hash Match where the optimizer underestimated rows and ran out of memory — spill warnings in the plan.
* Nested Loops where the outer input is unexpectedly large — caused by cardinality misestimate.
* Sort before Merge Join on a huge input — investigate if a supporting index could avoid the sort.
### 6. Interview takeaway
The optimizer does not pick joins arbitrarily. Each choice reflects what it knows about cardinality, indexes, and memory. Bad join choices usually trace back to bad estimates or missing indexes.`
      },
      {
        id: 'sql-29',
        category: 'Stored Procedures',
        difficulty: 'Medium',
        question: 'What are best practices for writing production-grade stored procedures in SQL Server?',
        answer: `Production stored procedures need predictable plans, clear error handling, and maintainability.
### 1. Schema and parameter hygiene
* Use schema-qualified object names (\`dbo.ProcedureName\`) to avoid name resolution overhead and ambiguity.
* Match parameter data types exactly to column types to avoid implicit conversions.
* Avoid \`SELECT *\` even inside procedures — schema changes can break plans silently.
### 2. Error handling
* Wrap DML in \`BEGIN TRY ... BEGIN CATCH\` blocks.
* In the CATCH, capture \`ERROR_NUMBER()\`, \`ERROR_SEVERITY()\`, \`ERROR_MESSAGE()\`, \`ERROR_PROCEDURE()\`, \`ERROR_LINE()\`.
* Re-throw with \`THROW\` (preferred since SQL 2012) for any error you cannot handle locally.
* Always check \`XACT_STATE()\` before committing or rolling back — some errors leave transactions uncommittable.
\`\`\`sql
BEGIN TRY
    BEGIN TRAN;
    -- DML
    COMMIT;
END TRY
BEGIN CATCH
    IF XACT_STATE() = -1 ROLLBACK;
    THROW;
END CATCH;
\`\`\`
### 3. Plan stability
* Avoid parameter sniffing regressions with local variable copies or \`OPTION (RECOMPILE)\` for highly variable workloads.
* Keep SET options consistent across the connection. \`SET NOCOUNT ON\` avoids noise traffic and is good practice.
* Avoid long-running procedures; long transactions hold locks and grow the version store.
### 4. Maintainability
* Comment intent, not mechanics.
* Keep procedures focused on one operation. A 2000-line procedure is a maintenance liability.
* Prefer CTEs and table variables over deeply nested subqueries for readability.
* Use \`SET XACT_ABORT ON\` to ensure runtime errors roll back the transaction automatically.
### 5. Interview takeaway
The difference between a junior SP and a senior SP is not cleverness — it is predictability. The procedure should fail loudly, plan consistently, and be easy to read at 2 a.m.`
      },
      {
        id: 'sql-30',
        category: 'Architecture & Scaling',
        difficulty: 'Hard',
        question: 'Walk through how you would scale a SQL Server backend that has hit a vertical scaling limit.',
        answer: `Vertical scaling always ends. The senior engineer's job is to scale horizontally and operationally before performance collapses.
### 1. Diagnose the bottleneck
* Wait stats: is it CPU, log I/O, data I/O, memory grants, locking, or TempDB?
* Top queries by average and total resource consumption.
* Lock contention points and deadlocks.
* Cache hit ratios (buffer pool, procedure cache, plan cache).
### 2. Tune before scaling
* Index strategy: covering indexes for top queries, removal of unused indexes.
* Statistics maintenance and plan forcing via Query Store.
* Rewrite RBAR code.
* Address implicit conversions and non-sargable predicates.
### 3. Architecture changes
* **Read scaling**: readable secondaries via Always On Availability Groups for reporting workloads.
* **Functional partitioning**: separate write-heavy from read-heavy workloads into different databases or instances.
* **Sharding**: split tenants or large tables across instances. Application-aware routing layer is required.
* **Caching**: read-through caching layer (Redis, in-process) for hot, low-volatility data.
* **Offload analytics**: columnstore + ETL or a separate analytics replica instead of running reporting on the OLTP instance.
### 4. Data lifecycle
* Archive cold data to historical tables.
* Partition by date and switch out old partitions.
* Compress backups with BACKUP COMPRESSION; consider backup to URL for off-site storage.
### 5. Operational hardening
* Standardize on a runbook for incidents.
* Monitor TempDB, version store size, log reuse waits, and long-running transactions.
* Capacity-plan on actual growth trends, not peak wishful thinking.
### 6. The hardest conversation
Scaling is rarely just "add servers." It often requires saying: this query design is the bottleneck; this schema has grown beyond its design; we need to refactor. The senior engineer's job is to surface that conversation early, with evidence, before an outage forces it.
### 7. Interview takeaway
A senior answer distinguishes **symptomatic fixes** (more RAM, faster disks) from **structural fixes** (partitioning, sharding, read replicas, schema redesign). Symptomatic fixes buy time; structural fixes change the scaling curve.`
      }
    ]
  },
  {
    id: 'azure-guide',
    title: 'Azure Cloud Solutions',
    subtitle: 'App Services, Serverless, Key Vault, and Azure DevOps integration',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Award',
    description: 'Learn Managed Identities, Service Bus, Azure Functions, Key Vault, AKS, Cosmos DB, and enterprise CI/CD.',
    questions: [
      {
        id: 'az-1',
        category: 'Identity & Security',
        difficulty: 'Medium',
        question: 'What is Azure Managed Identity (System-Assigned vs. User-Assigned)? Why does it represent a major security best practice for .NET cloud deployments?',
        answer: `**Azure Managed Identities** provide an automatically managed identity in Microsoft Entra (Azure AD) for cloud resources to authenticate to services supporting Microsoft Entra auth.

### 1. Types of Managed Identities
*   **System-Assigned Identity:**
    *   Tied directly to a **single** Azure resource (e.g., an App Service or VM).
    *   Deleted automatically when the resource is deleted.
*   **User-Assigned Identity:**
    *   Created as a standalone Azure resource.
    *   Can be assigned to **multiple** Azure resources.
    *   Lifecycle is managed independently from the resources.

### 2. Why it is a Security Best Practice
Managed identity completely eliminates **hardcoded credentials and secrets** from AppSettings, git repos, or CI/CD pipelines.

\`\`\`csharp
// Program.cs - Zero-secret connection to SQL Database or Key Vault
builder.Services.AddDbContext<AppDbContext>(options =>
    options.UseSqlServer(
        builder.Configuration.GetConnectionString("SqlDb"),
        sqlOptions => sqlOptions.Authentication(SqlAuthenticationMethod.ActiveDirectoryManagedIdentity)
    )
);
\`\`\`
The Azure infrastructure handles updating, rotating, and managing the active cryptographic tokens automatically under the hood.`
      },
      {
        id: 'az-2',
        category: 'Messaging & Serverless',
        difficulty: 'Hard',
        question: 'Compare Azure Service Bus Queues vs. Topics. When should you choose Service Bus over Storage Queues or Event Grid?',
        answer: `Choosing the right messaging framework determines the stability and scalability of distributed cloud systems.

### 1. Service Bus Queues vs. Topics
*   **Queues (Point-to-Point):** One sender sends a message, and only **one** receiver processes it. Used for load leveling and message processing.
*   **Topics (Publish-Subscribe):** One sender publishes a message. Multiple subscriptions can filter and receive a copy of that message based on custom headers/metadata.

### 2. Choose: Service Bus vs. Event Grid vs. Storage Queues
*   **Storage Queues:** Choose if you need simple, inexpensive queues with message sizes < 64KB and total storage capacity > 80TB.
*   **Azure Service Bus:** Choose if you need enterprise-grade messaging features:
    *   Transactional safety.
    *   Strict FIFO (First-In-First-Out) Ordering using Sessions.
    *   Duplicate Detection.
    *   Dead-lettering of failed messages.
*   **Event Grid:** Choose for lightweight reactive notifications (push-push model, e.g. "blob file created", "user registered"). High throughput, poor state tracking.`
      },
      {
        id: 'az-3',
        category: 'Compute',
        difficulty: 'Medium',
        question: 'Compare Azure App Service, Azure Functions, Azure Kubernetes Service (AKS), and Virtual Machines. When should each be used?',
        answer: `Each Azure compute option fits a different operational model.

### 1. Azure App Service
Best for web apps and APIs where you want managed hosting with minimal infrastructure work.

### 2. Azure Functions
Best for event-driven or scheduled workloads with short-lived execution and elastic scaling.

### 3. AKS
Best for containerized microservices when you need orchestration, advanced deployment patterns, and platform control.

### 4. Virtual Machines
Best when you need full OS-level control or must run legacy/custom workloads.

### 5. Quick rule
* **App Service:** standard APIs and websites
* **Functions:** event-based serverless jobs
* **AKS:** complex container platforms
* **VMs:** maximum control, highest ops overhead`
      },
      {
        id: 'az-4',
        category: 'Storage',
        difficulty: 'Medium',
        question: 'What are the main Azure storage options, and how do Blob Storage, Files, Queues, and Tables differ?',
        answer: `Azure Storage offers multiple services for different data shapes.

### 1. Blob Storage
Object storage for files such as images, videos, backups, and documents.

### 2. Azure Files
Managed file shares accessible via SMB.
Useful for shared legacy app storage.

### 3. Queue Storage
Simple message queueing for basic asynchronous workflows.

### 4. Table Storage
Key-attribute NoSQL store for simple structured datasets.

### 5. Practical view
* **Blob:** unstructured file/object data
* **Files:** shared file systems
* **Queues:** lightweight messaging
* **Tables:** simple NoSQL key-value style data`
      },
      {
        id: 'az-5',
        category: 'Security',
        difficulty: 'Hard',
        question: 'How does Azure Key Vault work, and what are best practices for integrating it into .NET applications?',
        answer: `**Azure Key Vault** securely stores secrets, keys, and certificates.

### 1. What it stores
* secrets like connection strings and API keys
* certificates
* encryption keys

### 2. Best practice integration
Use **Managed Identity** so the application authenticates to Key Vault without embedded credentials.

### 3. .NET integration
You can load Key Vault into configuration or access secrets directly with Azure SDK clients.

### 4. Best practices
* use RBAC or access policies carefully
* restrict network access where possible
* rotate secrets regularly
* do not log secret values
* cache secret retrieval responsibly to avoid repeated remote calls

Key Vault is valuable because it separates secret management from deployment artifacts and source code.`
      },
      {
        id: 'az-6',
        category: 'Networking',
        difficulty: 'Hard',
        question: 'What is the difference between Azure VNet, Subnet, NSG, Private Endpoint, and Load Balancer?',
        answer: `These are core building blocks of Azure networking.

### 1. VNet
A **Virtual Network** is the logical private network boundary in Azure.

### 2. Subnet
A **Subnet** partitions a VNet into smaller network segments.

### 3. NSG
A **Network Security Group** defines inbound and outbound traffic rules.

### 4. Private Endpoint
A **Private Endpoint** gives a PaaS service a private IP inside your VNet, avoiding public internet exposure.

### 5. Load Balancer
Distributes traffic across backend instances for availability and scale.

### 6. Interview takeaway
A common secure architecture is:
* app in private subnet
* database behind private endpoint
* NSGs controlling east-west and north-south traffic
* load balancer or application gateway at the edge`
      },
      {
        id: 'az-7',
        category: 'Monitoring',
        difficulty: 'Medium',
        question: 'What are Azure Monitor, Log Analytics, and Application Insights? How do they work together?',
        answer: `These services work together to provide observability.

### 1. Azure Monitor
The umbrella monitoring platform for metrics, logs, alerts, and dashboards.

### 2. Log Analytics
Stores and queries telemetry logs using KQL.

### 3. Application Insights
Focused on application telemetry such as request rates, response times, dependencies, traces, and exceptions.

### 4. How they fit together
An ASP.NET Core app can send telemetry to Application Insights, which is then queryable and alertable through Azure Monitor and Log Analytics.

### 5. Common use cases
* alert on API failure spikes
* investigate slow dependencies
* analyze user flows
* monitor availability and exceptions`
      },
      {
        id: 'az-8',
        category: 'DevOps & Deployment',
        difficulty: 'Medium',
        question: 'What is a good CI/CD strategy for deploying .NET applications to Azure?',
        answer: `A strong CI/CD pipeline should automate build, test, security checks, deployment, and rollback strategy.

### 1. CI stages
* restore packages
* build
* run unit tests
* run code quality and security scans
* publish artifacts

### 2. CD stages
* deploy to dev/test
* run smoke tests
* approve and deploy to staging/production
* monitor post-deployment health

### 3. Recommended practices
* infrastructure as code
* environment-specific config separation
* blue-green or canary deployments where appropriate
* automatic rollback triggers based on health signals

### 4. Interview takeaway
The goal is not just deployment automation. It is safe, repeatable, observable delivery.`
      },
      {
        id: 'az-9',
        category: 'Data',
        difficulty: 'Hard',
        question: 'When would you choose Azure SQL Database, Cosmos DB, or Table Storage?',
        answer: `The right database depends on consistency, query model, scale, and latency requirements.

### 1. Azure SQL Database
Choose when you need relational modeling, joins, transactions, and strong SQL support.

### 2. Cosmos DB
Choose when you need global distribution, low latency, elastic throughput, and flexible NoSQL models.

### 3. Table Storage
Choose when the data model is simple and cost efficiency matters more than advanced querying.

### 4. Rule of thumb
* **Azure SQL:** transactional business data
* **Cosmos DB:** globally distributed high-scale NoSQL
* **Table Storage:** simple, cheap key-based storage`
      },
      {
        id: 'az-10',
        category: 'Security & Identity',
        difficulty: 'Hard',
        question: 'Explain Managed Identities (System-Assigned vs User-Assigned) in Azure. How do they eliminate hardcoded secrets in connection strings and Key Vault access?',
        answer: `**Azure Managed Identities** provide an automatically managed identity in Microsoft Entra ID (formerly Azure AD) for applications to use when connecting to resources that support Entra authentication.

### 1. System-Assigned vs User-Assigned Managed Identity
* **System-Assigned Managed Identity:** Bound directly to a single Azure resource (App Service, VM, Azure Function). Its lifecycle is tied to the resource—when the resource is deleted, the identity is automatically removed.
* **User-Assigned Managed Identity:** Created as an independent Azure resource. Can be assigned to multiple Azure resources (1-to-many), ideal for workloads sharing identical Key Vault or SQL permissions.

### 2. How Managed Identities Eliminate Secrets
Without Managed Identities, credentials must be stored in \`appsettings.json\` or environment variables. With Managed Identities:
1. The Azure runtime requests an OAuth token directly from the internal Azure instance metadata endpoint (\`http://169.254.169.254\`).
2. Entra ID returns an access token without exposing passwords or keys in code.
3. In .NET, \`DefaultAzureCredential\` automatically retrieves this token transparently:

\`\`\`csharp
var credential = new DefaultAzureCredential();
var client = new SecretClient(new Uri("https://myvault.vault.azure.net/"), credential);
KeyVaultSecret secret = await client.GetSecretAsync("ThirdPartyApiKey");
\`\`\``
      },
      {
        id: 'az-11',
        category: 'Serverless & Microservices',
        difficulty: 'Hard',
        question: 'Compare Azure Container Apps (ACA) vs Azure Kubernetes Service (AKS) vs Azure Functions. How do you select the right hosting platform?',
        answer: `Selecting the right Azure compute platform depends on control requirements, operational overhead, event-driven scaling needs, and container ecosystem complexity.

### 1. Azure Functions (FaaS)
* **Best for:** Event-driven micro-tasks (HTTP webhooks, queue triggers, timer jobs, blob processing).
* **Scaling:** Scales to zero instantly based on incoming event count.
* **Trade-off:** Potential cold starts on consumption plans; limited underlying host control.

### 2. Azure Container Apps (ACA)
* **Best for:** Containerized microservices requiring serverless scaling (KEDA) without managing Kubernetes control planes.
* **Features:** Built on Kubernetes + Dapr + Envoy, fully managed by Azure. Supports scale-to-zero and HTTP split routing.

### 3. Azure Kubernetes Service (AKS)
* **Best for:** Complex enterprise microservice meshes, custom ingress controllers, and full Kubernetes ecosystem control.
* **Trade-off:** High operational overhead; requires dedicated DevOps skills for cluster upgrades and network security policies.`
      },

      {
        id: 'az-12',
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'How do you design a multi-tenant .NET application on Azure? What are the tradeoffs between shared and isolated tenant models?',
        answer: `A multi-tenant Azure design starts by deciding how much infrastructure, data, and identity isolation each tenant requires.
### 1. Shared vs Isolated tenancy
* **Shared model:** multiple tenants share the same app instance and database/schema with tenant discrimination in code and queries.
* **Isolated model:** each tenant gets dedicated infrastructure, database, or even subscription/resource group boundaries.
* **Hybrid model:** smaller tenants share infrastructure, while premium or regulated tenants get isolated resources.
### 2. Key design concerns
* tenant resolution strategy
* data isolation
* noisy neighbor protection
* per-tenant configuration
* per-tenant monitoring and throttling
* billing and usage attribution
### 3. Azure services commonly used
* App Service / AKS / Container Apps for hosting
* Azure SQL elastic pools or separate databases
* Cosmos DB for partitioned tenant workloads
* Key Vault for tenant-specific secrets
* Front Door / API Management for routing and policy enforcement
### 4. Tradeoff summary
* **Shared:** lower cost, higher operational efficiency, more complexity in isolation safeguards
* **Isolated:** stronger security and customization, higher cost and operational overhead
For senior candidates, the real discussion is not just tenancy style, but how to evolve from shared to hybrid as the product grows.`
      },
      {
        id: 'az-13',
        category: 'Reliability',
        difficulty: 'Hard',
        question: 'How would you design a highly available and disaster-resilient .NET application in Azure?',
        answer: `High availability and disaster recovery are related but different concerns: HA minimizes local failures, while DR addresses regional or catastrophic failures.
### 1. High availability design
* deploy multiple app instances across availability zones where supported
* avoid single points of failure
* use load balancing via Front Door, Application Gateway, or Azure Load Balancer
* use zone-redundant data services when available
### 2. Disaster recovery design
* replicate data to another region
* define recovery time objective (RTO) and recovery point objective (RPO)
* prepare failover procedures and test them regularly
* externalize configuration and secrets so recovery environments can be activated quickly
### 3. Data layer considerations
* Azure SQL active geo-replication / failover groups
* Cosmos DB multi-region design
* Storage account replication strategy
* backup, restore, and corruption recovery planning
### 4. Application concerns
* stateless service design
* idempotent message processing
* retry with backoff
* circuit breakers
* graceful degradation when dependencies fail
### 5. Interview takeaway
Senior engineers should explain not only the architecture, but also **how failover is validated**, monitored, and rehearsed.`
      },
      {
        id: 'az-14',
        category: 'API Management',
        difficulty: 'Hard',
        question: 'What is Azure API Management, and when should it be introduced in a .NET microservices platform?',
        answer: `**Azure API Management (APIM)** is an API gateway and governance layer for exposing, securing, transforming, versioning, and monitoring APIs.
### 1. What APIM provides
* centralized authentication and authorization
* rate limiting and quotas
* request/response transformation
* API versioning strategy
* subscription keys and productization
* developer portal and API discoverability
* observability and policy enforcement
### 2. When to introduce it
APIM becomes valuable when:
* multiple APIs need a unified entry point
* clients require different contracts over the same backend
* security policies must be standardized
* you need partner/external developer exposure
* backend services should remain hidden from consumers
### 3. When not to overuse it
If you have one small internal API with simple requirements, APIM may add cost and operational complexity without enough benefit.
### 4. Senior-level discussion
A strong answer covers gateway vs ingress responsibilities, internal vs external APIM, policy ownership, and how APIM fits with Front Door, App Gateway, and microservice boundaries.`
      },
      {
        id: 'az-15',
        category: 'Data & Performance',
        difficulty: 'Hard',
        question: 'How do you optimize Azure SQL performance for a large .NET application?',
        answer: `Azure SQL performance tuning requires looking at schema design, query behavior, connection management, and workload shape together.
### 1. Application-level improvements
* use efficient EF Core queries
* avoid N+1 query problems
* use async I/O appropriately
* cache read-heavy data where possible
* tune connection pooling
### 2. Database-level improvements
* proper indexing strategy
* query plan analysis
* partitioning where appropriate
* avoid over-fetching and excessive locking
* choose the right service tier and compute model
### 3. Azure-specific considerations
* elastic pools for multi-database workloads
* read scale replicas where supported
* automatic tuning recommendations
* monitoring DTU/vCore, IO, waits, and long-running queries
### 4. Senior-level takeaway
A strong candidate should distinguish between **database bottlenecks**, **ORM inefficiencies**, and **infrastructure sizing mistakes** rather than treating all slowness as a SQL problem.`
      },
      {
        id: 'az-16',
        category: 'Distributed Systems',
        difficulty: 'Hard',
        question: 'What patterns do you use to handle distributed transactions in Azure microservices when a single ACID transaction is not possible?',
        answer: `In distributed cloud systems, trying to preserve a single database-style transaction across services usually causes coupling and fragility.
### 1. Preferred patterns
* **Saga pattern** for multi-step business workflows
* **Outbox pattern** for reliable event/message publishing
* **Compensating actions** to reverse previously completed steps
* **Idempotency** to safely retry operations
### 2. Why this matters in Azure
Services communicating through Service Bus, Functions, or HTTP APIs often fail partially. You must design for eventual consistency rather than perfect immediate consistency.
### 3. Implementation concerns
* message deduplication
* retry and poison message handling
* correlation IDs
* workflow state tracking
* observability across service boundaries
### 4. Interview takeaway
Senior candidates should explain that the goal is not to simulate a giant distributed SQL transaction, but to design a business process that remains correct under retries, delays, and partial failure.`
      },
      {
        id: 'az-17',
        category: 'Cloud Security',
        difficulty: 'Hard',
        question: 'How would you secure a production-grade .NET application on Azure end to end?',
        answer: `A production-grade Azure security model must cover identity, network boundaries, secret handling, data protection, platform hardening, and monitoring.
### 1. Identity and access
* use Managed Identity wherever possible
* enforce least privilege with Azure RBAC
* use Privileged Identity Management for elevated admin access
* enforce MFA and Conditional Access for operators
### 2. Network security
* prefer private endpoints for PaaS dependencies
* restrict public ingress
* segment networks with VNets, subnets, and NSGs
* use WAF-capable edge services where needed
### 3. Secret and data protection
* store secrets in Key Vault
* encrypt data at rest and in transit
* rotate credentials still required for external systems
* classify and minimize sensitive data storage
### 4. Monitoring and governance
* enable diagnostic logging
* collect security telemetry in Azure Monitor / Log Analytics
* use Defender for Cloud recommendations
* enforce policy with Azure Policy and IaC guardrails
### 5. Senior-level answer
The strongest answers connect security controls to actual threat reduction, not just a checklist of Azure services.`
      },
      {
        id: 'az-18',
        category: 'Containers',
        difficulty: 'Hard',
        question: 'What are the key architectural tradeoffs between Azure Kubernetes Service and Azure Container Apps for enterprise .NET workloads?',
        answer: `Both AKS and Azure Container Apps run containers, but they target different operational models.
### 1. Azure Container Apps
* lower operational overhead
* strong fit for HTTP services, background workers, and event-driven container workloads
* scale-to-zero support
* simpler developer platform for many teams
### 2. AKS
* full Kubernetes control
* broader ecosystem support
* advanced networking, policy, service mesh, and operator scenarios
* better for highly customized platform requirements
### 3. Decision factors
* team Kubernetes expertise
* compliance and network control requirements
* portability expectations
* workload scale patterns
* platform engineering maturity
### 4. Interview takeaway
A senior answer should avoid saying AKS is always better because it is more powerful. In many organizations, **operational simplicity** is the more important architectural win.`
      },
      {
        id: 'az-19',
        category: 'Integration',
        difficulty: 'Hard',
        question: 'How do you choose between synchronous HTTP communication and asynchronous messaging in Azure-based microservices?',
        answer: `The main decision is whether the caller needs an immediate response and whether the business process can tolerate eventual consistency.
### 1. Use synchronous HTTP when
* the caller needs immediate confirmation
* latency must be low and predictable
* the operation is simple and request-response oriented
### 2. Use asynchronous messaging when
* workloads need buffering and decoupling
* failures should not block the user path
* processing can happen later
* systems scale independently
* reliability matters more than immediate completion
### 3. Azure examples
* HTTP via App Service / APIM for customer-facing APIs
* Service Bus for commands and reliable back-end workflows
* Event Grid for lightweight event notifications
* Functions for event-driven handlers
### 4. Senior-level takeaway
Strong candidates explain that most real systems use both: synchronous calls for interactive flows, asynchronous messaging for resilience and scale.`
      },
      {
        id: 'az-20',
        category: 'Observability',
        difficulty: 'Hard',
        question: 'What does good observability look like for a distributed .NET application running on Azure?',
        answer: `Good observability means you can understand system health, diagnose failures, and trace business flows across service boundaries without guessing.
### 1. Core pillars
* logs
* metrics
* distributed traces
### 2. Azure implementation
* Application Insights for request, dependency, trace, and exception telemetry
* Log Analytics for cross-resource querying
* Azure Monitor alerts and dashboards
* correlation IDs and OpenTelemetry instrumentation across services
### 3. What to measure
* request latency
* error rates
* dependency failures
* queue depth
* retry frequency
* business KPIs such as order completion or payment success
### 4. Senior-level takeaway
Mature observability is not just collecting telemetry. It is designing telemetry so incidents can be detected, triaged, and explained quickly.`
      },
      {
        id: 'az-21',
        category: 'DevOps & Governance',
        difficulty: 'Hard',
        question: 'How do Infrastructure as Code, Azure Policy, and CI/CD work together in a mature Azure platform?',
        answer: `A mature Azure delivery model treats infrastructure, policy, and application deployment as one controlled system.
### 1. Infrastructure as Code
Use Bicep, Terraform, or ARM templates to provision reproducible environments.
### 2. Azure Policy
Use policy to enforce organizational rules such as:
* allowed regions
* mandatory tags
* restricted SKU usage
* requirement for private endpoints or diagnostic settings
### 3. CI/CD integration
Pipelines should validate, scan, deploy, and verify both infrastructure and application changes.
### 4. Why this matters
Without policy and automation, cloud environments drift, security posture weakens, and production becomes inconsistent.
### 5. Senior-level takeaway
The best answers connect platform governance with developer velocity: guardrails should reduce risk without turning every deployment into a manual approval bottleneck.`
      },
      {
        id: 'az-22',
        category: 'Identity & Security',
        difficulty: 'Hard',
        question: 'How do you choose between system-assigned and user-assigned managed identities in a real enterprise Azure environment?',
        answer: `The choice depends mainly on identity lifecycle, permission reuse, and operational ownership.
### 1. Choose system-assigned when
* the identity belongs to exactly one resource
* the resource and identity should be created and deleted together
* you want the simplest setup with minimal lifecycle management
### 2. Choose user-assigned when
* multiple resources need the same permissions
* identity lifecycle must be independent from compute lifecycle
* you want stable principal reuse across redeployments
* you want fewer repeated role assignments
### 3. Important tradeoff
System-assigned identities are simpler and reduce shared blast radius. User-assigned identities improve reuse and consistency, but if overused they can broaden access more than necessary.
### 4. Senior-level takeaway
The right answer is often: start with **system-assigned by default**, then use **user-assigned** deliberately where shared identity or lifecycle independence provides clear operational value.`
      },
      {
        id: 'az-23',
        category: 'Platform Design',
        difficulty: 'Hard',
        question: 'How would you design environment strategy for dev, test, staging, and production in Azure for a large .NET organization?',
        answer: `Environment strategy is about balancing isolation, cost, release confidence, compliance, and operational simplicity.
### 1. Common principles
* production must be strongly isolated
* non-production should be reproducible
* configuration must be externalized
* secrets and identities should differ by environment
### 2. Design choices
* separate subscriptions for prod vs non-prod
* separate resource groups per application or bounded context
* staging slots or blue-green environments for safer release
* environment-specific monitoring, alerting, and RBAC boundaries
### 3. Risks to avoid
* sharing production dependencies with test
* weak separation of duties
* manual configuration drift
* overloading one giant shared subscription for everything
### 4. Senior-level takeaway
A good answer explains not just naming conventions, but governance boundaries, deployment safety, and how teams scale operationally across many applications.`
      },
      {
        id: 'az-24',
        category: 'Cost Optimization',
        difficulty: 'Hard',
        question: 'How do you approach Azure cost optimization without compromising reliability and security?',
        answer: `Cost optimization is not simply reducing spend - it is aligning spend with actual workload value and behavior.
### 1. Common tactics
* right-size compute and database tiers
* use autoscaling
* remove idle resources
* choose appropriate storage tiers
* review log retention and telemetry volume
* reserve capacity where usage is predictable
### 2. Areas often overlooked
* overprovisioned App Service plans
* excessive Application Insights ingestion
* forgotten snapshots, disks, and public IPs
* duplicated environments with low business value
### 3. Guardrails
Do not cut costs by:
* removing redundancy required for SLA
* weakening security controls
* shrinking monitoring so much that incidents become harder to resolve
### 4. Senior-level takeaway
The best answers frame cost as an architectural quality attribute, with explicit tradeoffs rather than blanket cost-cutting.`
      },
      {
        id: 'az-25',
        category: 'Caching',
        difficulty: 'Hard',
        question: 'When should you introduce Azure Cache for Redis into a .NET architecture, and what problems can it create if used poorly?',
        answer: `Azure Cache for Redis is useful when your application has repeated reads, expensive recomputation, or hot data that does not need to be fetched from the primary store every time.
### 1. Good use cases
* reference data caching
* session/state caching
* rate limiting
* distributed locking with care
* pub-sub or transient coordination patterns
### 2. Benefits
* reduced database load
* lower latency
* improved throughput
### 3. Risks
* stale data
* cache stampede
* inconsistent invalidation
* treating cache as source of truth
* operational issues during cache failure
### 4. Senior-level takeaway
A strong answer includes eviction strategy, TTL design, invalidation approach, and fallback behavior when Redis is unavailable.`
      },
      {
        id: 'az-26',
        category: 'Messaging',
        difficulty: 'Hard',
        question: 'How do you design idempotent consumers for Azure Service Bus in .NET?',
        answer: `Idempotent consumers ensure that processing the same message more than once does not corrupt business state.
### 1. Why duplicates happen
* at-least-once delivery
* retries after transient failures
* handler success before message completion acknowledgement
* redelivery after lock expiration
### 2. Common techniques
* store processed message IDs
* enforce unique business keys
* make updates naturally idempotent
* use transactional outbox/inbox patterns where needed
### 3. Azure considerations
* dead-letter queue handling
* lock renewal
* poison message investigation
* duplicate detection on the sender side where applicable
### 4. Senior-level takeaway
Reliable messaging is not just about sending messages. It is about making consumers safe under retries and replay.`
      },
      {
        id: 'az-27',
        category: 'Data Architecture',
        difficulty: 'Hard',
        question: 'What are the most important partitioning considerations when using Cosmos DB for a large-scale .NET system?',
        answer: `Partition key choice is one of the most important Cosmos DB design decisions because it affects scalability, cost, and query performance.
### 1. Good partition key characteristics
* high cardinality
* even workload distribution
* aligned with common access patterns
### 2. Risks of a poor partition key
* hot partitions
* expensive cross-partition queries
* throughput imbalance
* scaling bottlenecks
### 3. Design considerations
* read/write patterns
* tenant distribution
* time-based growth
* transactional boundaries within a logical partition
### 4. Senior-level takeaway
A strong answer explains that partitioning is not just a database setting - it is a workload design decision that often becomes painful to change later.`
      },
      {
        id: 'az-28',
        category: 'Deployment Strategy',
        difficulty: 'Hard',
        question: 'Compare blue-green, canary, and rolling deployments for Azure-hosted .NET applications.',
        answer: `These strategies all reduce deployment risk, but they solve different operational needs.
### 1. Blue-green
Two full environments exist. Traffic switches from old to new after validation.
* simple rollback
* higher infrastructure cost
### 2. Canary
A small percentage of users or traffic goes to the new version first.
* excellent for risk reduction
* requires stronger observability and traffic control
### 3. Rolling
Instances are replaced gradually.
* efficient for many workloads
* rollback can be messier if state compatibility is poor
### 4. Azure examples
* App Service deployment slots for blue-green style rollout
* Front Door / APIM / ingress rules for canary control
* AKS rolling updates for container workloads
### 5. Senior-level takeaway
The best deployment strategy depends on traffic control, state compatibility, rollback speed, and operational maturity.`
      },
      {
        id: 'az-29',
        category: 'Identity & Access',
        difficulty: 'Hard',
        question: 'How do Azure RBAC and application authorization differ, and why is confusing them dangerous?',
        answer: `Azure RBAC controls access to Azure resources, while application authorization controls what users or services can do inside your application.
### 1. Azure RBAC
Used for actions like:
* reading Key Vault secrets
* accessing Storage
* managing infrastructure
### 2. Application authorization
Used for actions like:
* who can approve an order
* who can view invoices
* which tenant data a user can access
### 3. Why confusion is dangerous
If teams rely on Azure RBAC to model business permissions, they often create rigid, over-privileged, or incorrect authorization boundaries.
### 4. Senior-level takeaway
Strong candidates clearly separate cloud control-plane authorization from application domain authorization.`
      },
      {
        id: 'az-30',
        category: 'Resilience',
        difficulty: 'Hard',
        question: 'What retry patterns are appropriate for Azure-based .NET services, and when do retries make failures worse?',
        answer: `Retries help with transient faults, but careless retries can amplify outages and overload dependencies.
### 1. Good retry practices
* exponential backoff
* jitter
* bounded retry counts
* retry only transient failures
* respect cancellation and timeout budgets
### 2. When retries are harmful
* non-idempotent operations
* persistent configuration failures
* authentication/authorization failures
* overloaded dependencies already under stress
### 3. Complementary patterns
* circuit breakers
* bulkheads
* fallback paths
* queue-based buffering
### 4. Senior-level takeaway
The goal is not "retry everything." The goal is to recover gracefully without causing retry storms or duplicate side effects.`
      },
      {
        id: 'az-31',
        category: 'Migration',
        difficulty: 'Hard',
        question: 'How would you modernize a legacy on-prem .NET application into Azure incrementally rather than through a full rewrite?',
        answer: `Incremental modernization reduces delivery risk by moving constraints one layer at a time.
### 1. Typical sequence
* assess dependencies and architecture bottlenecks
* externalize configuration and secrets
* containerize or rehost selected components
* move databases and supporting services carefully
* extract high-change or high-value capabilities into APIs or background services
### 2. Azure options
* App Service for web front ends
* Azure SQL for relational workloads
* Service Bus for decoupling
* Key Vault for secret management
* Container Apps or AKS for modernized services
### 3. Key risks
* hidden coupling
* identity integration challenges
* data migration downtime
* partial-cloud complexity during transition
### 4. Senior-level takeaway
The strongest answers discuss strangler-fig migration, coexistence, and how to reduce business risk while improving architecture.`
      },
      {
        id: 'az-32',
        category: 'Performance Engineering',
        difficulty: 'Hard',
        question: 'How do you diagnose latency issues in a distributed .NET application on Azure?',
        answer: `Latency diagnosis requires separating client latency, application latency, dependency latency, and infrastructure latency.
### 1. Investigation path
* check end-to-end traces
* identify slow dependencies
* inspect queue delays and thread pool behavior
* review database query timing
* compare p50, p95, and p99 latency
### 2. Azure tools
* Application Insights distributed tracing
* Log Analytics queries
* Azure Monitor metrics
* database performance insights
* network path and gateway telemetry
### 3. Common root causes
* chatty service-to-service calls
* database inefficiency
* cold starts
* thread starvation
* retry cascades
* serialization overhead
### 4. Senior-level takeaway
A strong answer shows structured troubleshooting rather than guessing based on one symptom.`
      },
      {
        id: 'az-33',
        category: 'Governance',
        difficulty: 'Hard',
        question: 'What should a well-designed Azure landing zone provide for enterprise .NET teams?',
        answer: `An Azure landing zone provides the governed foundation on which application teams can safely build and deploy.
### 1. Core capabilities
* subscription structure
* network topology
* identity and RBAC model
* policy enforcement
* logging and monitoring baselines
* connectivity to on-prem or other clouds
* standardized tagging and cost controls
### 2. Why it matters
Without a landing zone, every team invents infrastructure patterns differently, increasing risk, inconsistency, and operational friction.
### 3. Senior-level takeaway
A mature answer explains how landing zones enable both governance and self-service, rather than acting only as a control mechanism.`
      },
      {
        id: 'az-34',
        category: 'Security',
        difficulty: 'Hard',
        question: 'How do you protect Azure-hosted .NET APIs from common internet-facing threats?',
        answer: `Protecting public APIs requires layered controls across identity, edge security, application behavior, and monitoring.
### 1. Edge protections
* WAF
* DDoS-aware architecture
* TLS enforcement
* rate limiting and quotas
### 2. Identity protections
* OAuth2 / OpenID Connect
* token validation
* least-privilege scopes
* managed identity for outbound dependency access
### 3. Application protections
* input validation
* output encoding
* secure headers
* abuse detection
* safe error handling
### 4. Monitoring
* suspicious traffic analysis
* failed auth patterns
* anomaly alerts
* dependency abuse visibility
### 5. Senior-level takeaway
The strongest answers reflect defense in depth rather than reliance on any single gateway or framework feature.`
      },
      {
        id: 'az-35',
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'How do you decide whether a .NET workload should remain a modular monolith or be split into microservices on Azure?',
        answer: `Microservices are not automatically better. The decision should be driven by team structure, change patterns, scaling needs, and domain boundaries.
### 1. Stay modular monolith when
* domain complexity is manageable
* team size is limited
* deployment independence is not yet critical
* operational simplicity is valuable
### 2. Move toward microservices when
* domains are clearly bounded
* teams need independent release cycles
* scaling characteristics differ significantly
* one codebase is creating delivery bottlenecks
### 3. Costs of microservices
* distributed tracing complexity
* network failure modes
* deployment and security overhead
* eventual consistency challenges
### 4. Senior-level takeaway
A strong candidate explains how to evolve architecture based on product and organization maturity, not trends.`
      },
      {
        id: 'az-36',
        category: 'Identity',
        difficulty: 'Hard',
        question: 'How do Managed Identity, service principals, and app registrations relate in Azure and Microsoft Entra?',
        answer: `These concepts are related but not interchangeable.
### 1. App registration
Represents an application definition in Microsoft Entra.
### 2. Service principal
Represents the application identity in a tenant and is what receives permissions.
### 3. Managed identity
Is a Microsoft-managed workload identity for Azure resources that avoids manual secret or certificate management.
### 4. Practical distinction
Managed identities are ideal for Azure-hosted workloads accessing Azure resources. App registrations and service principals are still used when you need broader application identity scenarios, external integrations, or non-Azure-hosted authentication flows.
### 5. Senior-level takeaway
A good answer makes clear that managed identities reduce credential handling but do not replace every identity scenario.`
      },
      {
        id: 'az-37',
        category: 'Operations',
        difficulty: 'Hard',
        question: 'What should an incident response and production support model look like for critical Azure-based .NET systems?',
        answer: `A solid production support model combines technical telemetry, ownership clarity, response processes, and learning loops.
### 1. Core elements
* actionable alerts
* on-call ownership
* severity classification
* runbooks
* dashboards for system and business health
* incident communication process
### 2. During incidents
* detect
* triage
* mitigate
* communicate
* identify root cause
* prevent recurrence
### 3. Azure-specific needs
* dependency health visibility
* deployment correlation
* regional awareness
* access to logs, traces, and platform events
### 4. Senior-level takeaway
The strongest answers include post-incident review and systemic improvement, not just firefighting.`
      },
      {
        id: 'az-38',
        category: 'Architecture & Integration',
        difficulty: 'Hard',
        question: 'When would you use Azure Functions together with Service Bus instead of building the same workflow as synchronous APIs?',
        answer: `Functions plus Service Bus work well when the workflow is event-driven, bursty, or tolerant of asynchronous completion.
### 1. Strong use cases
* background order processing
* document transformation
* integration with external systems
* retryable back-office workflows
### 2. Benefits
* decoupling
* elastic scale
* failure isolation
* natural buffering
### 3. When synchronous APIs are better
* immediate user response is required
* the operation must complete before the caller can continue
* workflow latency is part of the business contract
### 4. Senior-level takeaway
A strong answer explains where asynchronous architecture improves resilience and where it creates unnecessary complexity.`
      },
      {
        id: 'az-39',
        category: 'Compliance & Data Protection',
        difficulty: 'Hard',
        question: 'How do you design Azure solutions for sensitive or regulated data in .NET applications?',
        answer: `Sensitive-data architecture starts with understanding what data is collected, why it is needed, and what regulatory obligations apply.
### 1. Core controls
* data minimization
* encryption at rest and in transit
* strict RBAC
* private networking where required
* audit logging
* retention and deletion policies
### 2. Application design concerns
* mask sensitive data in logs
* separate secrets from configuration
* apply tenant/data boundary rules carefully
* support key rotation and access review
### 3. Azure capabilities
* Key Vault
* Private Endpoints
* Defender for Cloud
* Policy
* SQL auditing
* Storage and database encryption features
### 4. Senior-level takeaway
The strongest answers connect regulatory needs to concrete design decisions instead of treating compliance as paperwork alone.`
      },
      {
        id: 'az-40',
        category: 'Leadership & Architecture',
        difficulty: 'Hard',
        question: 'What distinguishes a senior/principal-level Azure .NET architect answer from a mid-level engineer answer in interviews?',
        answer: `The difference is usually not memorizing more services. It is showing judgment.
### 1. Mid-level answers often focus on
* what a service does
* basic setup
* feature lists
### 2. Senior/principal answers focus on
* tradeoffs
* failure modes
* security implications
* operational ownership
* migration path
* cost and scale impact
* organizational fit
### 3. Strong signals
* explains why one option is better in a given context
* identifies risks and mitigations
* connects code, platform, security, and operations
* thinks in systems, not isolated services
### 4. Interview takeaway
At higher experience levels, the interviewer is usually testing decision quality more than product recall.`
      }
    ]
  },
  {
    id: 'angular-guide',
    title: 'Angular Web Frontend',
    subtitle: 'Modern Angular Core, RxJS reactive control, and state design',
    rating: '⭐⭐⭐⭐☆',
    icon: 'Layout',
    description: 'Master Components, Directives, Pipes, RxJS pipes, Signals, Dependency Injection, NgRx, and lazy loading.',
    questions: [
      {
        id: 'ng-1',
        category: 'Modern Core & Signals',
        difficulty: 'Hard',
        question: 'What are Angular Signals? How do they differ from RxJS Observables, and how do they optimize change detection?',
        answer: `Introduced in Angular 16+, **Signals** provide a reactive model that tracks state modifications throughout the application and notifies dependent components automatically.

### 1. Signals vs. RxJS Observables
*   **Signals:**
    *   Synchronous.
    *   Always have an initial/current value.
    *   Do not require subscriptions (read value dynamically using getter: \`mySignal()\`).
    *   Designed for local/component UI state management.
*   **RxJS Observables:**
    *   Asynchronous or synchronous.
    *   Event-driven (streams of 0, 1, or many items over time).
    *   Require explicit cleanup/unsubscribe to prevent memory leaks.
    *   Ideal for handling HTTP pipelines, WebSockets, or debounce operations.

### 2. Change Detection Optimization
Without Signals, Angular relies on **Zone.js** to detect changes. Any event (click, timer, network return) triggers a full-tree dirty check from the root component downwards (even with \`OnPush\` optimisation).
*   **Signals Change Detection:** Signals allow Angular to track *specifically* where a state value is referenced in the DOM. Instead of a global dirty check loop, the renderer updates only the exact DOM node bound to the signal, enabling **zone-less** ultra-fast renders.`
      },
      {
        id: 'ng-2',
        category: 'RxJS & Async',
        difficulty: 'Medium',
        question: 'What is the difference between switchMap, mergeMap, concatMap, and exhaustMap in RxJS? Provide concrete examples.',
        answer: `These operators are used to flatten high-order observables (mapping a stream value to a new nested observable), but differ in how concurrent requests are managed:

*   **switchMap (Search/Typing):** Unsubscribes from the previous inner observable when a new source value arrives.
    *   *Usage:* Typeahead searching. If user types quickly, cancel previous network fetches.
*   **mergeMap (Concurrent Fetches):** Subscribes to all inner observables concurrently as they arrive.
    *   *Usage:* Retracting metadata updates for a list of items where order doesn't matter and parallel execution is fine.
*   **concatMap (Sequential Operations):** Subscribes to inner observables sequentially, queueing others.
    *   *Usage:* Saving steps of a sequential multi-page checkout flow where order must be preserved.
*   **exhaustMap (Double-Submit Protection):** Ignores new incoming values if the current active inner observable has not completed.
    *   *Usage:* Form submit button. Block action until payment gateway returns status.`
      },
      {
        id: 'ng-3',
        category: 'Performance & Optimization',
        difficulty: 'Hard',
        question: 'How does ChangeDetectionStrategy.OnPush work in Angular? How does it differ from Default change detection, and what triggers an OnPush component update?',
        answer: `By default, Angular runs change detection on **every component in the component tree** whenever an asynchronous event occurs (click, timer, HTTP response).

### 1. Default vs OnPush Change Detection
* **Default Strategy:** Every component is checked during every change detection cycle, regardless of whether its data actually changed.
* **OnPush Strategy:** Instructs Angular to skip change detection for this component and its child subtree **unless explicitly triggered**.

### 2. What triggers change detection in an OnPush component?
1. **Input Reference Change:** An \`@Input()\` property reference changes (primitive value change or new object reference via immutability).
2. **Event Emitted from Component:** An event handler inside the component or its template fires (e.g. \`(click)\`).
3. **Async Pipe Emission:** An Observable bound via \`async\` pipe emits a new value (calls \`markForCheck()\`).
4. **Explicit Trigger:** Manual invocation of \`ChangeDetectorRef.markForCheck()\` or \`detectChanges()\`.
5. **Signal Update:** A Signal referenced inside the component's template updates its value.`
      },
      {
        id: 'ng-4',
        category: 'Architecture & State',
        difficulty: 'Medium',
        question: 'Explain Dependency Injection (DI) in Angular. What are ProvidedIn options ("root", "platform", component-level) and how does Injector Hierarchy resolution work?',
        answer: `Angular's Dependency Injection system manages service instantiation and lifecycle across a hierarchical injector tree.

### 1. ProvidedIn Options
* **\`providedIn: 'root'\` (Recommended):** Creates a single, shared singleton instance across the entire application. Enables **tree-shaking**—if the service is never used in code, it is omitted from the production bundle.
* **Component-level Providers (\`@Component({ providers: [MyService] })\`):** Creates a new instance of the service scoped to that component and its children. The instance is destroyed when the component is unmounted.
* **\`providedIn: 'platform'\`:** Shared singleton across multiple Angular applications running on the same webpage.

### 2. Injector Hierarchy Resolution
When a component requests a dependency, Angular searches upwards in the injector tree:
\`Element Injector (Component) -> Parent Component Injector -> Module / Environment Injector -> Root Injector -> NullInjector (throws Error)\`.`
      },
      {
        id: 'ng-5',
        category: 'Routing & Lazy Loading',
        difficulty: 'Medium',
        question: 'How do functional Route Guards (canActivate, canDeactivate) and Lazy Loading work in modern Angular (v15+)?',
        answer: `Modern Angular replaced class-based guards with lightweight, composable **functional guards** using the \`inject()\` function.

### 1. Functional Route Guards
\`\`\`typescript
// Modern Angular Functional Guard
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoggedIn()) {
    return true;
  }
  return router.createUrlTree(['/login']);
};
\`\`\`

### 2. Standalone Component Lazy Loading
Route configurations use \`loadComponent\` or \`loadChildren\` to split bundles dynamically:

\`\`\`typescript
export const routes: Routes = [
  {
    path: 'dashboard',
    loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [authGuard]
  }
];
\`\`\``
      },
      {
        id: 'ng-6',
        category: 'Directives & Pipes',
        difficulty: 'Medium',
        question: 'What is the difference between Structural Directives and Attribute Directives? How do Pure vs Impure Pipes differ?',
        answer: `### 1. Structural vs Attribute Directives
* **Structural Directives:** Change the DOM layout by adding, removing, or manipulating DOM elements (e.g. \`*ngIf\`, \`*ngFor\`, or custom directives using \`TemplateRef\` and \`ViewContainerRef\`).
* **Attribute Directives:** Change the appearance or behavior of an existing DOM element (e.g. \`ngClass\`, \`ngStyle\`, or a custom \`appHighlight\` directive).

### 2. Pure vs Impure Pipes
* **Pure Pipes (Default):** Executed **only** when Angular detects a change to the input value (primitive change or object reference change). Highly performant.
* **Impure Pipes (\`@Pipe({ name: 'myPipe', pure: false })\`):** Executed on **every change detection cycle**, regardless of whether the input reference changed. Can cause severe performance degradation if complex logic is inside.`
      },
      {
        id: 'ng-7',
        category: 'Forms & Validation',
        difficulty: 'Hard',
        question: 'Compare Reactive Forms vs Template-Driven Forms in Angular. How do typed forms and custom Async Validators work?',
        answer: `### 1. Reactive Forms vs Template-Driven Forms
* **Reactive Forms:** Synchronous, immutable data model driven by code in the TypeScript class (\`FormGroup\`, \`FormControl\`). Highly scalable, testable, and supports typed controls since Angular 14.
* **Template-Driven Forms:** Asynchronous, mutable model driven by directives in the HTML template (\`ngModel\`). Ideal for simple forms.

### 2. Custom Async Validator Example
Async validators return an Observable or Promise yielding validation errors or \`null\`:

\`\`\`typescript
export function uniqueUsernameValidator(userService: UserService): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return timer(300).pipe( // Debounce check
      switchMap(() => userService.checkUsernameExists(control.value)),
      map(isTaken => (isTaken ? { usernameTaken: true } : null)),
      catchError(() => of(null))
    );
  };
}
\`\`\``
      },
      {
        id: 'ng-8',
        category: 'RxJS & Reactive Patterns',
        difficulty: 'Hard',
        question: 'Compare Subject, BehaviorSubject, ReplaySubject, and AsyncSubject in RxJS. When would you use each in Angular?',
        answer: `### Comparison Matrix
* **Subject:** No initial value, no memory buffer. Late subscribers only receive values emitted *after* subscription.
  * *Use case:* Event bus triggers (e.g. user click events, global toast notifications).
* **BehaviorSubject:** Requires an initial value. Stores the **latest** emitted value. New subscribers immediately receive the current value.
  * *Use case:* App theme state, user authentication profile status.
* **ReplaySubject:** Stores a specified buffer size of previous values (\`new ReplaySubject(3)\`). New subscribers receive the last N values.
  * *Use case:* Audit log history, recent search history cache.
* **AsyncSubject:** Only emits the **final value** when the observable completes.
  * *Use case:* Long-running computational tasks or single HTTP requests.`
      },
      {
        id: 'ng-9',
        category: 'Modern Control Flow',
        difficulty: 'Easy',
        question: 'Explain the built-in control flow syntax (@if, @for, @switch) introduced in Angular 17. Why is it superior to structural directives?',
        answer: `Angular 17 introduced built-in template control flow syntax replacing micro-syntax directives (\`*ngIf\`, \`*ngFor\`).

### 1. Modern Syntax Example
\`\`\`html
@if (user.isLoggedIn) {
  <p>Welcome, {{ user.name }}!</p>
} @else {
  <button (click)="login()">Log in</button>
}

@for (item of items; track item.id) {
  <div>{{ item.name }}</div>
} @empty {
  <p>No items found.</p>
}
\`\`\`

### 2. Key Advantages
1. **Performance:** Does not require importing \`CommonModule\` or directive overhead.
2. **Mandatory Tracking:** \`@for\` mandates a \`track\` expression, preventing accidental un-optimized re-renders.
3. **Type Narrowing:** Better type-checking inside template blocks.`
      },
      {
        id: 'ng-10',
        category: 'Micro Frontends & Component Communication',
        difficulty: 'Medium',
        question: 'How do @Input(), @Output(), ViewChild, and Services manage component communication in Angular?',
        answer: `### Component Communication Patterns
1. **Parent to Child:** Pass data down via \`@Input()\` properties or signal inputs (\`input()\`).
2. **Child to Parent:** Emit events up via \`@Output() new EventEmitter<T>()\` or signal outputs (\`output()\`).
3. **Parent to Direct Child View:** Use \`@ViewChild(ChildComponent)\` to access methods/properties directly after \`ngAfterViewInit\`.
4. **Unrelated / Deep Components:** Use a shared **Injectable Service** with a \`BehaviorSubject\` or \`Signal\` state.`
      },
      {
        "id": "ng-11",
        "category": "Signals & Reactivity Deep Dive",
        "difficulty": "Hard",
        "question": "How does Angular's signal reactivity graph work internally? Explain the push-pull model, the role of ReactiveNode, and why effects do not run synchronously on signal mutation.",
        "answer": "Angular's signal system is built on a **push-pull (glitch-free) reactivity** model implemented atop `ReactiveNode` in `@angular/core/primitives/signals`.\n\n### 1. Push-Pull Model\n* **Push phase (dependency tracking):** When a consumer (`computed` or `effect`) runs, it registers as a dependent of any signal read during its execution. Mutating a signal (`set`/`update`) only **marks** dependent consumers as dirty — it does NOT immediately recompute or re-run them.\n* **Pull phase (lazy evaluation):** When a dirty `computed` is later read, it re-evaluates and caches the new value. Effects are scheduled by the scheduler, not run synchronously.\n\n### 2. Why effects are async\nThis deferral prevents **glitches** — transient inconsistent states where a downstream consumer reads two signals that have not yet both updated. By batching notifications in a scheduler tick, Angular guarantees every signal has stabilized before effects fire.\n\n### 3. ConsumerProducerNode\nInternally each signal is a `SignalNode`, each `computed` a `ComputedNode`, and each `effect` an `EffectNode`. They share the `ReactiveNode` base which tracks `producers` (things I read) and `consumers` (things reading me), enabling fine-grained graph traversal and propagation."
      },
      {
        "id": "ng-12",
        "category": "Signals & RxJS Interop",
        "difficulty": "Hard",
        "question": "Explain toSignal and toObservable semantics. What are the timing pitfalls (sync vs async emission, equal comparator, manualCleanup) and how do they affect memory and change detection?",
        "answer": "### 1. toSignal(Observable → Signal)\n* Subscribes immediately (similar to `async` pipe) and auto-unsubscribes on component/service destruction.\n* **Initial value problem:** Observables may not emit synchronously. Options:\n  * `initialValue: T` — fallback until first emission.\n  * Omit it — signal is `undefined | T` initially.\n  * `requireSync: true` — enforces synchronous emission (e.g., `BehaviorSubject`); throws otherwise.\n* **`equal` comparator:** prevents redundant signal updates when consecutive emissions are deep-equal but reference-different, avoiding unnecessary CD cycles.\n* **`manualCleanup: true`** disables auto-unsubscribe; useful for naturally-completing streams.\n\n### 2. toObservable(Signal → Observable)\n* Internally uses an `effect` that pushes signal values into a `ReplaySubject(1)`.\n* **Timing pitfall:** The first value may emit synchronously on subscribe; **all subsequent values are async** (scheduled in the effect's flush cycle).\n* If you mutate a signal multiple times in the same tick, the observable emits only the **final** stabilized value — intermediate values are coalesced.\n\n### 3. Common Mistakes\n* Calling `toSignal(sameObservable)` inside a method repeatedly — creates multiple subscriptions and leaks. Hoist it to a field initializer.\n* Expecting `toObservable` to emit every intermediate signal set value."
      },
      {
        "id": "ng-13",
        "category": "Change Detection Internals",
        "difficulty": "Hard",
        "question": "How does Angular's change detection work with zoneless mode? What is the role of `markForCheck()` vs `ChangeDetectorRef.detectChanges()`, and how do signals eliminate the need for these?",
        "answer": "### 1. Zoneless Change Detection\nIn `bootstrapApplication(AppComponent, { providers: [provideExperimentalZonelessChangeDetection()] })` (now stable in v20+), Angular no longer monkey-patches async APIs via Zone.js. Instead, CD is triggered only by:\n* Signal updates (notify scheduler).\n* `ChangeDetectorRef.markForCheck()`.\n* Component template event handlers.\n* `async` pipe emissions (internally calls `markForCheck`).\n* `afterRender` / `afterNextRender` hooks.\n\nA **scheduler** batches these notifications into a single CD pass.\n\n### 2. markForCheck vs detectChanges\n* **`markForCheck()`:** Marks the component + ancestors as dirty. The actual CD runs once during the next scheduled tick — preferred, avoids redundant CD passes.\n* **`detectChanges()`:** Synchronously runs CD for the component subtree. Use sparingly (e.g., imperative DOM measurements before next tick). Often a code smell indicating missing signal/OnPush wiring.\n\n### 3. Why Signals Make Both Obsolete\nSignals register themselves with the renderer. On mutation, only the specific DOM binding referencing that signal is patched — no full-component dirty check needed. This is the foundation of true fine-grained reactivity and the path to making `ChangeDetectorRef` largely unnecessary."
      },
      {
        "id": "ng-14",
        "category": "Dependency Injection Internals",
        "difficulty": "Hard",
        "question": "Describe Angular's DI resolution algorithm including forwardRef, multi-providers, Optional/Self/SkipSelf, and the new `NodeInjectorLookup` token behavior with standalone components.",
        "answer": "### 1. Resolution Walk\n`ElementInjector → ViewContainerRef injector → Parent ElementInjector → ... → EnvironmentInjector → PlatformInjector → NullInjector (throws `StaticError`).` Each lookup consults `directive` providers first, then component-level `providers`, then walks up.\n\n### 2. Resolution Modifiers\n* **`@Optional()`:** Returns `null` instead of throwing when not found.\n* **`@Self()`:** Only consults the current element's injector (skips parents).\n* **`@SkipSelf()`:** Starts lookup one level above.\n* **`@Host()`:** Stops at the host element of the component tree (used by `@ViewChild`-style directive injection).\n\n### 3. multi: true\nAdds to an array bound to the same token. Useful for HTTP interceptors, `NG_VALIDATORS`, or plugin systems: `inject(MY_TOKEN)` returns `T[]`.\n\n### 4. forwardRef\nResolves circular reference issues when a class references itself or a not-yet-declared class in `@Inject`.\n\n### 5. Standalone + NodeInjectorLookup\nWith standalone APIs, the framework uses a more efficient `NodeInjectorLookup` table. `imports` in a standalone component do NOT contribute to its injector — only `providers` and `viewProviders` do. This eliminated the `ModuleInjector` ambiguity that existed with NgModules."
      },
      {
        "id": "ng-15",
        "category": "RxJS Advanced Patterns",
        "difficulty": "Hard",
        "question": "How do you handle higher-order observables safely in Angular? Discuss the difference between combineLatest, forkJoin, zip, and withLatestFrom — and common pitfalls like the 'combineLatest of empty' problem.",
        "answer": "### 1. Combination Operators\n* **`combineLatest([a$, b$])`:** Emits when **any** source emits **after all have emitted at least once**. New subscribers receive latest values from each. Pitfall: emits nothing until every source emits at least once.\n* **`forkJoin([a$, b$])`:** Waits for **all sources to complete**, then emits a single array of their **last** values. Ideal for parallel HTTP calls. **Pitfall:** if any source is a non-completing stream (e.g., `BehaviorSubject` piped through `map`), `forkJoin` never emits.\n* **`zip([a$, b$])`:** Emits when **each source has emitted its next value** — pairs emissions by index. Slowest source paces emission.\n* **`withLatestFrom(b$)`:** Source A drives emissions; each emission snapshots the latest value from B (B must have emitted first or the emission is skipped).\n\n### 2. Higher-Order Flattening Safety\nUse `switchMap`/`mergeMap`/`concatMap`/`exhaustMap` per use-case (see ng-2). Always `takeUntilDestroyed()` or rely on `toSignal` to avoid leaks.\n\n### 3. The `combineLatest` of Empty Problem\n`combineLatest([])` completes immediately with no emission. `combineLatest` of an array containing a never-emitting observable also stalls. Guard with `startWith`, `defaultIfEmpty`, or use `combineLatest` only when all sources are guaranteed to emit (e.g., `BehaviorSubject`)."
      },
      {
        "id": "ng-16",
        "category": "Performance & Memory Leaks",
        "difficulty": "Hard",
        "question": "What are the most common memory leak patterns in Angular applications and how do you diagnose them using Chrome DevTools, Angular DevTools, and the `takeUntilDestroyed` pattern?",
        "answer": "### 1. Common Leak Patterns\n* **Unsubscribed Observables:** Subscriptions in `ngOnInit` that aren't torn down in `ngOnDestroy`. Fix with `takeUntilDestroyed()` (auto-uses `DestroyRef`) or `takeUntil(destroy$)`.\n* **`setInterval`/`setTimeout` not cleared:** Always clear timers in `ngOnDestroy`.\n* **Event listeners added imperatively:** `addEventListener` without `removeEventListener`.\n* **Detached DOM nodes held by closures:** ViewChild references retained in long-lived services.\n* **NgRx effects / component store subscriptions** not registered with the lifecycle.\n* **`ResizeObserver`/`IntersectionObserver`** not disconnected.\n\n### 2. Diagnostic Tools\n* **Chrome DevTools → Memory tab:** Heap snapshot diff before/after navigating away from a component. Look for retained `ComponentRef`, `ViewRef`, or DOM nodes.\n* **Angular DevTools Profiler:** Shows components still in tree after navigation — indicates missing route reuse strategy cleanup.\n* **Performance → Memory timeline:** Detect growing JS heap over time.\n\n### 3. Modern Cleanup Pattern\n```typescript\nconstructor() {\n  this.api.data$\n    .pipe(takeUntilDestroyed())\n    .subscribe(data => this.data.set(data));\n}\n```\n`takeUntilDestroyed()` injects `DestroyRef` automatically — no manual `ngOnDestroy` needed."
      },
      {
        "id": "ng-17",
        "category": "SSR & Hydration",
        "difficulty": "Hard",
        "question": "Explain Angular's hydration strategy. What is the difference between full hydration, partial hydration with @defer, and resumability? Why does Angular still require hydration while Qwik claims resumability?",
        "answer": "### 1. Hydration Basics\nAfter SSR, the browser receives fully-rendered HTML and the JS bundle. **Hydration** attaches event listeners and reconciles the existing DOM with the client-side component tree. Angular v17+ ships **non-destructive hydration** (`provideClientHydration()`) — it reuses SSR DOM rather than throwing it away, eliminating the prior flash-of-replaced-content.\n\n### 2. Partial Hydration via @defer\n`@defer` blocks are NOT rendered on the server (or rendered as placeholders) and only hydrate on the client when trigger conditions (`on viewport`, `on idle`, `on interaction`, `on timer`) fire. This reduces initial JS execution and TTI on content-heavy pages.\n\n### 3. Resumability vs Hydration\n* **Hydration:** Re-executes component logic on the client to rebuild the reactive graph; framework must re-walk the tree to attach listeners. Memory and CPU cost.\n* **Resumability (Qwik):** Serializes the entire execution state (component closures, listeners, subscriptions) into the HTML itself as JSON. The browser can resume exactly where the server left off — no component logic executed until an interaction triggers it.\n\n### 4. Why Angular Hydrates Instead of Resumes\nAngular's DI, lifecycle hooks, and `NgModule`/standalone tree are too complex to serialize lazily. Resumability requires the framework to be designed around lazy execution from day one. Angular opts for incremental optimizations (partial hydration, incremental hydration roadmap) rather than full resumability."
      },
      {
        "id": "ng-18",
        "category": "Routing & State Management",
        "difficulty": "Hard",
        "question": "Compare NgRx (Store/Effects/Entity), NgRx ComponentStore, Signals-based state, and Akita/Elf. When would you choose each for a large enterprise app?",
        "answer": "### 1. NgRx (Store + Effects + Entity)\n* **Pros:** Single source of truth, time-travel debugging, serializable actions, mature ecosystem, devtools integration.\n* **Cons:** Significant boilerplate, steep learning curve, overkill for small/medium apps.\n* **Use when:** Large team, complex domain logic, need for strict unidirectional flow, audit requirements (banking, healthcare).\n\n### 2. NgRx ComponentStore\n* Lightweight reactive store tied to a component lifecycle. Uses RxJS.\n* **Use when:** Component-scoped state that doesn't belong in global store (e.g., a complex widget with internal state).\n\n### 3. Signals-based State (e.g., `signalStore` from ngrx/signals or custom)\n* Uses Angular Signals as the primitive — no RxJS subscriptions, fine-grained updates, integrates seamlessly with zoneless CD.\n* **Use when:** Modern Angular app (v17+), want simplicity, prefer signals-first state, fine-grained reactivity.\n\n### 4. Akita / Elf\n* Entity-oriented stores with normalized schema. Elf is Akita's modern signal-aware successor.\n* **Use when:** Heavy CRUD over collections, want ORM-like querying without Redux ceremony.\n\n### Senior Decision Matrix\n| Need | Recommended |\n|---|---|\n| Strict unidirectional flow, audit | NgRx Store |\n| Modern, signal-first app | NgRx SignalStore |\n| Component-local state | ComponentStore or signal fields |\n| Entity-heavy CRUD | Elf / SignalCollection |"
      },
      {
        "id": "ng-19",
        "category": "HTTP & Interceptors",
        "difficulty": "Medium",
        "question": "How do functional HTTP interceptors differ from class-based ones in modern Angular? Show a real-world interceptor that handles JWT refresh tokens, retries, and silent failure.",
        "answer": "### 1. Functional vs Class Interceptors\nModern Angular (v15+) replaces `HttpInterceptor` class with `HttpInterceptorFn`. They are:\n* Tree-shakeable.\n* Composable (no `@Injectable` boilerplate).\n* Easier to test — pure functions.\n* Configurable per request via closures.\n\n### 2. JWT Refresh Interceptor Example\n```typescript\nexport const authInterceptor: HttpInterceptorFn = (req, next) => {\n  const auth = inject(AuthService);\n  const router = inject(Router);\n\n  if (isPublicApi(req.url)) return next(req);\n\n  return auth.getValidToken().pipe(\n    switchMap(token => {\n      const authReq = req.clone({\n        setHeaders: { Authorization: `Bearer ${token}` }\n      });\n      return next(authReq).pipe(\n        catchError(err => {\n          if (err.status === 401) {\n            return auth.refreshToken().pipe(\n              switchMap(newToken => {\n                const retryReq = req.clone({\n                  setHeaders: { Authorization: `Bearer ${newToken}` }\n                });\n                return next(retryReq);\n              }),\n              catchError(() => {\n                auth.logout();\n                router.navigate(['/login']);\n                return throwError(() => err);\n              })\n            );\n          }\n          return throwError(() => err);\n        })\n      );\n    })\n  );\n};\n```\nRegister via `provideHttpClient(withInterceptors([authInterceptor]))`."
      },
      {
        "id": "ng-20",
        "category": "Forms Architecture",
        "difficulty": "Hard",
        "question": "Design a complex dynamic forms system in Angular that supports runtime schema-driven form generation with nested FormArrays, cross-field validators, and conditional visibility. What are the pitfalls?",
        "answer": "### 1. Architecture\n* **Schema layer:** JSON Schema or custom DSL describing fields, types, validators, visibility rules.\n* **Form builder service:** Recursively builds `FormGroup`/`FormArray`/`FormControl` from schema. Use `FormBuilder.nonNullable` and typed controls (`FormControl<string>)`.\n* **Component registry:** Map schema `type` → dynamic component rendered via `*ngComponentOutlet` or `ViewContainerRef.createComponent`.\n* **Visibility engine:** Subscribe to value changes; toggle field presence by adding/removing controls — **never just hide via CSS** (else stale values submitted).\n\n### 2. Cross-field Validators\nAttach to the parent `FormGroup`:\n```typescript\nconst dateRangeValidator: ValidatorFn = (group) => {\n  const start = group.get('start')?.value;\n  const end = group.get('end')?.value;\n  return start && end && start > end ? { range: true } : null;\n};\n```\n\n### 3. Pitfalls\n* **Removing controls via `removeAt` while iterating:** Iterate in reverse or use `clear()`.\n* **Forgetting to update value/validity** of parent when dynamically adding controls — call `group.updateValueAndValidity({ emitEvent: false })`.\n* **Mixing reactive + template-driven:** Pick one; mixing causes race conditions.\n* **Memory leaks in subscriptions to `valueChanges`**: Use `takeUntilDestroyed`.\n* **Performance with large FormArrays:** Use virtual scrolling (`cdk-virtual-scroll`) and track by id."
      },
      {
        "id": "ng-21",
        "category": "Standalone Components & NgModules",
        "difficulty": "Medium",
        "question": "What is the migration path from NgModule-based apps to standalone? What are the tradeoffs of lazy loading via `loadChildren` vs `loadComponent`, and how does `provideRouter` with `withComponentInputBinding` change data fetching?",
        "answer": "### 1. Migration Path\n* Run `ng generate @angular/core:standalone` — automated schematic that:\n  1. Converts components/directives/pipes to `standalone: true`.\n  2. Removes them from `declarations`.\n  3. Refactors `exports`/`imports`.\n  4. Optionally bootstraps via `bootstrapApplication`.\n* Gradual: keep `AppModule` as bootstrap while converting leaf components first.\n\n### 2. Lazy Loading Tradeoffs\n* **`loadChildren` (NgModule):** Loads entire feature module (all its components, services, routing tree). Useful when many routes share components/services.\n* **`loadComponent`:** Loads only the single component — smaller initial chunk, ideal for leaf pages.\n* **`loadChildren` returning `Routes[]`:** Modern pattern — lazy load a routes array without a module wrapper.\n\n### 3. withComponentInputBinding\nBinds route parameters, query params, and resolved data directly to `@Input()` (or `input()`) of the component — eliminates `ActivatedRoute.snapshot` boilerplate.\n```typescript\n{ path: 'user/:id', loadComponent: () => import('./user').then(m => m.UserComponent) }\n// In component: id = input<string>();  // auto-populated\n```\nSupports signals inputs naturally."
      },
      {
        "id": "ng-22",
        "category": "Custom Structural Directives",
        "difficulty": "Hard",
        "question": "Build a custom `*appRepeat` structural directive and explain how TemplateRef, ViewContainerRef, embeddedView lifecycle, and context typing work. How does this differ from the modern `@for` block?",
        "answer": "### 1. Implementation\n```typescript\n@Directive({ selector: '[appRepeat]' })\nexport class RepeatDirective<T> implements OnChanges {\n  @Input() appRepeatOf!: T[];\n  @Input() appRepeatTrackBy?: (i: number, item: T) => any;\n\n  constructor(\n    private tpl: TemplateRef<RepeatContext<T>>,\n    private vcr: ViewContainerRef\n  ) {}\n\n  ngOnChanges() {\n    this.vcr.clear();\n    this.appRepeatOf.forEach((item, i) => {\n      this.vcr.createEmbeddedView(this.tpl, {\n        $implicit: item, index: i, appRepeatOf: this.appRepeatOf\n      });\n    });\n  }\n}\n\ninterface RepeatContext<T> {\n  $implicit: T;\n  index: number;\n  appRepeatOf: T[];\n}\n```\nUsage: `<div *appRepeat=\"let item of items\">{{ item }}</div>`\n\n### 2. Concepts\n* **TemplateRef:** Reference to the `<ng-template>` Angular wraps around `*`-prefixed directives.\n* **ViewContainerRef:** Anchor where embedded views are inserted/removed.\n* **createEmbeddedView:** Instantiates the template with a context object; `$implicit` is the default `let x` binding.\n\n### 3. Difference vs `@for`\n* `@for` is compiled inline by the Angular template compiler — no `TemplateRef`/`ViewContainerRef` allocation, no directive instance, and the `track` expression is mandatory and verified at compile time.\n* Custom directives are slower (extra directive instance + view container overhead) and easier to misuse (no compile-time track enforcement).\n* Use `@for` for production iterations; reserve custom structural directives for cases the built-ins can't express."
      },
      {
        "id": "ng-23",
        "category": "Testing Strategies",
        "difficulty": "Hard",
        "question": "How do you set up an effective testing strategy for an Angular app covering unit, integration, and component harness tests? Discuss TestBed, harnesses, fakeAsync, and signal-based testing.",
        "answer": "### 1. Layered Strategy\n* **Pure unit tests:** Services, utilities, reducers — no TestBed. Use plain `new Service()` with mocked deps.\n* **Component integration tests:** `TestBed.configureTestingModule({ imports: [Component] })` with real templates but mocked services via `provide` overrides.\n* **Harness tests:** Use `@angular/cdk/testing` harnesses for Material components — encapsulates interaction APIs and insulates tests from DOM structure.\n* **E2E:** Cypress or Playwright for user journeys.\n\n### 2. TestBed Modernization\n* Use `TestBed.configureTestingModule` with standalone imports instead of `TestBed.configureTestingModule({ declarations: [...] })`.\n* Prefer `provideMockStore` (NgRx) or override providers via `{ provide: X, useValue: mock }`.\n\n### 3. fakeAsync / tick / flush\n* `fakeAsync` enables deterministic testing of timers and promises.\n* `tick(100)` advances virtual time by 100ms.\n* `flush()` drains all pending microtasks/macrotasks.\n* **Pitfall:** Mixing `fakeAsync` with real `setTimeout` or `requestAnimationFrame` breaks — use `flushMicrotasks` selectively.\n\n### 4. Signal Testing\n```typescript\nit('counter increments', () => {\n  const counter = signal(0);\n  // Read inside an effect to track\n  let last = 0;\n  effect(() => last = counter());\n  TestBed.runInInjectionContext(() => counter.update(v => v + 1));\n  TestBed.flushEffects();  // or tick()\n  expect(last).toBe(1);\n});\n```\nAlways call `TestBed.flushEffects()` (or `tick`) after mutating signals to run scheduled effects synchronously."
      },
      {
        "id": "ng-24",
        "category": "Accessibility & CDK",
        "difficulty": "Medium",
        "question": "How do you build an accessible custom dropdown component using Angular CDK? Discuss Overlay, FocusTrap, AriaDescriber, and keyboard navigation patterns.",
        "answer": "### 1. CDK Building Blocks\n* **`Overlay` service:** Creates a floating panel attached to the trigger via `ConnectedPositionStrategy`. Handles scroll/close backdrop.\n* **`CdkTrapFocus` directive:** Confines Tab/Shift+Tab navigation inside the dropdown while open; restores focus to trigger on close.\n* **`AriaDescriber`:** Adds `aria-describedby` IDs for hint/error text — useful for form controls.\n* **`LiveAnnouncer`:** Speaks state changes to screen readers (e.g., '3 results available').\n* **`CdkScrollable`/`ScrollDispatcher`:** Closes overlay when the page scrolls (optional).\n\n### 2. Keyboard Pattern\n* `ArrowDown` / `Enter` / `Space`: open menu, focus first option.\n* `ArrowDown`/`ArrowUp`: move active option (use `ListKeyManager` from CDK a11y).\n* `Enter`: select.\n* `Escape`: close, restore focus to trigger.\n* `Home`/`End`: jump to first/last option.\n* Type-ahead: `ListKeyManager` supports `skipPredicate` and `typeAheadDebounceInterval`.\n\n### 3. Required ARIA\n* Trigger: `role='combobox'`, `aria-expanded`, `aria-haspopup='listbox'`, `aria-controls`.\n* Panel: `role='listbox'`, `aria-activedescendant` pointing to active option.\n* Options: `role='option'`, `aria-selected`."
      },
      {
        "id": "ng-25",
        "category": "Internationalization (i18n)",
        "difficulty": "Medium",
        "question": "Compare Angular's built-in i18n vs third-party libraries (@ngx-translate, Transloco). When do you choose each? How does runtime i18n impact bundle size and change detection?",
        "answer": "### 1. Angular Built-in i18n\n* Compile-time: extracts template strings into XLIFF/JSON. Per-locale builds (`--localize`) produce separate bundles.\n* **Pros:** Zero runtime cost, no library, native plural/gender support.\n* **Cons:** One bundle per locale (multi-build deployment), no runtime switching without reload, ICU message syntax is verbose.\n* **Use when:** App serves one locale per deployment, SEO and initial-load performance are critical.\n\n### 2. @ngx-translate / Transloco\n* Runtime translation via JSON files. Transloco is the more modern successor with lazy loading, scope-based loading, and signal support.\n* **Pros:** Single bundle, runtime locale switching, hot-swappable translations.\n* **Cons:** Library overhead, JSON files included in bundle (or fetched), `translate` pipe runs on every CD cycle (impure) — can impact OnPush performance unless memoized.\n* **Use when:** Multi-locale SPA, SaaS dashboards, runtime locale switch required.\n\n### 3. Bundle / CD Impact\n* Angular i18n: ~0 KB runtime cost.\n* Transloco: ~10–15 KB minified + translation files. Use `translocoSignalPipe` or memoized `pure` pipe to avoid running translations every CD tick."
      },
      {
        "id": "ng-26",
        "category": "Animation & Performance",
        "difficulty": "Medium",
        "question": "How do Angular animations work, and what are the performance tradeoffs between Web Animations API (Angular Animations), CSS animations, and the new `@defer` + CSS view transitions API?",
        "answer": "### 1. Angular Animations\nBuilt atop the Web Animations API via `@angular/animations`. Define `trigger`/`state`/`transition`/`animate` in component metadata.\n* **Pros:** Declarative state-based animations, integrates with lifecycle (`:enter`/`:leave`), works with `*ngIf` and `@if`.\n* **Cons:** Polyfill needed in some browsers, ~30 KB extra bundle, can stutter on low-end mobile without GPU-accelerated properties.\n\n### 2. CSS Animations\n* Lightest, browser-native, no JS overhead.\n* Best for hover/micro-interactions, transitions on `transform`/`opacity`.\n* Harder to coordinate with Angular lifecycle (no `:leave` equivalent without manual classes).\n\n### 3. View Transitions API + Angular\n* Modern browser API (`document.startViewTransition`) snapshots old/new DOM and crossfades automatically.\n* Pair with `@defer` for lazy route transitions — Angular v17+ supports `withViewTransitions()` in router config.\n* **Pros:** Zero-animation-code route transitions, GPU-accelerated, native.\n* **Cons:** Browser support limited (Chrome/Edge; Safari partial). Progressive enhancement recommended.\n\n### 4. Performance Rules\n* Animate only `transform` and `opacity` (compositor-only properties).\n* Avoid animating `width`/`height`/`top`/`left` — triggers layout reflow.\n* Use `will-change` sparingly; remove after animation ends."
      },
      {
        "id": "ng-27",
        "category": "Micro Frontends",
        "difficulty": "Hard",
        "question": "Design a micro frontend architecture using Angular. Compare single-spa, Module Federation (Webpack/Rspack/Vite federation), and Angular Elements. What are the pitfalls around routing, DI, and shared state?",
        "answer": "### 1. Approaches\n* **Angular Elements (`@angular/elements`:** Wraps a component as a custom element (`customElements.define`). Each MFE is a self-contained bundle. Pros: framework-agnostic, works in any host. Cons: full Angular runtime per MFE → large payload when multiple MFEs coexist.\n* **Module Federation (Webpack 5 / Rspack / Vite native federation):** Shares Angular runtime and common libs via `shared` singleton config. Lazy-loads remote bundles at runtime. Best for homogeneous Angular MFEs.\n* **single-spa:** Lifecycle-based MFE orchestrator. Each MFE exposes `bootstrap`/`mount`/`unmount`. Works across frameworks (Angular + React + Vue). More glue code.\n\n### 2. Pitfalls\n* **Multiple Angular instances:** Each MFE that bundles its own Angular breaks DI and CD performance. Use `shared: ['@angular/core', '@angular/common', ...]` with `singleton: true, strictVersion: true`.\n* **Router conflict:** Each MFE has its own `Router` — collisions on routes. Use `provideRouter` with `withInMemoryScrolling` and prefix route paths per MFE.\n* **Shared state:** Avoid shared services across MFEs (version drift). Use postMessage, CustomEvents, or a shared state microservice (e.g., via BroadcastChannel).\n* **CSS encapsulation:** Use Shadow DOM (`ViewEncapsulation.ShadowDom`) or CSS modules to avoid style bleed.\n* **Bundle size:** Module Federation shared chunks can bloat if not tree-shaken; audit with `bundle-stats`."
      },
      {
        "id": "ng-28",
        "category": "Security",
        "difficulty": "Hard",
        "question": "Discuss Angular's built-in security model: how does Angular sanitize content? Explain DomSanitizer bypass patterns, CSRF handling, and XSS prevention in modern Angular with Trusted Types.",
        "answer": "### 1. Built-in Sanitization\nAngular auto-sanitizes interpolated values (`{{ }}`) and `[property]` bindings using context-aware sanitizers:\n* `SecurityContext.HTML` — strips dangerous tags/scripts.\n* `SecurityContext.URL` — blocks `javascript:` URIs.\n* `SecurityContext.RESOURCE_URL` — only allows same-origin or trusted URLs.\n* `SecurityContext.STYLE` — neutralizes `expression()`, `url()`, and similar constructs.\n\n### 2. Explicit Bypass (Use Sparingly)\n```typescript\nconstructor(private sanitizer: DomSanitizer) {}\ntrustedHtml = this.sanitizer.bypassSecurityTrustHtml(userInput);\n```\nOnly when content is from a fully-trusted source (e.g., generated from a vetted CMS). Misuse reintroduces XSS.\n\n### 3. CSRF\n* Angular's `HttpClientXsrfModule` (or `provideHttpClient(withXsrfConfiguration())`) reads a token from a cookie and sends it as a header (default `X-XSRF-TOKEN`) on mutating requests. Server must set the cookie on the same origin.\n* **Pitfall:** Doesn't work cross-origin by default — configure `withCredentials: true` and CORS properly.\n\n### 4. Trusted Types (Angular v14+)\nSet `angular.trusted-types` CSP policy. Angular auto-creates a Trusted Types policy named `angular#bundler` for its internal DOM writes. To inject custom trusted HTML, create your own policy and bypass through it. Protects against DOM-based XSS sinks (`innerHTML`, `eval`).\n\n### 5. SSR Security\n* Use `provideServerRouting` with `withInMemoryScrolling` — never expose server-only secrets in client bundles.\n* `isPlatformBrowser` guards prevent `window`/`document` access during SSR."
      }
    ]
  },
  {
    id: 'system-design-guide',
    title: 'System Design Interview',
    subtitle: 'Senior Architectural Blueprint and Scaling Practices',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Database',
    description: 'Design WhatsApp, Netflix, Uber, E-commerce platforms, Banking systems, and Chatbox scale. Covers scaling, caching, queues, and trade-offs.',
    questions: [
      {
        id: 'sd-1',
        category: 'Architectural Design Scenarios',
        difficulty: 'Hard',
        question: 'Design a scalable E-commerce Platform (Black Friday Traffic). How do you handle database scaling, inventory checks, checkout queues, and cache design?',
        answer: `Designing an e-commerce platform that can handle Black Friday flash sales requires segregating write workloads from read workloads and applying asynchronous processing.

### 1. Requirements & Scaling Goals
*   **Scale:** Support 100,000 requests per second.
*   **Availability:** Keep catalog readable even if database writes are locked/throttled.
*   **Consistency:** Avoid double-selling inventory (over-allocation).

### 2. High-Level Architecture
\`\`\`
Client -> CDN (Static Assets)
  | -> API Gateway (Cors, Security validation)
         | -> Catalog Service (Read-optimized Redis Cache -> NoSQL Database)
         | -> Order Handling Service (RabbitMQ/Kafka Queue -> Worker App -> Relational DB)
\`\`\`

### 3. Key Design Components
*   **Database Partitioning:**
    *   **Catalog Service:** Use a document database (like Amazon DynamoDB or Azure Cosmos DB) containing the product records. Read queries hitting Cosmos DB are highly scalable.
    *   **Order Service:** Requires ACID transactions. Use PostgreSQL or SQL Server with sharding (e.g. sharding by CustomerId).
*   **Inventory Preservation (Flash Sale Prevention):**
    *   *Direct DB Write:* Modifying database tables with lock flags directly causes instant deadlock timeouts.
    *   *Solution (Distributed Lock / Redis Cache Dedication):* Store inventory count in a distributed Redis database.
    *   *Process:* Perform a Redis \`DECRBY\` check. If Redis counter returns value >= 0:
        1. Push Order request into queue messaging pipeline (messaging queue like RabbitMQ or Azure Service Bus).
        2. Customer receives order summary confirmation.
        3. Background consumers process messages, write to SQL database, and update official registry order details.
*   **Caching Strategy:**
    *   Cache popular listings in Redis.
    *   Use Cache-Aside pattern for specific product lookups.
    *   Apply Cache Stampede mitigation (use distributed locks to ensure only one worker repopulates expired values).`
      },
      {
        id: 'sd-2',
        category: 'Real-Time Systems',
        difficulty: 'Hard',
        question: 'Design WhatsApp (Real-Time Messaging). Discuss connection handling, message delivery notification structure (sent, delivered, read), and group chats scaling.',
        answer: `Real-time messaging applications require persistent connection management and low-latency storage.

### 1. Core Services & Communication Protocols
*   **WebSockets / Gateway:** Use persistent **WebSockets** (or MQTT) connections between clients and the Gateway (Chat Servers) to send and receive real-time messages.
*   **HTTPS REST API:** Used for user authentication, profile settings, and uploading media.

### 2. Message Lifecycle (Sent -> Delivered -> Read)
\`\`\`
User A --------(WebSocket message)--------> Chat Server (Session Manager lookup)
                                                   |
                                                   v
User B <-------(WebSocket delivery)-------- Chat Server (If online)
                                                   | (If offline)
                                                   v
                                            Push Notification Engine
\`\`\`
1.  **Sent:** User A sends a message. The gateway writes it to the database (column database like Cassandra for high-volume writes) and returns an ACK receipt to User A.
2.  **Delivered:** The Chat Server looks up User B's active session.
    *   If User B is **online**, the message is pushed immediately over User B's WebSocket. User B's client responds with an ACK receipt. The Chat Server sends a "Delivered" receipt to User A.
    *   If User B is **offline**, update the DB marking message pending. A push notification (FCM/APNS) is triggered. When User B re-connects, pull all pending messages in a single batch, and push delivery receipts back.
3.  **Read:** Triggered when User B opens chat. User B client sends a "Read" status message API trigger, updating User A.

### 3. Scale challenges
*   **Connection State Management:** Chat servers store active TCP/WebSocket connections. We need a routing tracker (e.g., Redis cluster) mapping User IDs to Gateway Server IPs.
*   **Group Chat scaling:** When a message is sent to a group of 500 people:
    *   Instead of copying the message 500 times, write the message *once* to a Group message table.
    *   Then, write a small metadata row for each recipient in a Delivery index, or use a worker queue to fan-out the notifications.`
      },
      {
        id: 'sd-3',
        category: 'High Scale Storage & Caching',
        difficulty: 'Hard',
        question: 'Design a Distributed Rate Limiter system (e.g. 100 requests per minute per IP). Discuss algorithms (Token Bucket vs Sliding Window) and Redis implementation.',
        answer: `A Rate Limiter prevents abuse, DDoS attacks, and resource exhaustion by throttling client request frequencies.

### 1. Rate Limiting Algorithms
* **Token Bucket:** Tokens added to a bucket at a fixed rate. Requests consume tokens. If bucket is empty, request is dropped. Handles bursts well.
* **Leaky Bucket:** Requests enter a FIFO queue and are processed at a constant leak rate. Smoothes out traffic spikes.
* **Sliding Window Counter:** Divides time into sliding sub-windows, tracking weighted request counts across boundaries. Prevents burst attacks at boundary edges.

### 2. Distributed Redis Implementation (Sliding Window)
Using Redis Sorted Sets (\`ZSET\`):
\`\`\`
1. Key: "rate_limit:user_123"
2. Value: Timestamp (epoch ms)
3. Remove old entries outside window: ZREMRANGEBYSCORE rate_limit:user_123 0 (now - 60000)
4. Count remaining entries: ZCARD rate_limit:user_123
5. If count < limit: ZADD rate_limit:user_123 now now; Allow request. Else: Reject (HTTP 429 Too Many Requests).
\`\`\``
      },
      {
        id: 'sd-4',
        category: 'Distributed Systems',
        difficulty: 'Hard',
        question: 'Design a URL Shortener service (like bit.ly). How do you handle short key generation, collision resolution, and high-volume redirect caching?',
        answer: `### 1. Key Requirements
* Shorten a long URL to 7 characters (e.g. \`https://short.url/abc123X\`).
* Handle 10,000 writes/sec and 100,000 reads/sec with low latency (<10ms).

### 2. Short Key Generation Strategies
* **Base62 Encoding on Auto-Increment ID:** Convert numeric ID (e.g. 100,000,000,000) to Base62 character string (\`[a-z, A-Z, 0-9]\`). Guarantee uniqueness with zero collisions.
* **Pre-generated Key Service (KGS):** A dedicated worker service generates unique 7-char keys ahead of time and stores them in a key-bank database.

### 3. Caching & Redirects
* Cache hot long URLs in **Redis** with LRU eviction.
* Return HTTP **301 Permanent Redirect** for max browser caching, or HTTP **302 Found** if click analytics collection per click is required.`
      },
      {
        id: 'sd-5',
        category: 'Event-Driven Systems',
        difficulty: 'Hard',
        question: 'Design an Event-Driven Notification System handling millions of push, email, and SMS notifications daily. How do you handle idempotency and failure retries?',
        answer: `### Architecture Overview
\`\`\`
Client -> API Gateway -> Notification Dispatcher -> Message Queue (Kafka/ServiceBus) -> Worker Fleet -> Providers (Twilio, SendGrid, FCM)
\`\`\`

### Key Features
1. **Decoupled Queues:** Separate queues per channel (SMS, Email, Push) to prevent slow SMS delivery from blocking fast push notifications.
2. **Idempotency:** Assign a unique \`NotificationId\` per message. Workers check Redis before delivering to prevent sending duplicate emails.
3. **Dead Letter Queue (DLQ):** Failed messages retry with exponential backoff. After N failures, route to DLQ for manual inspection.`
      },
      {
        id: 'sd-6',
        category: 'Distributed Caching',
        difficulty: 'Hard',
        question: 'Explain Consistent Hashing and virtual nodes in distributed caching (Memcached / Redis clusters). What problem does it solve?',
        answer: `### The Problem with Traditional Modulo Hashing
In a traditional cache cluster, keys are mapped via \`hash(key) % N\` (where N is the number of servers). If a server is added or removed, almost **100% of keys re-hash to different servers**, causing a massive cache stampede on the database.

### Consistent Hashing Solution
1. Keys and Servers are mapped to a conceptual **Hash Ring** (e.g. $0$ to $2^{32}-1$).
2. A key is routed to the first server encountered moving clockwise on the ring.
3. **Adding/Removing a Server:** Only $\frac{1}{N}$ of the keys need to be re-mapped!
4. **Virtual Nodes:** Assigning multiple virtual positions per physical server prevents uneven hot-spot key distribution.`
      },
      {
        id: 'sd-7',
        category: 'Data Streaming & Search',
        difficulty: 'Hard',
        question: 'Design a Real-Time Search Autocomplete / Typeahead Suggestion System. Discuss Trie data structures and prefix caching.',
        answer: `### 1. Data Structure: Trie (Prefix Tree)
Store search terms in a **Trie** where each node represents a character. At each node, store the top 5 most frequently searched terms to enable $O(L)$ retrieval time (where L is length of prefix).

### 2. High Availability Architecture
* **Frontend:** Debounce user input by 200ms.
* **Edge Caching:** CDN caches top prefix query results (e.g. "a", "an", "app").
* **Backend:** Trie structure stored in memory on search nodes, updated asynchronously via Kafka log stream of search trends.`
      },
      {
        id: 'sd-8',
        category: 'Microservices Architecture',
        difficulty: 'Hard',
        question: 'Explain the Saga Pattern for distributed transactions across microservices. Compare Orchestration vs Choreography.',
        answer: `Because microservices use Database-per-service, traditional 2-Phase Commit (2PC) transactions do not scale. The **Saga Pattern** breaks a distributed transaction into a sequence of local transactions.

### Orchestration vs Choreography
* **Choreography (Event-Driven):** Each microservice listens to events and executes local transactions independently.
  * *Pros:* Simple for small workflows.
  * *Cons:* Hard to track workflow state as service count grows.
* **Orchestration (Central Coordinator):** A central Saga Orchestrator directs services on what local transactions to execute.
  * *Pros:* Centralized status control and easy rollback management via compensating transactions.
  * *Cons:* Orchestrator can become a single point of failure if not scaled.`
      },
      {
        id: 'sd-9',
        category: 'Video Streaming Architecture',
        difficulty: 'Hard',
        question: 'Design a Video Streaming Platform (like YouTube/Netflix). How do adaptive bitrate streaming (HLS/DASH) and video transcoding workers work?',
        answer: `### Key Workflow
1. **Upload & Chunking:** Original video uploaded to Blob storage. Worker breaks video into 2-10 second chunk files (\`.ts\` / \`.m4s\`).
2. **Transcoding Pipeline:** Async workers transcode chunks into multiple resolutions (1080p, 720p, 480p) using H.264/AV1 codecs.
3. **Adaptive Bitrate Streaming (HLS / DASH):** Master playlist file (\`.m3u8\`) lists available bitrates. Client player dynamically switches chunk quality based on current network bandwidth.`
      },
      {
        id: 'sd-10',
        category: 'Resilience & Reliability',
        difficulty: 'Medium',
        question: 'Explain Circuit Breaker, Bulkhead Isolation, and Backpressure patterns in high-scale systems.',
        answer: `### 1. Circuit Breaker
Monitors remote service calls. States: **Closed** (normal), **Open** (failing, fail fast without calling remote service), **Half-Open** (test probe requests).

### 2. Bulkhead Isolation
Isolates resources (thread pools, memory) into distinct pools so a failure in one component (e.g. recommendations service) does not starve critical services (e.g. checkout).

### 3. Backpressure
Signals downstream consumers to slow down or drop incoming requests when buffers reach capacity.`
      },
      {
        id: 'sd-11',
        category: 'Database Design',
        difficulty: 'Hard',
        question: 'How do you choose between SQL and NoSQL? Explain with real production scenarios.',
        answer: `SQL databases provide ACID guarantees and are ideal for financial systems, order processing, ERP, and inventory management.

NoSQL databases prioritize scalability and flexible schemas.

Examples:
• SQL → Banking, Payments, Orders
• MongoDB → Product Catalog
• Cassandra → Chat Messages
• DynamoDB → User Sessions
• Redis → Caching

In enterprise systems it is common to use Polyglot Persistence where different databases solve different business problems.`
      },
      {
        id: 'sd-12',
        category: 'Caching',
        difficulty: 'Hard',
        question: 'Explain Cache Aside, Read Through, Write Through, Write Behind and Refresh Ahead caching strategies.',
        answer: `

Cache Aside
Application checks Redis first.
If missing → DB → Store in cache.

Read Through
Application always reads cache.
Cache automatically loads from DB.

Write Through
Every DB write updates cache immediately.

Write Behind
Update cache immediately.
Persist asynchronously.

Refresh Ahead
Frequently accessed keys are refreshed before expiration.

Use Cases:
Cache Aside → Most APIs
Write Through → Inventory
Write Behind → Analytics
Refresh Ahead → Product Catalog`
      },
      {
        id: 'sd-13',
        category: 'Microservices',
        difficulty: 'Hard',
        question: 'When should you avoid Microservices?',
        answer: `Microservices are not always the right choice.

Avoid when:
• Small team
• Small product
• Limited DevOps maturity
• Low traffic
• Strong transactional consistency required

Problems introduced:
• Distributed transactions
• Network latency
• Service discovery
• Monitoring
• Deployment complexity

Start with Modular Monolith and migrate gradually.`
      },
      {
        id: 'sd-14',
        category: 'Distributed Systems',
        difficulty: 'Hard',
        question: 'Explain CAP Theorem with real-world examples.',
        answer: `CAP states a distributed system can guarantee only two:

Consistency
Availability
Partition Tolerance

CP:
MongoDB (majority write)
ZooKeeper

AP:
Cassandra
DynamoDB

CA:
Traditional SQL Server in single node.

Network partitions always happen, therefore modern distributed databases usually choose CP or AP.`
      },
      {
        id: 'sd-15',
        category: 'Messaging',
        difficulty: 'Hard',
        question: 'Kafka vs RabbitMQ vs Azure Service Bus. When would you use each?',
        answer: `Kafka
• Event Streaming
• Replay support
• Very high throughput
• Log storage

RabbitMQ
• Task queues
• Work distribution
• Low latency

Azure Service Bus
• Enterprise messaging
• FIFO
• Sessions
• Dead Letter Queue
• Transactions

Choose Kafka for analytics and event sourcing.
Choose RabbitMQ for background processing.
Choose Service Bus for enterprise business workflows.`
      },
      {
        id: 'sd-16',
        category: 'Scalability',
        difficulty: 'Hard',
        question: 'Horizontal Scaling vs Vertical Scaling. What are the trade-offs?',
        answer: `Vertical Scaling
Increase CPU/RAM.
Simple.
Limited by hardware.

Horizontal Scaling
More servers.
Better availability.
Requires load balancers and distributed cache.

Modern cloud-native applications prefer horizontal scaling.`
      },
      {
        id: 'sd-17',
        category: 'Cloud Architecture',
        difficulty: 'Hard',
        question: 'Design a multi-region application. How do you handle failover and disaster recovery?',
        answer: `Use Active-Active or Active-Passive architecture.

Components:
• Global Load Balancer
• Regional API Gateways
• Geo-replicated Database
• Redis replication
• Blob replication
• DNS failover

Recovery objectives:
RPO
RTO

Regular disaster recovery testing is essential.`
      },
      {
        id: 'sd-18',
        category: 'Performance',
        difficulty: 'Hard',
        question: 'Your API suddenly becomes slow after deployment. How would you troubleshoot?',
        answer: `Investigate:

• Application logs
• CPU
• Memory
• Thread pool starvation
• SQL execution plans
• Redis hit ratio
• Network latency
• External API latency
• GC pressure
• Lock contention

Use Application Insights, Grafana, Prometheus or OpenTelemetry to isolate bottlenecks.`
      },
      {
        id: 'sd-19',
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'Explain CQRS. When should it be used?',
        answer: `CQRS separates read and write models.

Advantages:
• Independent scaling
• Optimized read models
• Better performance

Disadvantages:
• Complexity
• Eventual consistency

Use for high read traffic systems like ecommerce, banking and reporting platforms.`
      },
      {
        id: 'sd-20',
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'What is Event Sourcing? Explain advantages and disadvantages.',
        answer: `Instead of storing current state, store every business event.

Example:
AccountCreated
MoneyDeposited
MoneyWithdrawn

Advantages:
Audit history
Replay
Temporal queries

Disadvantages:
Complexity
Storage growth
Migration challenges`
      },
      {
        id: 'sd-21',
        category: 'API Design',
        difficulty: 'Medium',
        question: 'REST vs GraphQL vs gRPC. When would you choose each?',
        answer: `REST
Simple CRUD APIs.

GraphQL
Flexible client queries.
Avoid over-fetching.

gRPC
Binary protocol.
Fast internal communication.
Streaming support.

REST for public APIs.
GraphQL for frontend-heavy apps.
gRPC for microservice communication.`
      },
      {
        id: 'sd-22',
        category: 'Observability',
        difficulty: 'Hard',
        question: 'Explain Logging, Monitoring and Distributed Tracing.',
        answer: `Logging records application events.

Monitoring measures health:
CPU
Memory
Latency
Availability

Tracing follows a request across services.

Popular stack:
OpenTelemetry
Application Insights
Prometheus
Grafana
Jaeger`
      },
      {
        id: 'sd-23',
        category: 'Security',
        difficulty: 'Hard',
        question: 'How do you secure APIs in production?',
        answer: `Best practices:

• OAuth2
• OpenID Connect
• JWT validation
• API Gateway
• Rate Limiting
• Input Validation
• HTTPS
• Secret Vault
• WAF
• CORS
• OWASP Top 10 protections
• Least privilege access`
      },
      {
        id: 'sd-24',
        category: 'Reliability',
        difficulty: 'Hard',
        question: 'Explain Idempotency and why it is important.',
        answer: `Idempotency means repeated requests produce the same result.

Used in:
Payments
Orders
Retries
Webhook processing

Implementation:
• Idempotency Key
• Redis lookup
• Database unique constraint
• Duplicate detection`
      },
      {
        id: 'sd-25',
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'Monolithic vs Modular Monolith vs Microservices. Compare all three.',
        answer: `Monolith
Simple deployment.
Hard to scale.

Modular Monolith
Strong module boundaries.
Single deployment.
Easy migration.

Microservices
Independent deployment.
Independent scaling.
Operational complexity.

For most enterprise applications, a Modular Monolith is often the best starting architecture before evolving to microservices where justified.`
      }
    ]
  },
  {
    id: 'scenarios-guide',
    title: 'Real Production Scenarios',
    subtitle: 'System Outages, Debugging & Firefighting Guide',
    rating: '⭐⭐⭐⭐⭐',
    icon: 'Award',
    description: 'Diagnose and remediate 100% CPU on SQL DB, memory leaks, connection exhaustion, cache stampede, and thread starvation.',
    questions: [
      {
        id: 'ps-1',
        category: 'Infrastructure & DB Firefighting',
        difficulty: 'Hard',
        question: 'Production SQL Server is hitting 100% CPU. API responses have slowed dramatically. Walk through your immediate and long-term investigation, debugging, and remediation steps.',
        answer: `Handling a production database outage requires quick triage followed by targeted mitigation.

### 1. Immediate Symptoms
*   CPU remains pinned at 98-100%.
*   App Service threads pool starts queuing up (HTTP 503 / timeout errors).

### 2. Step-by-Step Investigation
1.  **Identify the Culprit Queries:**
    Run a query to retrieve active execution plans sorted by execution CPU usage:
    \`\`\`sql
    SELECT TOP 5
        qs.total_worker_time/qs.execution_count AS AvgCPU,
        st.text AS QueryText,
        qp.query_plan AS ExecutionPlan
    FROM sys.dm_exec_query_stats qs
    CROSS APPLY sys.dm_exec_sql_text(qs.sql_handle) st
    CROSS APPLY sys.dm_exec_query_plan(qs.plan_handle) qp
    ORDER BY AvgCPU DESC;
    \`\`\`
2.  **Examine Locks and Blocks:**
    Execute \`sp_who2\` or query \`sys.dm_tran_locks\` to check if a specific transaction is locking resources, creating blocking queues.
3.  **Analyze CPU Resource Consumers:**
    *   Are we missing indexes (Index Scan instead of Seek)?
    *   Is there a parameter sniffing issue (plan cached for small result set is being used for a huge scan)?

### 3. Immediate Remediation (Emergency Fixes)
*   **Quick Indexing:** If a missing index is causing 1,000,000 record table scans, create it immediately.
*   **Clear Bad Cache Plans:** If a bad plan is cached, flush it from memory:
    \`\`\`sql
    -- Clear specific plan from cash. Or clear all plans:
    DBCC FREEPROCCACHE;
    \`\`\`
*   **Scale Up:** In cloud databases (e.g. Azure SQL), scale up DB tier momentarily to cope with the query load.

### 4. Prevention (Long-term)
*   Integrate APM tools (Application Insights, Datadog) to alert on query timings.
*   Review NHibernate/Entity Framework generated queries for N+1 problems.
*   Setup database auto-tuning rules where appropriate.`
      },
      {
        id: 'ps-2',
        category: 'Memory & Threading Outages',
        difficulty: 'Hard',
        question: 'How do you diagnose database connection pool exhaustion in a .NET application running in production? What are the symptoms, root causes, and fixes?',
        answer: `**Database Connection Pool Exhaustion** occurs when the application runs out of free connections in the client-side connection pool.

### 1. Symptoms
*   API begins throwing errors like: \`TimeoutException: Timeout expired. The timeout period elapsed prior to obtaining a connection from the pool...\`
*   Latency spike across all dependent endpoints.
*   Database server itself has normal CPU / memory (since requests aren't even reaching the database server; they are blocked in the web-app client engine).

### 2. Root Causes
*   **Incorrect Disposal:** SQL connection object not disposed (missing C# \`using\` block or scope failure).
*   **Slow Queries:** Slow queries keep connections open longer, holding on to pool quota.
*   **Leaked Transactions:** Transactions started but never committed or rolled back.
*   **High Concurrency:** Application concurrency exceeds maximum pool size configuration (default is usually 100 in .NET).

### 3. Investigation
*   **Log Analysis:** Confirm exception types. Look for leaks or slow-query reports in APM.
*   **CLR Monitoring:** Track performance counters for \`Active Connections\` and \`Blocked Requests\`.

### 4. Fixes and Prevention
*   **Guarantee Disposal (Using blocks):**
    \`\`\`csharp
    // C# 8+ using statement
    using var connection = new SqlConnection(connectionString);
    await connection.OpenAsync();
    // Execute queries
    // Connection disposed automatically at block exit
    \`\`\`
*   **For EF Core:** Register DbContext with the default Scoped lifetime. Ensure tasks are fully \`awaited\` before exiting context scopes.
*   **Tune pool sizes:** Adjust \`Max Pool Size=200;\` in connection string config if needed (only after resolving code leak issues).`
      },
      {
        id: 'ps-3',
        category: 'Memory & GC Outages',
        difficulty: 'Hard',
        question: 'Your .NET microservice in Kubernetes is experiencing OutOfMemoryException (OOMKilled). How do you debug managed vs unmanaged memory leaks using dotnet-dump?',
        answer: `When a container gets OOMKilled, Linux terminates the process because memory exceeded container memory limits.

### Debugging Steps
1. **Trigger Memory Dump:** Capture a memory dump before crash: \`dotnet-dump collect --process-id <pid>\`.
2. **Analyze Dump:** Analyze using \`dotnet-dump analyze <dump_file>\`.
3. **Identify Leaking Heap:**
   * Run \`clrstack -all\` and \`dumpheap -stat\` to list memory consumption grouped by type.
   * If \`System.String\` or \`byte[]\` dominates, inspect references via \`gcroot <object_address>\` to locate what object holds references (e.g. unbounded static Dictionary cache).`
      },
      {
        id: 'ps-4',
        category: 'Performance & Concurrency',
        difficulty: 'Hard',
        question: 'What is Thread Starvation in .NET, how does sync-over-async code trigger it under load, and how do you fix it?',
        answer: `### Sync-over-Async Mechanism
Calling \`.Result\` or \`.Wait()\` on an async task blocks the executing ThreadPool thread.

### Thread Starvation Cascade
Under heavy traffic, ThreadPool threads get blocked waiting for async operations to complete. The ThreadPool rate-limits injecting new threads (1-2 threads per 500ms). This creates a thread starvation bottleneck, driving API latency from 20ms to 30+ seconds.

### Fix
Replace all sync-over-async calls with proper \`async / await\` top-to-bottom throughout the execution call chain.`
      },
      {
        id: 'ps-5',
        category: 'Distributed Cache Outages',
        difficulty: 'Hard',
        question: 'What is a Cache Stampede / Thundering Herd problem? How do you prevent it using distributed locking or probabilistic early expiration?',
        answer: `### Cache Stampede
Occurs when a popular cache key expires under high concurrent traffic. Thousands of simultaneous requests fail cache lookup and hit the underlying database at once, causing CPU spikes and database crash.

### Prevention Strategies
1. **Distributed Mutex / Lock:** The first request acquires a lock (e.g. via Redis \`SETNX\`) to query DB and repopulate cache. Other requests wait or receive stale data.
2. **Probabilistic Early Expiration (XFetch Algorithm):** Recompute and update cache entry before it officially expires based on read frequency.`
      },
      {
        id: 'ps-6',
        category: 'API & Network Outages',
        difficulty: 'Medium',
        question: 'How do you design resilience strategies for third-party API dependencies using Polly in .NET?',
        answer: `Using **Polly v8** resilience pipelines:

\`\`\`csharp
var pipeline = new ResiliencePipelineBuilder<HttpResponseMessage>()
    .AddTimeout(TimeSpan.FromSeconds(3))
    .AddRetry(new RetryStrategyOptions<HttpResponseMessage>
    {
        ShouldHandle = new PredicateBuilder<HttpResponseMessage>()
            .Handle<HttpRequestException>()
            .HandleResult(r => r.StatusCode == HttpStatusCode.InternalServerError),
        MaxRetryAttempts = 3,
        Delay = TimeSpan.FromSeconds(2),
        BackoffType = DelayBackoffType.Exponential
    })
    .AddCircuitBreaker(new HttpCircuitBreakerStrategyOptions())
    .Build();
\`\`\``
      },
      {
        id: 'ps-7',
        category: 'Data Integrity & Lock Conflicts',
        difficulty: 'Hard',
        question: 'Compare Optimistic Concurrency Control vs Pessimistic Concurrency Control in Entity Framework Core. When do you use each?',
        answer: `### Optimistic Locking
Assumes conflicts are rare. Uses a \`[Timestamp]\` or \`[ConcurrencyCheck]\` version column (\`byte[] RowVersion\`).
* *Mechanism:* \`UPDATE ... WHERE Id = @Id AND RowVersion = @OldVersion\`. If row version changed, throws \`DbUpdateConcurrencyException\`.
* *Use case:* High-throughput web applications with low update collisions.

### Pessimistic Locking
Locks database records using \`SELECT ... FOR UPDATE\` (or \`WITH (UPDLOCK)\`).
* *Use case:* Banking/Financial transactions where conflict cost is high.`
      },
      {
        id: 'ps-8',
        category: 'Security & Auth Outages',
        difficulty: 'Medium',
        question: 'How do you handle JWT signing key rotation in microservices without causing 401 Unauthorized errors during deployment?',
        answer: `1. Maintain multiple active keys in the identity provider's **JWKS (JSON Web Key Set)** endpoint.
2. Sign new tokens with the new private key, but keep the old public key valid in JWKS for token verification until all active user tokens expire naturally.`
      },
      {
        id: 'ps-9',
        category: 'Message Queue Starvation',
        difficulty: 'Hard',
        question: 'How do you handle Poison Messages and consumer backlog in RabbitMQ / Azure Service Bus?',
        answer: `1. **Poison Message Handling:** If a message fails processing N times, move it to a **Dead Letter Queue (DLQ)** to prevent infinite loop processing.
2. **Backlog Scaling:** Scale consumer worker instances using KEDA (Kubernetes Event-driven Autoscaling) based on queue depth metrics.`
      },
      {
        id: 'ps-10',
        category: 'Deployment & CI/CD Incidents',
        difficulty: 'Hard',
        question: 'How do you perform zero-downtime database schema migrations (Expand-Contract Pattern)?',
        answer: `### Expand-Contract (Parallel Change) Pattern
1. **Expand:** Add new column/table alongside old column. Application writes to both columns.
2. **Migrate:** Backfill historical data to new column.
3. **Contract:** Switch application reads to new column. Once verified, drop old column in subsequent release.`
      }
    ]
  },
  {
    id: 'coding-guide',
    title: 'Coding & Machine Coding',
    subtitle: '100+ Coding exercises, LINQ design, and Pattern implementations',
    rating: '⭐⭐⭐⭐☆',
    icon: 'Code',
    description: 'Solve coding challenges, LINQ collections mapping, optimized SQL queries, and design patterns implementations.',
    questions: [
      {
        id: 'cd-1',
        category: 'Algorithms & Core coding',
        difficulty: 'Medium',
        question: 'Implement a thread-safe Singleton design pattern in C# (without locks if possible, or using Lazy<T>). Explain why your implementation is secure and performant.',
        answer: `Here is the modern, industry-standard way to implement a thread-safe Singleton pattern in C# using \`Lazy<T>\`.

### 1. Optimized Solution
\`\`\`csharp
public sealed class CacheManager
{
    // Lazy<T> ensures thread-safe, lazy initialization automatically
    private static readonly Lazy<CacheManager> _instance = 
        new Lazy<CacheManager>(() => new CacheManager());

    // Private constructor to prevent direct instantiation
    private CacheManager()
    {
        Console.WriteLine("Cache Manager Initialized.");
    }

    // Public entry point to retrieve instance
    public static CacheManager Instance => _instance.Value;

    public void SetValue(string key, object value) { /* store data */ }
}
\`\`\`

### 2. Why this is Secure & Performant
*   **Thread Safety:** The \`Lazy<T>\` class is thread-safe by default. If multiple threads call \`Instance\` at the same time, the runtime guarantees only one thread initializes the factory expression.
*   **Lazy Initialization:** The instance is only created when \`CacheManager.Instance\` is first queried in code, saving startup memory.
*   **No Lock Overhead:** Performance is optimized because we do not have to hold a heavy \`lock(syncLock)\` block for read checks on subsequent calls.

### 3. Alternative Approach (Double-Check Locking)
If target is legacy .NET framework without \`Lazy<T>\` support, use the double-check lock check pattern:
\`\`\`csharp
public sealed class DoubleCheckSingleton {
    private static volatile DoubleCheckSingleton _instance;
    private static readonly object _lock = new object();

    private DoubleCheckSingleton() {}

    public static DoubleCheckSingleton Instance {
        get {
            if (_instance == null) { // Check 1
                lock (_lock) {
                    if (_instance == null) { // Check 2
                        _instance = new DoubleCheckSingleton();
                    }
                }
            }
            return _instance;
        }
    }
}
\`\`\``
      },
      {
        id: 'cd-2',
        category: 'LINQ Exercises',
        difficulty: 'Medium',
        question: 'Given a list of employees, write a LINQ query to find the department with the highest average salary. The result should contain the Department name and the average salary.',
        answer: `This question tests query syntax and projection optimization inside LINQ.

### 1. The Linq Query solution
\`\`\`csharp
public class Employee {
    public string Name { get; set; }
    public string Department { get; set; }
    public decimal Salary { get; set; }
}

public class DepartmentStats {
    public string DepartmentName { get; set; }
    public decimal AverageSalary { get; set; }
}

public DepartmentStats GetHighestPayingDepartment(List<Employee> employees)
{
    return employees
        .GroupBy(e => e.Department)
        .Select(g => new DepartmentStats
        {
            DepartmentName = g.Key,
            AverageSalary = g.Average(e => e.Salary)
        })
        .OrderByDescending(d => d.AverageSalary)
        .FirstOrDefault(); // Returns null check if employees is empty
}
\`\`\`

### 2. Complexity Analysis
*   **Time Complexity:** \`O(N)\` where \`N\` is the number of employees. We traverse the elements once to group them, and then perform average operations.
*   **Space Complexity:** \`O(D)\` where \`D\` is the number of distinct departments to store the grouped structures.

### 3. Alternative Database Layer query (EF Core)
If writing this query against an EF Core DbContext, it translates into optimized SQL:
\`\`\`sql
SELECT TOP(1) [e].[Department] AS [DepartmentName], AVG([e].[Salary]) AS [AverageSalary]
FROM [Employees] AS [e]
GROUP BY [e].[Department]
ORDER BY AVG([e].[Salary]) DESC
\`\`\`\` `
      },
      {
        id: 'cd-3',
        category: 'Design Patterns & Architecture',
        difficulty: 'Medium',
        question: 'Implement Factory Method vs Abstract Factory in C#. When should you use Abstract Factory?',
        answer: `### 1. Abstract Factory Pattern
Creates families of related objects without specifying concrete classes.

\`\`\`csharp
public interface IButton { void Render(); }
public interface ITextBox { void Render(); }

public interface IUIFactory {
    IButton CreateButton();
    ITextBox CreateTextBox();
}

public class DarkUIFactory : IUIFactory {
    public IButton CreateButton() => new DarkButton();
    public ITextBox CreateTextBox() => new DarkTextBox();
}
\`\`\``
      },
      {
        id: 'cd-4',
        category: 'Algorithms & Collections',
        difficulty: 'Easy',
        question: 'Write a C# method to detect a cycle in a linked list using Floyd\'s Cycle-Finding Algorithm (Tortoise and Hare).',
        answer: `\`\`\`csharp
public class ListNode {
    public int Value;
    public ListNode Next;
}

public bool HasCycle(ListNode head) {
    if (head == null || head.Next == null) return false;
    ListNode slow = head;
    ListNode fast = head;

    while (fast != null && fast.Next != null) {
        slow = slow.Next;
        fast = fast.Next.Next;
        if (slow == fast) return true; // Cycle detected!
    }
    return false;
}
\`\`\`
**Time Complexity:** $O(N)$, **Space Complexity:** $O(1)$.`
      },
      {
        id: 'cd-5',
        category: 'Concurrency & Async',
        difficulty: 'Hard',
        question: 'Implement a Producer-Consumer pattern in C# using System.Threading.Channels.',
        answer: `\`\`\`csharp
var channel = Channel.CreateBounded<int>(100);

// Producer
Task producer = Task.Run(async () => {
    for (int i = 0; i < 10; i++) {
        await channel.Writer.WriteAsync(i);
    }
    channel.Writer.Complete();
});

// Consumer
Task consumer = Task.Run(async () => {
    await foreach (var item in channel.Reader.ReadAllAsync()) {
        Console.WriteLine($"Processed: {item}");
    }
});

await Task.WhenAll(producer, consumer);
\`\`\``
      },
      {
        id: 'cd-6',
        category: 'LINQ & Data Manipulation',
        difficulty: 'Medium',
        question: 'Write a recursive C# method to convert a flat list of items with ParentId into a nested Tree structure.',
        answer: `\`\`\`csharp
public class Item {
    public int Id { get; set; }
    public int? ParentId { get; set; }
    public string Name { get; set; }
}

public class TreeNode {
    public Item Data { get; set; }
    public List<TreeNode> Children { get; set; } = new();
}

public List<TreeNode> BuildTree(List<Item> items, int? parentId = null) {
    return items
        .Where(i => i.ParentId == parentId)
        .Select(i => new TreeNode {
            Data = i,
            Children = BuildTree(items, i.Id)
        })
        .ToList();
}
\`\`\``
      },
      {
        id: 'cd-7',
        category: 'Data Structures & Algorithms',
        difficulty: 'Hard',
        question: 'Design an LRU (Least Recently Used) Cache in C# with O(1) Get and Put complexity.',
        answer: `Combines \`Dictionary<Key, LinkedListNode<Item>>\` for $O(1)$ lookup and \`LinkedList<Item>\` for $O(1)$ eviction ordering.

\`\`\`csharp
public class LRUCache<K, V> {
    private readonly int _capacity;
    private readonly Dictionary<K, LinkedListNode<(K key, V val)>> _cache = new();
    private readonly LinkedList<(K key, V val)> _list = new();

    public LRUCache(int capacity) => _capacity = capacity;

    public V Get(K key) {
        if (!_cache.TryGetValue(key, out var node)) return default;
        _list.Remove(node);
        _list.AddFirst(node);
        return node.Value.val;
    }
}
\`\`\``
      },
      {
        id: 'cd-8',
        category: 'LINQ & Custom Operators',
        difficulty: 'Medium',
        question: 'Implement a custom LINQ extension method ChunkBy<T> that yields batches of specified size.',
        answer: `\`\`\`csharp
public static class LinqExtensions {
    public static IEnumerable<List<T>> ChunkBy<T>(this IEnumerable<T> source, int chunkSize) {
        var chunk = new List<T>(chunkSize);
        foreach (var item in source) {
            chunk.Add(item);
            if (chunk.Count == chunkSize) {
                yield return chunk;
                chunk = new List<T>(chunkSize);
            }
        }
        if (chunk.Count > 0) yield return chunk;
    }
}
\`\`\``
      },
      {
        id: 'cd-9',
        category: 'Design Patterns & Behavioral',
        difficulty: 'Medium',
        question: 'Implement Strategy Pattern with Dependency Injection in C# to calculate dynamic discounts.',
        answer: `\`\`\`csharp
public interface IDiscountStrategy { decimal Apply(decimal amount); }
public class VIPDiscount : IDiscountStrategy { public decimal Apply(decimal a) => a * 0.8m; }
public class RegularDiscount : IDiscountStrategy { public decimal Apply(decimal a) => a * 0.95m; }
\`\`\``
      },
      {
        id: 'cd-10',
        category: 'Async & Multi-threading',
        difficulty: 'Hard',
        question: 'How do you execute multiple tasks in parallel with a concurrency cap using SemaphoreSlim?',
        answer: `\`\`\`csharp
using var semaphore = new SemaphoreSlim(maxConcurrency: 5);
var tasks = items.Select(async item => {
    await semaphore.WaitAsync();
    try {
        await ProcessItemAsync(item);
    } finally {
        semaphore.Release();
    }
});
await Task.WhenAll(tasks);
\`\`\``
      }
    ]
  },
  {
    id: 'hr-guide',
    title: 'HR & Leadership Questions',
    subtitle: 'STAR Behavior Answers for Senior & Lead Roles',
    rating: '⭐⭐⭐⭐☆',
    icon: 'Award',
    description: 'Master behavioral questions like conflict resolution, biggest failures, leadership, and production-incidents handling using the STAR method.',
    questions: [
      {
        id: 'hr-1',
        category: 'Leadership & Conflict',
        difficulty: 'Easy',
        question: 'Tell me about a time you had a technical disagreement with another senior team member or architect. How did you resolve it?',
        answer: `This question evaluates your communication style, emotional intelligence, and ability to handle technical disagreements without creating friction inside the team.

### Recommended Answer (using the STAR method)

*   **Situation:**
    At my previous company, we were migration-routing our monolith order processing system to a microservices architecture. The lead architect wanted to use MongoDB for all document records, whereas I argued that the order processing service required strict transactional ACID compliance and proposed PostgreSQL.
*   **Task:**
    I had to convince the architect and the technical committee without causing team division or delaying project planning deadlines.
*   **Action:**
    Instead of arguing based on opinions, I set up a time constructs validation study. I created a structured **comparison matrix** displaying requirements: transaction rollback latency, scaling costs, and development efforts. I then set up a small proof of concept (POC) showing that under heavy connection traffic, MongoDB writes had occasional split-second dirty queries, whereas Postgres handled locks safely.
*   **Result:**
    After looking at the visual proof, the architect agreed to use PostgreSQL for the order records service while using MongoDB for the Catalog and User Session cache services. This hybrid design kept the system fast yet transactional, preventing zero-order leaks during our launch.`
      },
      {
        id: 'hr-2',
        category: 'Outage & Incident Management',
        difficulty: 'Medium',
        question: 'Describe a major production outage you were responsible for or led the firefighting for. What did you do, and what were the key takeaways?',
        answer: `This question assesses how you perform under intense pressure, your adherence to incident processes, and how you design post-mortem fixes.

### Recommended Answer (using the STAR method)

*   **Situation:**
    During a Friday afternoon deployment, our payment processing gateway started throwing HTTP 500 errors, creating a bottleneck. Order conversions dropped by 80%.
*   **Task:**
    As the lead developer, I had to immediately direct the team to identify, isolate, and roll back the bug while keeping stakeholders updated.
*   **Action:**
    First, I initiated our emergency bridge call. I assigned one developer to verify APM traces to locate the error, while I worked with release management to prepare a rollback plan. We identified the root cause: an unhandled NULL response returning from our third-party payment partner's API update that was crashing our controller pipeline.
    To recover services quickly, we rolled back to the previous stable release within 18 minutes. Under my command, we then drafted a hotfix adding robust default fallback properties and error boundary filters in the API client wrapper.
*   **Result:**
    The system was restored in under 20 minutes from triage starts. I conducted a **blameless post-mortem report** with the team. We added automated regression test coverage for third-party mock failures to ensure we never deploy gateway wrappers without fallback validation.`
      },
      {
        id: 'hr-3',
        category: 'Career & Impact',
        difficulty: 'Easy',
        question: 'What is your biggest technical failure or regret in software development, and what did you learn from it?',
        answer: `### Answer Structure (STAR)
* **Situation:** In an early project, I deployed a major database refactoring without automated integration test suites.
* **Impact:** Intermittent data loss for customer session settings over 2 hours.
* **Learning:** Established a strict "no deployment without automated integration test coverage and backwards-compatible expand-contract migration scripts" policy.`
      },
      {
        id: 'hr-4',
        category: 'Mentorship & Engineering Culture',
        difficulty: 'Medium',
        question: 'How do you conduct constructive Code Reviews for junior and peer engineers?',
        answer: `1. Focus on code, not person (use "we" instead of "you").
2. Distinguish between **Blockers** (bugs, security flaws) vs **Suggestions** (style, minor cleanups).
3. Explain *why* a change is suggested rather than just demanding it.`
      },
      {
        id: 'hr-5',
        category: 'Delivery & Prioritization',
        difficulty: 'Medium',
        question: 'How do you handle ambiguous requirements or sudden scope changes right before a deadline?',
        answer: `1. Break down changes into Must-have MVP vs Nice-to-have items.
2. Communicate trade-offs clearly to product owners (e.g. "We can add X feature, but launch moves back by 3 days").`
      },
      {
        id: 'hr-6',
        category: 'Architecture & Tech Debt',
        difficulty: 'Medium',
        question: 'How do you pitch paying down Technical Debt to business stakeholders?',
        answer: `Frame technical debt in business metrics: risk reduction, deployment velocity, and downtime prevention cost.`
      },
      {
        id: 'hr-7',
        category: 'Productivity & Learning',
        difficulty: 'Easy',
        question: 'Describe a situation where you had to quickly learn a new framework or technology under deadline pressure.',
        answer: `Focus on hands-on prototyping, reading official docs, and leveraging community best practices.`
      },
      {
        id: 'hr-8',
        category: 'Cross-Team Collaboration',
        difficulty: 'Medium',
        question: 'How do you work with a difficult or non-responsive external partner team?',
        answer: `Establish explicit API contracts (OpenAPI/Swagger), set SLA benchmarks, and schedule brief alignment check-ins.`
      },
      {
        id: 'hr-9',
        category: 'Mentorship & Leadership',
        difficulty: 'Easy',
        question: 'How do you mentor junior engineers and foster technical excellence?',
        answer: `Through pair programming, knowledge-sharing tech talks, and providing safe environments to take ownership.`
      },
      {
        id: 'hr-10',
        category: 'Senior Leadership & Vision',
        difficulty: 'Easy',
        question: 'Where do you see yourself in 3-5 years as a Senior / Lead Software Engineer?',
        answer: `Continuing to drive architectural design, scaling mission-critical cloud infrastructure, and mentoring high-performing engineering teams.`
      }
    ]
  }
];
